//sales order
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from "../../../Component/Layout";
import AsyncSelect from "react-select/async";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Bd from "./Bd";
import CharacteristicsModal from "../../../Component/CharacteristicsModal ";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import { hasPermission } from "../../../utils/permission";
import CreatableSelect from "react-select/creatable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ORDER_API, MASTER_API, PURCHASE_API } from "../../../apiConfig";
import sbeeLogo from "../../../assets/sbeelogo.png";
import { calculateToleranceRange } from "../../../ProcurementManagement/ProductManagement/ProductConfiguration";
import { useFieldPermissions } from "../../../utils/useFieldPermissions";
import { authHeaders } from "../../../utils/authHeaders";
import MaskedValue from "../../../Component/MaskedValue";
import { FileText, CalendarDays } from 'lucide-react';

// 🆕 This screen's own Module / Process / Sub Process — must exactly match
// what was picked in the User Manual upload screen (case-insensitive,
// trimmed) for the manual to be found.
const USER_MANUAL_SCOPE = {
    moduleName: "Order Management",
    process: "Sales",
    subProcess: "Sales Order"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;


// --- Shared dropdown option/menu styling (matches Home / Product Master / Enquiry dropdown look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

function SalesOrder() {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [rfqSummary, setRfqSummary] = useState([]);
    const [salesOrganization, setSalesOrganization] = useState("");
    const [customerSegment, setCustomerSegment] = useState("");
    const [dChannel, setDCHannel] = useState("");
    const [rfqId, setRfqId] = useState("");
    const [soIdFilter, setSoIdFilter] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    // Sales Order list filters: creation date range + status.
    const [creationDateFrom, setCreationDateFrom] = useState(null);
    const [creationDateTo, setCreationDateTo] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    // Driven by the Navbar alert bell: clicking "Sales Order Validity
    // Expired" navigates here with { showExpiredOnly: true }. expiredKeys
    // holds the so_id (or rfq_id, for rows with no so_id yet) of every SO
    // /sales-order/validity/expired currently flags — used both to filter
    // the list down to just those rows and to highlight them below.
    const [showExpiredOnly, setShowExpiredOnly] = useState(!!location.state?.showExpiredOnly);
    const [expiredKeys, setExpiredKeys] = useState(new Set());

    // The line above only fires ONCE, on mount. If you're already sitting on
    // this page when the Navbar bell is clicked, react-router navigates to
    // the SAME route — this component never remounts, so that useState
    // initializer never re-runs and showExpiredOnly stays stuck at whatever
    // it was on first load. This effect re-checks location.state on every
    // navigation (including same-route ones) so clicking the bell while
    // already here still turns the filter on.
    useEffect(() => {
        if (location.state?.showExpiredOnly) {
            setShowExpiredOnly(true);
        }
    }, [location.state]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [soToDelete, setSoToDelete] = useState(null);
    const [modalMode, setModalMode] = useState('view');
    const [activeTab, setActiveTab] = useState("basic");
    const [selectedRfq, setSelectedRfq] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);
    // Snapshot of each row's unit_price + markup% taken the moment "Edit" is
    // clicked. Used to live-recalculate Unit Price as the Markup (%) field
    // changes: basePrice = snapshotPrice / (1 + snapshotMarkup/100), then
    // reapply the current markup on top of that base — same math the backend
    // uses on Save, just mirrored client-side for an immediate preview.
    const [editPriceSnapshot, setEditPriceSnapshot] = useState({});
    // Full pre-edit snapshot (Planning Block deliberately excluded) used only
    // to decide whether Save should warn about a status reset.
    const preEditSnapshotRef = useRef(null);
    // Rows where the user typed directly into the Unit Price box — for those,
    // the typed number wins and markup changes no longer override it live.
    const [manualPriceEdits, setManualPriceEdits] = useState({});
    // Text currently being typed in a Spl. Price cell ({ grid: 'main'|'create', idx, text } | null).
    // The cell shows a CALCULATED value (unit price after Markup and Discount), so while the user
    // is typing we show exactly what they typed instead of the round-tripped figure — otherwise
    // decimals / trailing zeros get eaten mid-keystroke.
    const [splEdit, setSplEdit] = useState(null);
    useEffect(() => { setSplEdit(null); }, [showModal, isEditing]);
    // Snapshot of the header Planning Block value taken the moment "Edit" is
    // clicked. A product row that was just mirroring the header (i.e. its own
    // saved value equals this snapshot) keeps live-following the header
    // dropdown as it changes. A row with its own genuinely different saved
    // value — or one the user explicitly picks a value for — keeps that value
    // instead, regardless of further header changes.
    // (Planning Block tracking no longer needs a separate "initial" snapshot —
    // prod.planning_block and header planningBlock are now always kept in
    // sync directly, see handleProductChange / handleHeaderPlanningBlockChange.)
    const [purchaseOrder, setPurchaseOrder] = useState("");
    const [purchaseOrderDate, setPurchaseOrderDate] = useState(""); // PO Date — user-selected, stored as YYYY-MM-DD, shown as dd/MM/yyyy
    const [orderQty, setOrderQty] = useState({});
    const [requirementDate, setRequirementDate] = useState({});
    const [tentativeConfDate, setTentativeConfDate] = useState({});
    const [tentativeConfQty, setTentativeConfQty] = useState({});
    const [confirmationQty, setConfirmationQty] = useState({});
    const [confirmationDate, setConfirmationDate] = useState({});
    const [confirmationTime, setConfirmationTime] = useState({});
    const [deliveryQty, setDeliveryQty] = useState({});
    const [deliveryDate, setDeliveryDate] = useState({});
    const [recievingDate, setRecievingDate] = useState({});
    const [transportationDate, setTransportationDate] = useState({});
    const [packingDate, setPackingDate] = useState({});
    const [pickingDate, setPickingDate] = useState({});
    const [mad, setMad] = useState({});
    const [showProductModal, setShowProductModal] = useState(false);
    const [productDetails, setProductDetails] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCharacteristics, setSelectedCharacteristics] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [expandedRows] = useState([]);
    const [createActiveTab, setCreateActiveTab] = useState("basic");
    const [createModalOpen, setCreateModalOpen] = useState(false);
    useEffect(() => { setSplEdit(null); }, [createModalOpen]);
    const [salesOrgList, setSalesOrgList] = useState([]);
    const [customerSegmentList, setCustomerSegmentList] = useState([]);
    const [channelList, setChannelList] = useState([]);
    const [productList, setProductList] = useState([]);
    // --- GLOBAL FILTER STATES ---
    const [globalFilters, setGlobalFilters] = useState({
        construction: [], code: [], cableType: [], category: [],
        noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
    });
    const [filteredProductList, setFilteredProductList] = useState(null);
    // --- FILTER DROPDOWN MASTER DATA ---
    const [filterMasterData, setFilterMasterData] = useState({
        constructions: [], codes: [], cableTypes: [], categories: [],
        cores: [], colours: [], nominalCrossSections: [], referenceOdStandards: [], voltages: []
    });
    const [advancePayment, setAdvancePayment] = useState("");
    // View/Edit modal's own Yes/No flag — separate from advancePaymentOption
    // (which is Create-modal-only), same amount field (advancePayment) though.
    const [advancePaymentFlag, setAdvancePaymentFlag] = useState("No");
    // Advance Payment Yes/No toggle in the Create Sales Order modal — the
    // amount itself still lives in advancePayment/newSO.advancePayment
    // (unchanged, already wired end-to-end); this just controls whether the
    // amount field is shown, same Yes/No pattern as Customer Inspection.
    const [advancePaymentOption, setAdvancePaymentOption] = useState("No");
    const [advancePayments, setAdvancePayments] = useState([]);
    // Sales Order Checklist tab: rows pulled from Checklist Configuration,
    // merged with any values already saved against this specific SO.
    const [checklistItems, setChecklistItems] = useState([]);
    // Tracks which checklist rows (product_code__checklist_number__parameter_name)
    // the user has actually edited since the last successful save, so saveChecklist()
    // only PUTs the rows that changed instead of the entire checklist every time.
    const [dirtyChecklistKeys, setDirtyChecklistKeys] = useState(new Set());
    const [checklistLoading, setChecklistLoading] = useState(false);
    // RC Review — one Pending/Done status per product line, tracked
    // separately from the parameter-level checklist values above.
    // { [product_code]: "Pending" | "Done" }
    const [rcReviewStatus, setRcReviewStatus] = useState({});
    const [rcReviewSaving, setRcReviewSaving] = useState({}); // { [product_code]: true } while a toggle is in flight
    // Checklist Mandatory — the checkbox beside each product's code on the
    // Checklist tab. Ticked (default when unset) = that product's checklist
    // blocks approval as usual; unticked = it's exempted from the gate for
    // this SO. { [product_code]: true | false }
    const [checklistMandatory, setChecklistMandatory] = useState({});
    const [checklistMandatorySaving, setChecklistMandatorySaving] = useState({}); // { [product_code]: true } while a toggle is in flight
    // Checklist tab: product_code of the block currently expanded to show its
    // parameters (accordion — only one product's parameters visible at a
    // time; clicking it again, or clicking a different product, collapses
    // it). null = all collapsed, only the product-code headers are shown.
    const [expandedChecklistProduct, setExpandedChecklistProduct] = useState(null);
    //eslint-disable-next-line
    const [checklistSaving, setChecklistSaving] = useState(false);
    // Docs tab: product_code of the per-product "Purchase Order Checklist"
    // form currently open in the inline preview modal (null = none open).
    const [showChecklistDocPreview, setShowChecklistDocPreview] = useState(null);
    const [showOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
    // Find where your existing state is declared and add these:
    const [paymentHistoryTabType, setPaymentHistoryTabType] = useState('Advance');
    const [paymentHistoryTabAmount, setPaymentHistoryTabAmount] = useState('');
    const [paymentHistoryTabDate, setPaymentHistoryTabDate] = useState(formatDate(new Date()));
    const [paymentHistoryTabDescription, setPaymentHistoryTabDescription] = useState('');
    const [paymentHistoryTabReference, setPaymentHistoryTabReference] = useState('');
    // Payment History: index (within advancePayments) of the row currently
    // being edited, or null when the form above the table is adding a new
    // payment instead. Same pattern as the Purchase Order screen — clicking
    // the pencil in Balance Remaining fills the top form with that row's
    // values and turns "+ Add Payment" into "✓ Update Payment" + Cancel.
    const [editingPaymentIdx, setEditingPaymentIdx] = useState(null);
    const [newSO, setNewSO] = useState({
        salesOrganization: "",
        customerSegment: "",
        distributionChannel: "",
        customer: null,
        purchaseOrder: "",
        purchaseOrderDate: "",
        advancePayment: "",
        rfqId: "",
        customerCountry: ""
    });

    // 🆕 ORDER TYPE — Domestic SO / Export SO. Domestic keeps all existing
    // behavior as-is. Export-specific fields (exchange rate, dynamic tax
    // columns, etc.) will be layered on top of this later — for now this
    // just introduces the toggle + Customer Country.
    const [soOrderType, setSoOrderType] = useState("domestic");
    const soOrderTypeOptions = [
        { value: "domestic", label: "Domestic" },
        { value: "export", label: "Export SO" }
    ];

    // 🆕 Country master list — same config source as Import PO's Supplier
    // Country dropdown (${MASTER_API}/configuration/country), so this can
    // never drift from the GST Configuration screen's Country master.
    const [countryOptions, setCountryOptions] = useState([]);

    // 🆕 Exchange Rate + Currency for Export SO — same pattern as
    // PurchaseOrder.js's importCurrency/importExchangeRate, auto-filled
    // from Exchange Rate Configuration the moment a Customer Country is
    // picked (see fetchExchangeRateForCustomerCountry below), editable
    // manually afterward.
    const [soCurrency, setSoCurrency] = useState("");
    const [soExchangeRate, setSoExchangeRate] = useState("");
    const [newProducts, setNewProducts] = useState([]);
    const [changeLogs, setChangeLogs] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState("");
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [markup, setMarkup] = useState("");
    // Header-level Req. Date (shown above the Req. Date column on the Product tab).
    // Picking it stamps the same date on every line item; each row can still be
    // changed individually afterwards. Blank when rows have different dates.
    const [headerReqDate, setHeaderReqDate] = useState("");
    // Same idea for the Create / Convert modal's product grid (backed by newProducts).
    const [createHeaderReqDate, setCreateHeaderReqDate] = useState("");
    const [customerInspection, setCustomerInspection] = useState("No");
    const [customerInspectionProposedDate, setCustomerInspectionProposedDate] = useState("");
    const [customerInspectionProposedTime, setCustomerInspectionProposedTime] = useState("");
    const [customerInspectionConfirmedDate, setCustomerInspectionConfirmedDate] = useState("");
    const [customerInspectionConfirmedTime, setCustomerInspectionConfirmedTime] = useState("");
    // Where the CURRENT proposed slot was last set from: "audit_calendar"
    // (drag-and-drop there) or "sales_order" (typed in via this modal's own
    // "+ Add proposed date"). Only an "audit_calendar" slot is confirmable
    // from this modal — a slot typed here hasn't actually been placed on
    // the calendar yet.
    const [customerInspectionProposedSource, setCustomerInspectionProposedSource] = useState("");
    // Person Responsible for the current proposed/confirmed slot — always
    // auto-set to the logged-in user's name (getCurrentUser()) at the
    // moment a proposed date is saved or Confirm is clicked. Never a free
    // text field the user types into.
    const [customerInspectionAssignedTo, setCustomerInspectionAssignedTo] = useState("");
    // Department is not its own field/column — it's just a display label
    // derived from customerInspectionProposedSource: a slot proposed from
    // this Sales Order screen is "Sales", one set on the Audit Calendar is
    // "Quality".
    const inspectionDepartment = customerInspectionProposedSource === "sales_order" ? "Sales" : "Quality";
    // Icon-triggered "Inspection Schedule" modal (replaces the old 4 inline
    // fields). Opens regardless of isEditing/create mode and its own
    // Confirm button saves independently of the main Save button.
    const [showInspectionModal, setShowInspectionModal] = useState(false);
    // Trail of every proposed slot that was later replaced, and every past
    // confirmation, for the currently-open Sales Order — read-only, newest
    // first. Same data source as the Audit Calendar's own history list
    // (sales_order_inspection_history), just surfaced here too so this
    // modal doesn't need its own separate log.
    const [inspectionHistory, setInspectionHistory] = useState([]);
    const [inspectionHistoryLoading, setInspectionHistoryLoading] = useState(false);
    // Whether the user has manually edited the Confirmed Date/Time fields
    // since opening the modal. Mirrors the Audit Calendar's own Confirm
    // button: if untouched, Confirm just carries over whatever the Proposed
    // slot currently shows (including one just changed via the "+" button);
    // if the user has edited Confirmed directly, that edited value wins.
    //eslint-disable-next-line
    const [confirmedTouched, setConfirmedTouched] = useState(false);
    // Inline "add a new proposed slot" row inside the Proposed Date History
    // list. Hidden by default — clicking "+" reveals a blank Date/Time row;
    // "Save" on that row pushes it to the Audit Calendar (old current slot
    // drops into read-only history) and hides the row again.
    const [showAddProposedRow, setShowAddProposedRow] = useState(false);
    const [newProposedDate, setNewProposedDate] = useState("");
    const [newProposedTime, setNewProposedTime] = useState("");
    // Loading flag for the per-row "Confirm" button next to the current
    // proposed slot in the history list.
    const [confirmingProposed, setConfirmingProposed] = useState(false);
    const [planningBlock, setPlanningBlock] = useState("Blocked");
    // Sales Block — header-level Blocked/Released flag, same options as
    // Planning Block but tracked independently (no per-line cascade).
    const [salesBlock, setSalesBlock] = useState("Blocked");
    const [typeOfPacking, setTypeOfPacking] = useState("");
    const [validityNumber, setValidityNumber] = useState("");
    const [validityUnit, setValidityUnit] = useState("days");
    const [validityDate, setValidityDate] = useState(null);
    // --- NEW CANCEL STATES ---
    const [selectedCancelItems, setSelectedCancelItems] = useState([]);
    const [showCancelPrompt, setShowCancelPrompt] = useState(false);
    const [cancelReasonType, setCancelReasonType] = useState("");
    const [selectedRefSo, setSelectedRefSo] = useState(null);   // Reference SO for create mode
    const [refSoList, setRefSoList] = useState([]);              // Reference SO dropdown
    const [quotationType, setQuotationType] = useState(null);    // "Quotation Driven" | "RC Driven" — mandatory
    const [rcNumber, setRcNumber] = useState("");                 // shown only when quotationType === "RC Driven"
    const [quotationTypeList, setQuotationTypeList] = useState([]);
    const [quotationTypeLocked, setQuotationTypeLocked] = useState(false); // true when auto-set from RFQ conversion — not user-editable
    // Robust RC Driven check — tolerates whitespace/case differences from
    // the quotation-type master data (item.name), instead of an exact
    // string match against the literal "RC Driven".
    const isRcDriven = (qt) => (qt?.value || "").trim().toLowerCase() === "rc driven";
    const [selectedCreateRfq, setSelectedCreateRfq] = useState(null); // RFQ ID picked in Create SO modal (mandatory)
    const [rfqRefList, setRfqRefList] = useState([]);             // RFQ ID dropdown options (accepted RFQs not yet converted)
    const [soSalesFile, setSoSalesFile] = useState(null);
    const [soUploadMessage, setSoUploadMessage] = useState("");
    //eslint-disable-next-line
    const [isSoFileUploaded, setIsSoFileUploaded] = useState(false);
    const [showSoExcelModal, setShowSoExcelModal] = useState(false);
    //eslint-disable-next-line
    const [soExcelUploading, setSoExcelUploading] = useState(false);
    const salesFileInputRef = React.createRef();
    const [soDocFiles, setSoDocFiles] = useState([]);
    const soDocFileInputRef = useRef(null);
    const [createdBy, setCreatedBy] = useState("");
    const [createdDate, setCreatedDate] = useState("");
    const [approvedBy, setApprovedBy] = useState("");

    const [approvedDate, setApprovedDate] = useState("");   // ADD

    // ── GTP datasheet(s) — same form the Quotation shows, now also shown on the
    // Sales Order Docs tab for every approved product line. ──
    const [gtpDocs, setGtpDocs] = useState([]);
    const [showGtpPreview, setShowGtpPreview] = useState(null);
    const [unit1Location, setUnit1Location] = useState(null);

    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await fetch(`${MASTER_API}/location-master`, { headers: authHeaders() });
                const data = await res.json();
                const locations = Array.isArray(data) ? data : [];
                setUnit1Location(locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    const manufacturerName = unit1Location?.company_name || "SBEE Cables (India) Limited";
    const manufacturerAddress = unit1Location
        ? `${manufacturerName}, ${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}`
        : "SBEE Cables (India) Limited, Plot No. -282 & 283, 2nd Phase KIADB Industrial Area, Harohalli Village, Kanakapura Taluk, Ramanagar District";

    // Product codes that get a GTP — every line on the order except rejected
    // ones (same rule as the Quotation).
    const gtpEligibleCodes = (products) =>
        Array.from(new Set(
            (products || [])
                .filter((p) => String(p.so_action || "").toLowerCase() !== "rejected")
                .map((p) => String(p.product_code || p.productCode || p.product_number || "").trim())
                .filter(Boolean)
        ));

    const refreshGtpDocs = async (products) => {
        const codes = gtpEligibleCodes(products);
        if (codes.length === 0) { setGtpDocs([]); return; }
        try {
            const results = await Promise.all(codes.map(async (code) => {
                try {
                    const res = await fetch(`${MASTER_API}/gtp-configuration/by-product-code/${encodeURIComponent(code)}`, { headers: authHeaders() });
                    if (!res.ok) { console.warn("GTP fetch failed", code, res.status); return null; }
                    const gtp = await res.json();
                    if (!gtp || !gtp.gtpNumber) { console.warn("No GTP configured for", code, gtp); return null; }
                    return gtp;
                } catch (e) { console.warn("GTP fetch error", code, e); return null; }
            }));
            setGtpDocs(results.filter(Boolean));
        } catch (_) { setGtpDocs([]); }
    };

    useEffect(() => {
        if (!showModal || createModalOpen || !selectedRfq?.productDetails) { setGtpDocs([]); return; }
        refreshGtpDocs(selectedRfq.productDetails);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, createModalOpen, modalMode, selectedRfq?.productDetails]);

    const canView = hasPermission("SALES_ORDER", "view");
    const canEdit = hasPermission("SALES_ORDER", "edit");
    const canCreate = hasPermission("SALES_ORDER", "create");

    // Field-level visibility for Display-role users, same pattern as
    // Enquiry.js. When !isEditing (i.e. the modal is showing values, not
    // input controls), isFieldVisible(field_id) tells us whether to show
    // the real value or a masked placeholder — field_ids match FieldRegistry.js.
    const { isFieldVisible, isReviewOnly, isAdmin, canApprove } = useFieldPermissions("SALES_ORDER");

    // ── Discount Review rows ──
    // A line item needs Discount Review whenever its Discount (%) exceeds
    // its own effective Markup (%) — the row's own Markup if it has one,
    // otherwise the header Markup (%). Same rule as the Product tab's
    // inline warning (see handleProductChange), mirrored from the
    // Quotation module's Home.js discountReviewRows.
    //
    // 🆕 FIX: this used to flag a row purely on discount > markup, with no
    // regard for so_discount_review_action — so a line already cleared
    // (either carried over "Approved" from the quotation it was converted
    // from, or approved directly here) kept showing up asking to be
    // reviewed again, every time the modal opened. Mirrors the backend's
    // own has_pending_discount_review check (getAcceptedSummary /
    // getAcceptedConvert) and Bd.js's isPendingDiscountReview on the
    // quotation side — both already exclude so_discount_review_action ===
    // 'Approved' rows; this was the one place that didn't.
    const discountReviewRows = useMemo(() => {
        const tableData = selectedRfq?.productDetails || [];
        const markupNum = parseFloat(markup || 0);

        return tableData.filter((row) => {
            const rowMarkupNum = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                ? (parseFloat(row.markup) || 0)
                : markupNum;
            const discountVal = parseFloat(row.discount) || 0;
            return discountVal > rowMarkupNum && row.so_discount_review_action !== 'Approved';
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markup, selectedRfq]);

    // Discount Review only becomes relevant once the SO actually exists
    // (so_id present, not the Convert preview) and only for admins — same
    // gating Quotation's Home.js uses for its Discount Review tab.
    const isDiscountReviewVisible =
        canApprove && modalMode !== 'convert' && !!selectedRfq?.so_id && discountReviewRows.length > 0;

    // If the underlying conditions change out from under an open Discount
    // Review tab (e.g. the flagged rows get cleared elsewhere), fall back
    // to the Basic tab rather than leaving the user on a tab with no
    // button and no content.
    useEffect(() => {
        if (activeTab === "discount_review" && !isDiscountReviewVisible) {
            setActiveTab("basic");
        }
    }, [activeTab, isDiscountReviewVisible]);

    const soId = location.state?.soId;
    const returnTo = location.state?.returnTo;

    useEffect(() => {
        if (soId && returnTo) {
            handleOpenModal(soId, "view");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soId, returnTo]);


    const fetchLogs = async (id) => {
        if (!id) return;
        try {
            const res = await fetch(`${ORDER_API}/sales-order/logs/${id}`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setChangeLogs(data);
            }
        } catch (err) {
            console.error("Error fetching logs:", err);
        }
    };

    const fetchAdvancePayments = async (soId) => {
        try {
            const res = await fetch(
                `${ORDER_API}/sales-order/advance-payments/${soId}`
                , { headers: authHeaders() });

            const data = await res.json();

            if (data.success) {
                setAdvancePayments(data.advancePayments || []);
            }
        } catch (err) {
            console.error("Advance payment fetch error:", err);
        }
    };

    // A product line only counts as "approved" once Review has actually
    // approved it (a plain cancellation approval also counts, since the
    // underlying line item was approved before the cancellation flow began).
    // Still-pending, rejected, or cancel-initiated lines don't qualify.
    const isApprovedAction = (so_action) => {
        const s = so_action ? so_action.toLowerCase() : "";
        return s === "approved" || s === "cancellation approved";
    };

    // Set of product codes approved on the CURRENT sales order, or null when
    // there's nothing to restrict by yet (Create Direct SO / Convert-from-RFQ
    // flows, where line items haven't been through Review at all).
    const getApprovedProductCodes = () => {
        if (createModalOpen || modalMode === 'convert' || !selectedRfq?.productDetails) return null;
        return new Set(
            selectedRfq.productDetails
                .filter((p) => isApprovedAction(p.so_action))
                .map((p) => p.product_code || p.product_number)
        );
    };

    // --- SALES ORDER CHECKLIST ---
    const fetchChecklist = async (soId) => {
        if (!soId) return;
        setExpandedChecklistProduct(null);
        setChecklistLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/checklist`, { headers: authHeaders() });
            const data = await res.json();
            if (data.success) {
                setChecklistItems(data.data || []);
                setDirtyChecklistKeys(new Set());
            }
        } catch (err) {
            console.error("Checklist fetch error:", err);
        } finally {
            setChecklistLoading(false);
        }
        fetchRcReviewStatus(soId);
        fetchChecklistMandatory(soId);
    };

    // RC Review — Pending/Done per product line, independent of the
    // parameter-level checklist values. Only meaningful once the SO has a
    // real so_id (it's persisted keyed on so_id + product_code), so this is
    // only ever called from fetchChecklist(soId), never the pre-save preview.
    const fetchRcReviewStatus = async (soId) => {
        if (!soId || soId === 'NEW') { setRcReviewStatus({}); return; }
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/rc-review`, { headers: authHeaders() });
            const data = await res.json();
            if (data.success) {
                const map = {};
                (data.data || []).forEach((row) => { map[row.product_code] = row.status; });
                setRcReviewStatus(map);
            }
        } catch (err) {
            console.error("RC Review status fetch error:", err);
        }
    };

    const handleToggleRcReview = async (soId, productCode, checked) => {
        const newStatus = checked ? "Done" : "Pending";

        // No real so_id yet (Create Sales Order modal / RFQ Convert preview,
        // before Save) — there's nothing to persist to, just track the mark
        // locally. It gets pushed to the server via saveRcReviewStatusesTo
        // right after the SO is actually created.
        if (!soId || soId === 'NEW') {
            setRcReviewStatus((prev) => ({ ...prev, [productCode]: newStatus }));
            return;
        }

        // Optimistic update — flip immediately, roll back if the save fails.
        const prevStatus = rcReviewStatus[productCode] || "Pending";
        setRcReviewStatus((prev) => ({ ...prev, [productCode]: newStatus }));
        setRcReviewSaving((prev) => ({ ...prev, [productCode]: true }));
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/rc-review`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ productCode, status: newStatus, updatedBy: getCurrentUser() }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to update RC Review status");
        } catch (err) {
            console.error("RC Review status save error:", err);
            toast.error("Failed to update RC Review status");
            setRcReviewStatus((prev) => ({ ...prev, [productCode]: prevStatus }));
        } finally {
            setRcReviewSaving((prev) => ({ ...prev, [productCode]: false }));
        }
    };

    // Checklist Mandatory — same fetch/toggle shape as RC Review above, just
    // for the "is this product's checklist required at all" flag instead of
    // its Pending/Done status. Missing entries default to true (mandatory)
    // both here and wherever this map is read.
    const fetchChecklistMandatory = async (soId) => {
        if (!soId || soId === 'NEW') { setChecklistMandatory({}); return; }
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/checklist-mandatory`, { headers: authHeaders() });
            const data = await res.json();
            if (data.success) {
                const map = {};
                (data.data || []).forEach((row) => { map[row.product_code] = !!row.mandatory; });
                setChecklistMandatory(map);
            }
        } catch (err) {
            console.error("Checklist mandatory fetch error:", err);
        }
    };

    const handleToggleChecklistMandatory = async (soId, productCode, checked) => {
        // No real so_id yet — just track locally, pushed to the server via
        // saveChecklistMandatoryStatusesTo right after the SO is created.
        if (!soId || soId === 'NEW') {
            setChecklistMandatory((prev) => ({ ...prev, [productCode]: checked }));
            return;
        }

        // Optimistic update — flip immediately, roll back if the save fails.
        const prevVal = checklistMandatory[productCode] !== false;
        setChecklistMandatory((prev) => ({ ...prev, [productCode]: checked }));
        setChecklistMandatorySaving((prev) => ({ ...prev, [productCode]: true }));
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/checklist-mandatory`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ productCode, mandatory: checked, updatedBy: getCurrentUser() }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Failed to update checklist requirement");
        } catch (err) {
            console.error("Checklist mandatory save error:", err);
            toast.error("Failed to update checklist requirement");
            setChecklistMandatory((prev) => ({ ...prev, [productCode]: prevVal }));
        } finally {
            setChecklistMandatorySaving((prev) => ({ ...prev, [productCode]: false }));
        }
    };

    const updateChecklistValue = (productCode, checklistNumber, parameterName, value) => {
        const dirtyKey = `${productCode}__${checklistNumber}__${parameterName}`;
        setDirtyChecklistKeys((prev) => {
            const next = new Set(prev);
            next.add(dirtyKey);
            return next;
        });
        setChecklistItems((prev) =>
            prev.map((item) =>
                item.product_code === productCode &&
                    item.checklist_number === checklistNumber &&
                    item.parameter_name === parameterName
                    ? { ...item, value }
                    : item
            )
        );
    };

    // Product-wise checklist preview used BEFORE the SO has a so_id yet
    // (Create Sales Order modal, or Convert-from-RFQ before it's saved).
    // Builds the same one-block-per-product shape as the saved-SO view, from
    // whatever products are currently on the order. Any values the user has
    // already typed in this session are preserved (matched on product_code +
    // checklist_number + parameter_name) so reopening the tab, or adding
    // another product, doesn't wipe out entries made so far.
    const fetchChecklistForCurrentProducts = async (rows) => {
        const list = (rows || newProducts)
            .map((p) => ({
                product_code: p.product_code || p.product_number || "",
                description: p.description || "",
            }))
            .filter((p) => p.product_code);

        if (list.length === 0) {
            setChecklistItems([]);
            setExpandedChecklistProduct(null);
            return;
        }

        setChecklistLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/sales-order/checklist-preview`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ products: list }),
            });
            const data = await res.json();
            if (data.success) {
                const fresh = data.data || [];
                setChecklistItems((prev) => {
                    const prevMap = {};
                    prev.forEach((item) => {
                        prevMap[`${item.product_code}__${item.checklist_number}__${item.parameter_name}`] = item.value;
                    });
                    return fresh.map((item) => ({
                        ...item,
                        value: prevMap[`${item.product_code}__${item.checklist_number}__${item.parameter_name}`] ?? item.value,
                    }));
                });
            }
        } catch (err) {
            console.error("Checklist preview fetch error:", err);
        } finally {
            setChecklistLoading(false);
        }
    };

    // ── Product-wise Checklist PDF (one document per product code) ──
    // Mirrors the actual Checklist/GTP form layout (customer + PO header
    // block, boxed TECHNICAL ANALYSIS / COMMERCIAL ANALYSIS section bars,
    // signature footer) — one PDF per product, same as a GTP datasheet.
    const buildChecklistPdf = (productCode, productDescription, rows, header = {}) => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const margin = 40;
        const pageWidth = doc.internal.pageSize.getWidth();
        const contentWidth = pageWidth - margin * 2;
        let y = 46;

        // ── Header block: customer / offer / PO details ──
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(`NAME OF THE CUSTOMER : ${header.customerName || "-"}`, margin, y);
        y += 18;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Offer Details : ${productCode || "-"}${productDescription ? " - " + productDescription : ""}`, margin, y);
        y += 16;

        const col2X = margin + contentWidth * 0.5;
        doc.text(`P.O. No : ${header.poNumber || "-"}`, margin, y);
        doc.text(`Date : ${header.createdDate || "-"}`, col2X, y);
        y += 14;
        doc.text(`Received Date : ${header.receivedDate || "-"}`, margin, y);
        doc.text(`PO Delivery Date : ${header.deliveryDate || "-"}`, col2X, y);
        y += 18;

        const analysisGroups = {};
        rows.forEach((item) => {
            const key = item.analysis_type || "Uncategorized";
            if (!analysisGroups[key]) analysisGroups[key] = [];
            analysisGroups[key].push(item);
        });

        Object.entries(analysisGroups).forEach(([analysisType, items]) => {
            // Shaded section bar spanning the full content width, like the
            // "TECHNICAL ANALYSIS" / "COMMERCIAL ANALYSIS" banner rows in the
            // reference checklist form.
            doc.setFillColor(2, 24, 67);
            doc.rect(margin, y, contentWidth, 18, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(analysisType, margin + contentWidth / 2, y + 12.5, { align: "center" });
            doc.setTextColor(0, 0, 0);
            y += 18;

            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [["Parameter", "Value"]],
                body: items.map((i) => [i.uom ? `${i.parameter_name} (${i.uom})` : i.parameter_name, i.value || "-"]),
                styles: { fontSize: 9, cellPadding: 5 },
                headStyles: { fillColor: [226, 232, 240], textColor: [30, 41, 59] },
                theme: "grid",
            });

            y = doc.lastAutoTable.finalY + 22;
        });

        // ── Signature footer, matching "Prepared by & Date" / "Approved by
        // & Date" rows at the bottom of the reference checklist form ──
        if (y > doc.internal.pageSize.getHeight() - 80) {
            doc.addPage();
            y = 50;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Prepared by & Date :", margin, y);
        doc.text("Approved by & Date :", col2X, y);
        y += 20;
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, y, margin + contentWidth * 0.42, y);
        doc.line(col2X, y, col2X + contentWidth * 0.42, y);

        return doc;
    };

    // After a Sales Order is saved (created, converted, or edited), generate
    // one checklist PDF per product code (that actually has checklist rows
    // filled in) and upload it to that SO's Docs tab automatically — same
    // upload-docs endpoint the manual file picker in the Docs tab uses, so
    // it shows up there right away, named by product code.
    const uploadChecklistDocsForSO = async (soId, items, header = {}) => {
        if (!soId || !items || !items.length) return;

        const byProduct = {};
        items.forEach((item) => {
            if (!item.product_code) return;
            if (!byProduct[item.product_code]) {
                byProduct[item.product_code] = { description: item.product_description, rows: [] };
            }
            byProduct[item.product_code].rows.push(item);
        });

        const productCodes = Object.keys(byProduct);
        if (!productCodes.length) return;

        for (const code of productCodes) {
            const { description, rows } = byProduct[code];
            // Skip products where nothing was actually filled in.
            if (!rows.some((r) => (r.value || "").toString().trim() !== "")) continue;

            try {
                const pdfDoc = buildChecklistPdf(code, description, rows, header);
                const blob = pdfDoc.output("blob");
                const safeCode = code.replace(/[^a-zA-Z0-9_-]/g, "_");
                const file = new File([blob], `Checklist_${safeCode}.pdf`, { type: "application/pdf" });

                const formData = new FormData();
                formData.append("docs", file);

                await fetch(`${ORDER_API}/sales-order/upload-docs/${soId}`, {
                    headers: authHeaders(),
                    method: "POST",
                    body: formData,
                });
            } catch (err) {
                console.error(`Failed to generate/upload checklist doc for ${code}:`, err);
            }
        }
    };

    // silent=true is used when this is chained into the main "Save" flow
    // (SO save + checklist save happen together under one button now), so it
    // doesn't fire its own toast on top of the SO save toast.
    const saveChecklist = async (silent = false) => {
        const soId = selectedRfq?.so_id;
        if (!soId || !checklistItems.length) return true;

        // Only send rows the user actually touched since the last successful
        // save — sending the full checklist (which can run into the hundreds
        // of rows once several checklists are linked to a product) was
        // triggering 413 "request entity too large" responses from the API.
        const itemsToSend = checklistItems.filter((item) =>
            dirtyChecklistKeys.has(`${item.product_code}__${item.checklist_number}__${item.parameter_name}`)
        );

        if (itemsToSend.length === 0) {
            // Nothing changed since last save — skip the network call entirely.
            return true;
        }

        setChecklistSaving(true);
        try {
            const checklistUrl = `${ORDER_API}/sales-order/${soId}/checklist`;
            console.log("[CHECKLIST SAVE] →", { url: checklistUrl, method: 'PUT', count: itemsToSend.length });

            let res;
            try {
                res = await fetch(checklistUrl, {
                    method: "PUT",
                    headers: { ...authHeaders(), "Content-Type": "application/json" },
                    body: JSON.stringify({ values: itemsToSend }),
                });
                console.log("[CHECKLIST SAVE] ← status:", res.status, res.statusText, "type:", res.type, "ok:", res.ok);
            } catch (fetchErr) {
                console.error("[CHECKLIST SAVE] fetch() itself threw:", fetchErr.name, fetchErr.message, fetchErr);
                throw fetchErr;
            }

            const data = await res.json();
            if (data.success) {
                // Clear only the keys we just sent — if the user edited more
                // rows while this request was in flight, those stay dirty
                // and will go out on the next save.
                const sentKeys = new Set(
                    itemsToSend.map((item) => `${item.product_code}__${item.checklist_number}__${item.parameter_name}`)
                );
                setDirtyChecklistKeys((prev) => {
                    const next = new Set(prev);
                    sentKeys.forEach((k) => next.delete(k));
                    return next;
                });
                if (!silent) toast.success("Checklist saved successfully");
                return true;
            } else {
                toast.error(data.message || "Failed to save checklist");
                return false;
            }
        } catch (err) {
            console.error("Checklist save error:", err);
            toast.error("Failed to save checklist");
            return false;
        } finally {
            setChecklistSaving(false);
        }
    };

    // Used right after a brand-new SO is created (RFQ conversion or manual
    // "create-direct"), when we only just received the so_id back from the
    // server and can't rely on selectedRfq.so_id yet.
    const saveChecklistValuesTo = async (soId, items) => {
        if (!soId || !items || !items.length) return true;
        try {
            const res = await fetch(`${ORDER_API}/sales-order/${soId}/checklist`, {
                method: "PUT",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ values: items }),
            });
            const data = await res.json();
            if (!data.success) {
                toast.error(data.message || "Failed to save checklist");
            }
            return !!data.success;
        } catch (err) {
            console.error("Checklist save error:", err);
            toast.error("Failed to save checklist");
            return false;
        }
    };

    // Checklist must be fully filled out (every parameter given a value)
    // before the Sales Order can be created/saved. Returns the list of
    // items still missing a value so the caller can report a count and
    // jump the user to the Checklist tab.
    const getMissingChecklistItems = (items) =>
        (items || []).filter((item) => {
            const v = item.value;
            return v === null || v === undefined || String(v).trim() === "";
        });

    // Every product on the order must also have its checklist marked "Done"
    // (the RC Review checkbox) before the Sales Order can be created — not
    // just have its parameter values filled in. Returns the list of
    // product_codes still Pending.
    const getProductsMissingRcReview = (items) => {
        const codes = Array.from(
            new Set((items || []).map((item) => item.product_code).filter(Boolean))
        );
        return codes.filter((code) => (rcReviewStatus[code] || "Pending") !== "Done");
    };

    // Persists any RC Review "Done" marks the user set locally (pre-save,
    // when there was no so_id yet) against the real so_id we just got back
    // from create-direct / createSO. Mirrors handleToggleRcReview's request
    // but fires once per product right after creation instead of on click.
    const saveRcReviewStatusesTo = async (soId, items) => {
        if (!soId) return;
        const codes = Array.from(
            new Set((items || []).map((item) => item.product_code).filter(Boolean))
        );
        const doneCodes = codes.filter((code) => rcReviewStatus[code] === "Done");
        for (const code of doneCodes) {
            try {
                await fetch(`${ORDER_API}/sales-order/${soId}/rc-review`, {
                    method: "POST",
                    headers: { ...authHeaders(), "Content-Type": "application/json" },
                    body: JSON.stringify({ productCode: code, status: "Done", updatedBy: getCurrentUser() }),
                });
            } catch (err) {
                console.error("RC Review status save error (post-create):", err);
            }
        }
    };

    // Persists any Checklist Mandatory marks the user set locally (pre-save)
    // against the real so_id we just got back from create-direct / createSO.
    // Only needs to push the false (unticked/"not mandatory") ones — true is
    // already the server-side default for a product with no row saved yet.
    const saveChecklistMandatoryStatusesTo = async (soId, items) => {
        if (!soId) return;
        const codes = Array.from(
            new Set((items || []).map((item) => item.product_code).filter(Boolean))
        );
        const exemptedCodes = codes.filter((code) => checklistMandatory[code] === false);
        for (const code of exemptedCodes) {
            try {
                await fetch(`${ORDER_API}/sales-order/${soId}/checklist-mandatory`, {
                    method: "POST",
                    headers: { ...authHeaders(), "Content-Type": "application/json" },
                    body: JSON.stringify({ productCode: code, mandatory: false, updatedBy: getCurrentUser() }),
                });
            } catch (err) {
                console.error("Checklist mandatory save error (post-create):", err);
            }
        }
    };

    // --- HELPER TO GET USER ID (used to stamp Created By / Approved By / etc.) ---
    const getCurrentUser = () => {
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                // Prefer the full name (first + last) so Created By / Updated By
                // show a real name; fall back to the user id for accounts with
                // no name saved yet.
                const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
                if (fullName) return fullName;
                if (parsedUser.name && parsedUser.name.trim()) return parsedUser.name.trim();
                if (parsedUser.userId) return parsedUser.userId;
                if (parsedUser.username) return parsedUser.username;
                if (parsedUser.email) return parsedUser.email;
            } catch (e) { }
        }
        const keys = ["userId", "userName", "username", "user_name", "name", "fullName", "email"];
        for (const key of keys) {
            const val = localStorage.getItem(key);
            if (val && val !== "undefined" && val !== "null") return val;
        }
        return "User";
    };

    const getFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    useEffect(() => {
        fetchMasterData();
        fetchProductList();
    }, []);

    useEffect(() => {
        fetchProductFilterOptions(globalFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilters]);

    const buildFilterQuery = (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters || {}).forEach(([key, selectedOpts]) => {
            if (Array.isArray(selectedOpts) && selectedOpts.length > 0) {
                const values = selectedOpts.map(o => (o && o.value !== undefined) ? o.value : o);
                params.set(key, values.join(","));
            }
        });
        return params.toString();
    };

    const fetchProductFilterOptions = async (filters = globalFilters) => {
        try {
            const qs = buildFilterQuery(filters);
            const res = await fetch(`${ORDER_API}/sales-order/product-filters${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setFilterMasterData(data);
        } catch (err) {
            console.error("Error fetching product filter options:", err);
        }
    };

    const fetchMasterData = async () => {
        try {
            const [resOrg, resSeg, resChn] = await Promise.all([
                fetch(`${MASTER_API}/configuration/sales-organization`, { headers: authHeaders() }),
                fetch(`${MASTER_API}/configuration/dynamic/customer-segment`, { headers: authHeaders() }),
                fetch(`${MASTER_API}/configuration/distribution-channel`, { headers: authHeaders() })
            ]);

            const org = await resOrg.json();
            const seg = await resSeg.json();
            const chn = await resChn.json();

            setSalesOrgList(org.data || org || []);
            setCustomerSegmentList(seg.data || seg || []);
            setChannelList(chn.data || chn || []);

            fetch(`${MASTER_API}/configuration/dynamic/quotation-type`, { headers: authHeaders() })
                .then(r => r.json())
                .then(d => setQuotationTypeList(d.data || d || []))
                .catch(err => console.error("Error fetching quotation type list:", err));

            // 🆕 Country master (for Export SO's Customer Country dropdown)
            fetch(`${MASTER_API}/configuration/country`, { headers: authHeaders() })
                .then(r => r.json())
                .then(d => {
                    const raw = d.data || d || [];
                    setCountryOptions(Array.isArray(raw) ? raw : []);
                })
                .catch(err => console.error("Error fetching country list:", err));

            // NOTE: Category/Cable Type/Code/Color/Cores/Cross Section/Std. Ref/
            // Voltage/Construction options for the Product filter bar are
            // deliberately NOT fetched here. They used to come from the generic
            // ${MASTER_API}/configuration/... master-data endpoints, which
            // return every category in the system (including raw-material and
            // packaging entries like BOBBIN, BOXES, Compound, DRUM, etc.) with
            // no Finished Goods scoping. That call also raced against
            // fetchProductFilterOptions() below (both fire on mount) and could
            // overwrite its correctly-scoped result. filterMasterData for the
            // product filters is now populated exclusively by
            // fetchProductFilterOptions(), which is scoped to Finished Goods
            // products on the backend.
        } catch (err) {
            console.error(err);
        }
    };

    const fetchProductList = async () => {
        try {
            const res = await fetch(
                `${ORDER_API}/sales-order/fetch-product-list`
                , { headers: authHeaders() });

            const data = await res.json();

            const productArray = Array.isArray(data)
                ? data
                : data.products || data.data || [];

            const formatted = productArray.map(item => ({
                value: item.product_code || item.productCode || item.Product_Code || item.value || '',
                label: (() => {
                    const code = item.product_code || item.productCode || item.Product_Code || item.value || '';
                    const desc = item.description || item.Description || '';
                    return desc ? `${code} - ${desc}` : code;
                })(),
                description: item.description || item.Description || '',
                uom: item.uom || item.UOM || '',
                product_class: item.product_class || item.Product_Class || '',
                construction: item.construction || item.Construction || '',
                code: item.code || item.Code || '',
                cableType: item.cableType || item.cable_type || item.CableType || item.Cable_Type || '',
                category: item.productCategory || item.product_category || item.category || item.Category || '',
                noOfCores: item.noOfCores || item.no_of_cores || item.NoOfCores || item.No_Of_Cores || item.cores || item.Cores || '',
                color: item.color || item.Color || item.colour || item.Colour || '',
                nominalCrossSection: item.nominalCrossSection || item.nominal_cross_section || item.NominalCrossSection || item.Nominal_Cross_Section || '',
                referenceOfStandard: item.referenceOfStandard || item.reference_of_standard || item.ReferenceOfStandard || item.Reference_Of_Standard || item.referenceOdStandard || '',
                voltage: item.voltage || item.Voltage || '',
            }));

            setProductList(formatted);
        } catch (err) {
            console.error(err);
        }
    };

    const handleOpenCreateSO = () => {
        setNewSO({ salesOrganization: "", customerSegment: "", distributionChannel: "", customer: null, purchaseOrder: "", purchaseOrderDate: "", rfqId: "" });
        setNewProducts([]);
        setCreateHeaderReqDate("");
        setCreateActiveTab("basic");
        setCreateModalOpen(true);
        // Load the full approved-RFQ list up front — picking a Customer is
        // optional and only narrows this list when used (see handleCustomerChange).
        fetchApprovedRfqsForCustomer(null);
        setPaymentTerms("");
        setMarkup(""); setCustomerInspection("No"); setPlanningBlock("Blocked"); setAdvancePaymentOption("No"); setSalesBlock("Blocked");
        setValidityNumber(""); setValidityUnit("days"); setValidityDate(null);
        setSelectedRefSo(null); setSelectedCreateRfq(null); setSoSalesFile(null); setSoUploadMessage(""); setIsSoFileUploaded(false);
        setQuotationType(null); setRcNumber(""); setQuotationTypeLocked(false);
        setAdvancePayments([]);
        setCreatedBy(getCurrentUser());
        setCreatedDate(formatDateDisplay(new Date()));
        setApprovedBy("");
        setApprovedDate("");
        setChecklistItems([]);
        setExpandedChecklistProduct(null);
        // These aren't covered by the resets above, so a previously-viewed
        // SO's inspection schedule was leaking into a brand-new Create form.
        setCustomerInspectionProposedDate(""); setCustomerInspectionProposedTime("");
        setCustomerInspectionConfirmedDate(""); setCustomerInspectionConfirmedTime("");
        setCustomerInspectionProposedSource(""); setCustomerInspectionAssignedTo("");
        setInspectionHistory([]); setConfirmedTouched(false);
    };

    const handleRefSoSelect = async (selectedOption) => {
        setSelectedRefSo(selectedOption);
        if (!selectedOption) {
            // Reference SO cleared (X button) — undo everything it loaded, so
            // stale customer/product/quotation-type/RFQ data doesn't linger
            // once the reference is gone. Mirrors the blank-slate reset in
            // handleOpenCreateSO, minus the bits that aren't ref-SO-derived
            // (modal open state, active tab, createdBy/createdDate, etc.).
            setNewSO({ salesOrganization: "", customerSegment: "", distributionChannel: "", customer: null, purchaseOrder: "", purchaseOrderDate: "", rfqId: "" });
            setNewProducts([]);
            setCreateHeaderReqDate("");
            setPaymentTerms("");
            setMarkup(""); setCustomerInspection("No"); setPlanningBlock("Blocked"); setAdvancePaymentOption("No"); setSalesBlock("Blocked");
            setValidityNumber(""); setValidityUnit("days"); setValidityDate(null);
            setSelectedCreateRfq(null);
            setQuotationType(null); setRcNumber(""); setQuotationTypeLocked(false);
            setAdvancePayments([]);
            setCustomerInspectionProposedDate(""); setCustomerInspectionProposedTime("");
            setCustomerInspectionConfirmedDate(""); setCustomerInspectionConfirmedTime("");
            setCustomerInspectionProposedSource(""); setCustomerInspectionAssignedTo("");
            setInspectionHistory([]); setConfirmedTouched(false);
            return;
        }
        try {
            const res = await fetch(`${ORDER_API}/sales-order/accepted-convert/${selectedOption.value}`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) { toast.error("Failed to load reference SO"); return; }

            setNewSO(prev => ({
                ...prev,
                salesOrganization: data.salesOrganization || prev.salesOrganization,
                customerSegment: data.customer_segment || data.customerSegment || prev.customerSegment,
                distributionChannel: data.distributionChannel || prev.distributionChannel,
                customer: data.customer ? { label: data.customer, value: data.customer } : prev.customer,
                purchaseOrder: data.purchaseOrder || prev.purchaseOrder,
                advancePayment: data.advanceAmount || prev.advancePayment
            }));
            setAdvancePaymentOption(data.advancePaymentFlag || (data.advanceAmount ? "Yes" : "No"));

            if (data.markup) setMarkup(data.markup);
            if (data.customer_inspection) setCustomerInspection(data.customer_inspection);
            if (data.planning_block) setPlanningBlock(data.planning_block);
            if (data.type_of_packing) setTypeOfPacking(data.type_of_packing);
            if (data.payment_terms) setPaymentTerms(data.payment_terms);
            if (data.validity_terms) {
                const match = data.validity_terms.match(/^(\d+)\s*(.*)$/);
                setValidityNumber(match ? match[1] : data.validity_terms);
                setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
            }

            // Quotation Type / RC Number / RFQ ID — the reference SO's own
            // lifecycle info. If it was RFQ/Quotation Driven, carry the RFQ ID
            // over too (same accepted-convert payload already has rfq_id),
            // so a copy of an RFQ-driven SO doesn't silently drop that link.
            if (data.quotation_type) {
                setQuotationType({ value: data.quotation_type, label: data.quotation_type });
                setQuotationTypeLocked(!!data.quotation_type_locked);
            }
            setRcNumber(data.rc_number || "");
            if (data.rfq_id) {
                setSelectedCreateRfq({ value: data.rfq_id, label: data.rfq_id });
                setNewSO(prev => ({ ...prev, rfqId: data.rfq_id }));
            } else {
                setSelectedCreateRfq(null);
            }

            if (data.productDetails && data.productDetails.length > 0) {
                const mappedProducts = data.productDetails.map((p) => {
                    const chars = p.characteristics || [];
                    const qty = Number(p.quantity || 0);
                    return {
                        product_code: p.product_code || p.product_number || '',
                        description: p.description || '',
                        uom: p.uom || '',
                        unit_price: Number(p.unit_price || 0),
                        unit_cost: Number(p.unit_cost || 0),
                        quantity: qty,
                        discount: Number(p.discount || 0),
                        requirement_date: p.requirement_date ? formatDate(p.requirement_date) : '',
                        product_class: p.product_class || '',
                        currency: p.currency || p.Currency || '',
                        characteristics: chars,
                        // Over/Under Tolerance Range — calculated immediately from
                        // this product's characteristics, same as picking a product
                        // code directly does. Previously this only appeared after
                        // opening and closing the Fast Entry (▶) modal once.
                        range: computeRangeText(chars, qty)
                    };
                });
                setNewProducts(mappedProducts);
                toast.success(`Reference SO ${selectedOption.value} loaded! ${mappedProducts.length} products mapped.`);
            } else {
                setNewProducts([]);
                toast.warning("Reference SO loaded but no products found.");
            }
        } catch (err) {
            console.error("handleRefSoSelect error:", err);
            toast.error("Failed to load reference SO data");
        }
    };

    // Mirrors handleRefSoSelect, but keyed off RFQ ID (mandatory for a new
    // Sales Order) instead of an existing SO. Same accepted-convert endpoint
    // used by the Convert flow, so header + product data comes in identically.
    const handleCreateRfqSelect = async (selectedOption) => {
        setSelectedCreateRfq(selectedOption);
        setNewSO(prev => ({ ...prev, rfqId: selectedOption ? selectedOption.value : "" }));

        // A Sales Order tied to an RFQ is always Quotation Driven — same rule
        // as the Convert-from-RFQ flow. Force it and lock the field so it
        // can't be switched to RC Driven. Clearing the RFQ ID hands control
        // back to the user for a fully manual SO.
        if (selectedOption) {
            setQuotationType({ value: "Quotation Driven", label: "Quotation Driven" });
            setQuotationTypeLocked(true);
            setRcNumber("");
        } else {
            setQuotationTypeLocked(false);
            setQuotationType(null);
            setRcNumber("");
        }

        if (!selectedOption) return;
        try {
            const res = await fetch(`${ORDER_API}/sales-order/accepted-convert/${selectedOption.value}`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) { toast.error("Failed to load RFQ data"); return; }

            setNewSO(prev => ({
                ...prev,
                salesOrganization: data.salesOrganization || prev.salesOrganization,
                customerSegment: data.customer_segment || data.customerSegment || prev.customerSegment,
                distributionChannel: data.distributionChannel || prev.distributionChannel,
                customer: data.customer ? { label: data.customer, value: data.customer } : prev.customer,
                purchaseOrder: data.purchaseOrder || prev.purchaseOrder,
                advancePayment: data.advanceAmount || prev.advancePayment,
                rfqId: selectedOption.value
            }));
            setAdvancePaymentOption(data.advancePaymentFlag || (data.advanceAmount ? "Yes" : "No"));

            if (data.markup) setMarkup(data.markup);
            if (data.customer_inspection) setCustomerInspection(data.customer_inspection);
            if (data.planning_block) setPlanningBlock(data.planning_block);
            if (data.type_of_packing) setTypeOfPacking(data.type_of_packing);
            if (data.payment_terms) setPaymentTerms(data.payment_terms);
            if (data.validity_terms) {
                const match = data.validity_terms.match(/^(\d+)\s*(.*)$/);
                setValidityNumber(match ? match[1] : data.validity_terms);
                setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
            }

            if (data.productDetails && data.productDetails.length > 0) {
                const mappedProducts = data.productDetails.map((p) => {
                    const chars = p.characteristics || [];
                    const qty = Number(p.quantity || 0);
                    return {
                        product_code: p.product_code || p.product_number || '',
                        description: p.description || '',
                        uom: p.uom || '',
                        unit_price: Number(p.unit_price || 0),
                        unit_cost: Number(p.unit_cost || 0),
                        quantity: qty,
                        discount: Number(p.discount || 0),
                        requirement_date: p.requirement_date ? formatDate(p.requirement_date) : '',
                        product_class: p.product_class || '',
                        currency: p.currency || p.Currency || '',
                        characteristics: chars,
                        range: computeRangeText(chars, qty)
                    };
                });
                setNewProducts(mappedProducts);
                toast.success(`RFQ ${selectedOption.value} loaded! ${mappedProducts.length} products mapped.`);
            } else {
                setNewProducts([]);
                toast.warning("RFQ loaded but no products found.");
            }
        } catch (err) {
            console.error("handleCreateRfqSelect error:", err);
            toast.error("Failed to load RFQ data");
        }
    };

    const handleSoDownloadTemplate = () => {
        const a = document.createElement('a');
        a.href = "/assets/files/rfq_template.xlsx";
        a.download = "so_template.xlsx";
        a.click();
    };

    const handleSoSalesFileUpload = async () => {
        if (!soSalesFile) return;
        const formData = new FormData();
        formData.append("salesFile", soSalesFile);
        try {
            const res = await fetch(`${ORDER_API}/sales-order/parse-sales-excel`, {
                headers: authHeaders(),
                method: "POST",
                body: formData
            });
            const data = await res.json();
            if (res.ok && data.data && data.data.length > 0) {
                const mappedProducts = data.data.map((row) => {
                    const chars = row.Characteristics || [];
                    const qty = row.Quantity || 0;
                    return {
                        product_code: row["Product Code"] || '',
                        description: row.Description || '',
                        uom: row.UOM || '',
                        unit_price: row.Standard_Price || 0,
                        unit_cost: row.Product_Cost || 0,
                        quantity: qty,
                        discount: row.Discount || 0,
                        requirement_date: row.Requirement_Day || '',
                        product_class: row.Product_Class || '',
                        characteristics: chars,
                        // Over/Under Tolerance Range — calculated immediately, same
                        // as picking a product code directly.
                        range: computeRangeText(chars, Number(qty) || 0)
                    };
                });
                setNewProducts(mappedProducts);
                setIsSoFileUploaded(true);
                setSoUploadMessage(`✓ ${mappedProducts.length} products loaded from file`);
                toast.success(`${mappedProducts.length} products loaded from Excel!`);
            } else {
                setSoUploadMessage("No valid products found in file.");
                toast.warning(data.message || "No valid products found.");
            }
        } catch (err) {
            console.error("SO file upload error:", err);
            toast.error("Failed to parse Excel file.");
        }
    };

    // "+ Add" button in the Characteristics (Fast Entry) modal — appends a
    // fresh duplicate Drum/Coil GROUP (No. of Drum/Coil, Length of the
    // Drum/Coil, Over Tolerance, Under Tolerance, Type of Packing, ...) so a
    // single line item can carry more than one drum/coil grouping (e.g. 8
    // drums of length 100 AND 2 drums of length 200 on the same item).
    // Clones the first occurrence of EVERY characteristic currently on the
    // row, whatever it's named — this can never silently drop a field, and
    // computeRangeText / computeQtyBasedCalc (above) already know how to
    // group repeated occurrences by name and combine them. Mirrors
    // enquiry.js / Home.js's handleAddDrumGroup, but also has to cover this
    // module's two separate flows: Create Direct SO (newProducts) and
    // Edit/View SO (selectedRfq.productDetails).
    const handleAddDrumGroup = () => {
        setSelectedCharacteristics(prev => {
            const seen = new Set();
            const baseEntries = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    baseEntries.push(c);
                }
            });
            if (baseEntries.length === 0) return prev;
            const clones = baseEntries.map(t => ({
                ...t,
                value: "",
                tolerance_applied: false
            }));
            const updated = [...prev, ...clones];

            if (createModalOpen) {
                setNewProducts(prevRows => prevRows.map((row, i) => {
                    if (i !== selectedRowIndex) return row;
                    const baseQty = parseFloat(row.quantity) || 0;
                    return { ...row, characteristics: updated, range: computeRangeText(updated, baseQty) };
                }));
            } else {
                setSelectedRfq(prevRfq => {
                    if (!prevRfq) return prevRfq;
                    const updatedProductDetails = [...prevRfq.productDetails];
                    const currentProduct = { ...updatedProductDetails[selectedRowIndex] };
                    currentProduct.characteristics = updated;
                    const baseQty = parseFloat(currentProduct.quantity) || 0;
                    currentProduct.range = computeRangeText(updated, baseQty);
                    updatedProductDetails[selectedRowIndex] = currentProduct;
                    return { ...prevRfq, productDetails: updatedProductDetails };
                });
            }

            return updated;
        });
    };

    // "Delete" icon in the Characteristics (Fast Entry) modal — removes one
    // Drum/Coil GROUP (the group at groupIdx: every characteristic's
    // groupIdx-th occurrence, same "occurrence index = group index"
    // convention handleAddDrumGroup relies on above). Mirrors
    // handleAddDrumGroup's grouping logic in reverse, and the same dual-sync
    // (createModalOpen -> newProducts, else -> selectedRfq.productDetails)
    // it uses to push the change back into the row.
    const handleRemoveDrumGroup = (groupIdx) => {
        setSelectedCharacteristics(prev => {
            const byName = {};
            const order = [];
            prev.forEach((c) => {
                const key = (c.characteristic || "").trim().toLowerCase();
                if (!byName[key]) {
                    byName[key] = [];
                    order.push(key);
                }
                byName[key].push(c);
            });

            // Never delete the last remaining group — a line item must
            // always keep at least one Drum/Coil group.
            const groupCount = Math.max(0, ...order.map(k => byName[k].length));
            if (groupCount <= 1) {
                toast.info("At least one Drum/Coil group is required.", { autoClose: 2000, hideProgressBar: true });
                return prev;
            }

            order.forEach((key) => {
                if (byName[key][groupIdx] !== undefined) {
                    byName[key].splice(groupIdx, 1);
                }
            });

            const updated = [];
            order.forEach((key) => {
                byName[key].forEach((c) => updated.push(c));
            });

            if (createModalOpen) {
                setNewProducts(prevRows => prevRows.map((row, i) => {
                    if (i !== selectedRowIndex) return row;
                    const baseQty = parseFloat(row.quantity) || 0;
                    return { ...row, characteristics: updated, range: computeRangeText(updated, baseQty) };
                }));
            } else {
                setSelectedRfq(prevRfq => {
                    if (!prevRfq) return prevRfq;
                    const updatedProductDetails = [...prevRfq.productDetails];
                    const currentProduct = { ...updatedProductDetails[selectedRowIndex] };
                    currentProduct.characteristics = updated;
                    const baseQty = parseFloat(currentProduct.quantity) || 0;
                    currentProduct.range = computeRangeText(updated, baseQty);
                    updatedProductDetails[selectedRowIndex] = currentProduct;
                    return { ...prevRfq, productDetails: updatedProductDetails };
                });
            }

            return updated;
        });
    };

    const handleAddRow = () => {
        setNewProducts([...newProducts, {
            product_code: "", description: "", uom: "", unit_price: 0, quantity: 0,
            discount: 0, markup: "", requirement_date: "", unit_cost: 0, currency: "", characteristics: []
        }]);
    };

    // Delete a row from the Create-SO product grid (same idea as
    // handleDeleteProduct used by the Edit grid, just backed by
    // newProducts instead of selectedRfq.productDetails).
    const handleDeleteRow = (index) => {
        setNewProducts(prev => prev.filter((_, i) => i !== index));
    };

    // "Type of Packing" used to be a single header-level field for the whole
    // Sales Order. It's now saved per product (like "No. of Drum/Coil" /
    // "Length of the Drum/Coil"), so it's injected into each product's own
    // characteristics list, right after its Drum/Coil length characteristic,
    // instead of living in the header. Once saved once, it's just a normal
    // entry in the row's own characteristics array and this only fills in a
    // fallback default if it's missing.
    const TYPE_OF_PACKING_LABEL = "Type of Packing";
    const withTypeOfPacking = (charList, fallbackValue = "") => {
        const list = [...charList];
        const alreadyPresent = list.some(
            (c) => (c.characteristic || "").trim().toLowerCase() === "type of packing"
        );
        if (alreadyPresent) return list;

        const drumCoilLengthIdx = list.findIndex((c) => {
            const name = (c.characteristic || "").toLowerCase();
            return name.includes("length") && (name.includes("drum") || name.includes("coil"));
        });

        const topEntry = {
            characteristic: TYPE_OF_PACKING_LABEL,
            value: fallbackValue || "",
            calculation_type: "",
        };

        if (drumCoilLengthIdx === -1) {
            list.push(topEntry);
        } else {
            list.splice(drumCoilLengthIdx + 1, 0, topEntry);
        }
        return list;
    };

    // ---- Over/Under Tolerance Range ----
    // Uses the single shared formula from ProductConfiguration.js
    // (calculateToleranceRange) — same one Enquiry (Home.js) uses — so all
    // three modules (Enquiry, Quotation, Sales Order) always compute Range
    // identically. Do not re-duplicate the min/max math here; if the
    // formula ever needs to change, change it once in ProductConfiguration.js.
    // If no tolerance % was actually entered, calculateToleranceRange still
    // returns a "min - max" string where min === max (e.g. "25 - 25").
    // Collapse that down to a single value ("25") instead of showing a fake
    // range. Same fix as Enquiry (Home.js) — keep both in sync.
    const collapseIfSameRange = (text) => {
        if (!text) return text;
        const parts = String(text).split('-').map(s => s.trim());
        if (parts.length === 2 && parts[0] !== '' && parts[1] !== '') {
            const min = parseFloat(parts[0]);
            const max = parseFloat(parts[1]);
            if (!isNaN(min) && !isNaN(max) && min === max) {
                return parts[0];
            }
        }
        return text;
    };

    const computeRangeText = (chars, baseQty) => {
        const list = chars || [];
        const isToleranceOn = (c) => c?.tolerance_applied === true || c?.tolerance_applied === 1 || c?.tolerance_applied === "1";

        // Each "+ Add" click (handleAddDrumGroup below) appends another
        // Drum/Coil GROUP (row) with its own Length / No. of Drum / Over /
        // Under Tolerance values — so any of these can appear more than once
        // in `list`. Group each of these four characteristics by occurrence
        // (1st occurrence = group 0, 2nd = group 1, ...), compute a min/max
        // range per group, then combine across groups — overall min = lowest
        // of all groups' mins, overall max = highest of all groups' maxes.
        // Mirrors enquiry.js / Home.js's group-aware Range calc.
        const byName = (matcher) => list.filter(c => matcher((c.characteristic || '').trim().toLowerCase()));
        const overEntries = byName(n => n === 'over tolerance');
        const underEntries = byName(n => n === 'under tolerance');
        const lengthEntries = byName(n => n.includes('length') && (n.includes('drum') || n.includes('coil')));
        const noOfDrumEntries = byName(n => /no\.?\s*of\s*(drum|coil)/.test(n));

        let rangeText = '';

        if (lengthEntries.length > 0 || noOfDrumEntries.length > 0) {
            // Drum/Coil-style product — group-aware range calc (supports any
            // number of "+ Add"-ed groups, including just one).
            const groupRowCount = Math.max(
                1,
                overEntries.length, underEntries.length, lengthEntries.length, noOfDrumEntries.length
            );

            let overallMin = null;
            let overallMax = null;
            let validGroupCount = 0;
            for (let g = 0; g < groupRowCount; g++) {
                const overEntry = overEntries.length > g ? overEntries[g] : overEntries[0];
                const underEntry = underEntries.length > g ? underEntries[g] : underEntries[0];
                const lengthEntry = lengthEntries.length > g ? lengthEntries[g] : lengthEntries[0];
                const noOfDrumEntry = noOfDrumEntries.length > g ? noOfDrumEntries[g] : noOfDrumEntries[0];

                const rowToleranceApplied = isToleranceOn(overEntry) || isToleranceOn(underEntry);
                const rowLengthVal = parseFloat(lengthEntry?.value) || 0;
                const rowNoOfDrumVal = parseFloat(noOfDrumEntry?.value) || 0;
                const rowOverPct = parseFloat(overEntry?.value) || 0;
                const rowUnderPct = parseFloat(underEntry?.value) || 0;
                // This group's own qty contribution — same base
                // computeQtyBasedCalc uses per group when summing Qty.
                const rowQty = rowNoOfDrumVal * rowLengthVal;
                if (!rowLengthVal && !rowQty) continue;

                const { rangeMin, rangeMax } = calculateToleranceRange({
                    toleranceApplied: rowToleranceApplied,
                    length: rowLengthVal,
                    noOfDrum: rowNoOfDrumVal,
                    qty: rowQty,
                    overPercent: rowOverPct,
                    underPercent: rowUnderPct
                });
                if (rangeMin === null || rangeMax === null) continue;

                validGroupCount++;
                if (overallMin === null || rangeMin < overallMin) overallMin = rangeMin;
                if (overallMax === null || rangeMax > overallMax) overallMax = rangeMax;
            }

            // More than one Drum/Coil group ("+ Add"-ed) on this item — a
            // single combined min-max Range here would misleadingly read as
            // one continuous span (e.g. "97 - 208") when the groups actually
            // have their own separate ranges (97-103, 192-208). Point to the
            // Fast Entry (▶) table instead, which already lists each group's
            // own Range.
            if (validGroupCount > 1) {
                rangeText = "Refer Table";
            } else if (overallMin !== null) {
                rangeText = `${overallMin} - ${overallMax}`;
            }
        } else {
            // Not a Drum/Coil-style product (no Length / No. of Drum
            // characteristics at all) — keep the original flat formula,
            // tolerance % applied against the externally-supplied baseQty.
            const overChar = overEntries[0];
            const underChar = underEntries[0];
            const toleranceApplied = isToleranceOn(overChar) || isToleranceOn(underChar);

            const { rangeText: flatRangeText } = calculateToleranceRange({
                toleranceApplied,
                length: 0,
                noOfDrum: 0,
                qty: baseQty,
                overPercent: parseFloat(overChar?.value) || 0,
                underPercent: parseFloat(underChar?.value) || 0
            });
            rangeText = flatRangeText;
        }

        return collapseIfSameRange(rangeText);
    };

    // Same "Quantity Based Calculation" product formula used inside
    // handleSaveCreateChars / onModalCharChange / handleCharacteristicChange
    // (e.g. No. of Drum/Coil x Length of Drum/Coil), extracted so it can also
    // run automatically the moment a product is selected — instead of only
    // after the user manually opens + edits/saves the Fast Entry (▶) modal.
    //
    // Group-aware: a name like "No. of Drum/Coil" can appear more than once
    // (one occurrence per "+ Add"-ed Drum/Coil group). Group qty-based
    // characteristics by name, in the order they appear, so the 1st
    // occurrence of every qty-based name forms "group 0", the 2nd occurrence
    // forms "group 1", etc. Each group's values are multiplied together, and
    // the groups' products are then summed — e.g. two groups of (8, 100) and
    // (2, 200) give (8*100) + (2*200) = 1200, instead of flattening
    // everything into one product. A qty-based characteristic that only
    // appears once (no duplicate group for it) is broadcast — its single
    // value is reused as that characteristic's factor in every group's
    // product. Mirrors enquiry.js / Home.js's group-aware Qty calc.
    const computeQtyBasedCalc = (chars) => {
        const qtyByName = {};
        (chars || []).forEach(c => {
            if (!c.calculation_type) return;
            const types = c.calculation_type.split(",").map(s => s.trim());
            if (!types.includes("Quantity Based Calculation")) return;
            const key = (c.characteristic || "").trim().toLowerCase();
            if (!qtyByName[key]) qtyByName[key] = [];
            qtyByName[key].push(c.value);
        });

        const qtyNames = Object.keys(qtyByName);
        if (qtyNames.length === 0) return 0;

        const groupCount = Math.max(...qtyNames.map(k => qtyByName[k].length));
        const groupProducts = [];
        for (let g = 0; g < groupCount; g++) {
            let groupProduct = 1;
            let groupHasBlankFactor = false;
            qtyNames.forEach(key => {
                const values = qtyByName[key];
                // Broadcast a single-occurrence characteristic's value across
                // every group; otherwise use that group's own occurrence.
                const raw = values.length > g ? values[g] : values[0];
                const val = parseFloat(raw) || 0;
                if (!val) groupHasBlankFactor = true;
                groupProduct *= val;
            });
            // If a factor within THIS group is 0/blank, only that group's
            // contribution is voided — it no longer zeroes out the whole
            // total the way one flat product used to.
            groupProducts.push(groupHasBlankFactor ? 0 : groupProduct);
        }

        return Math.round(groupProducts.reduce((sum, p) => sum + p, 0));
    };

    const handleOpenCreateCharModal = async (index) => {
        setSelectedRowIndex(index);
        const product = newProducts[index];
        const charList =
            product.characteristics ||
            product.Characteristics ||
            [];

        // Normalize tolerance_applied on every saved row (0/1/"1" -> real
        // boolean) — keep ALL rows, including duplicate Drum/Coil groups.
        const normalizedCharList = charList.map((char) => ({
            ...char,
            characteristic: char.characteristic || char.Characteristic || "",
            value: char.value || char.Value || "",
            calculation_type: char.calculation_type || char.Calculation_Type || "",
            tolerance_checkbox: char.tolerance_checkbox ?? char.Tolerance_Checkbox,
            tolerance_applied: (char.tolerance_applied !== undefined ? char.tolerance_applied : char.Tolerance_Applied) === true
                || (char.tolerance_applied !== undefined ? char.tolerance_applied : char.Tolerance_Applied) === 1
                || (char.tolerance_applied !== undefined ? char.tolerance_applied : char.Tolerance_Applied) === "1",
        }));

        let modalData = normalizedCharList;

        // Rows built from a copied Reference SO (or any row whose
        // characteristics came from the saved `characteristics` table rather
        // than fresh from Product Configuration) never carry calculation_type
        // — that table only stores characteristic/value/tolerance_applied.
        // Without it, "No. of Drum/Coil x Length of Drum/Coil" isn't
        // recognized as a Quantity Based Calculation and Qty stops
        // recalculating live. Re-fetch the product master (same call
        // toggleRowExpansion makes for the existing-SO edit table) and merge
        // in the saved values, so Fast Entry always has calculation_type
        // regardless of where the row's characteristics came from.
        //
        // IMPORTANT: the merge below maps over the SAVED rows
        // (normalizedCharList), not over the master list — mapping over the
        // master list (one entry per characteristic name) would collapse any
        // Drum/Coil "+ Add" groups down to a single row, since a by-name
        // lookup only ever keeps the LAST group's value. Mapping over the
        // saved rows instead preserves every group, however many there are.
        const productCode = product.product_code || product.product_number || "";
        if (productCode) {
            try {
                const res = await fetch(`${ORDER_API}/sales-order/fetch-product-details?productCode=${productCode}`, { headers: authHeaders() });
                const data = await res.json();
                if (res.ok && data.data && data.data[0] && Array.isArray(data.data[0].Characteristics)) {
                    const masterChars = data.data[0].Characteristics;
                    const masterByName = {};
                    masterChars.forEach(mc => {
                        const key = (mc.characteristic || "").trim().toLowerCase();
                        masterByName[key] = mc;
                    });

                    if (normalizedCharList.length > 0) {
                        modalData = normalizedCharList.map((saved) => {
                            const key = (saved.characteristic || "").trim().toLowerCase();
                            const master = masterByName[key];
                            return {
                                characteristic: saved.characteristic,
                                calculation_type: saved.calculation_type || master?.calculation_type || "",
                                tolerance_checkbox: saved.tolerance_checkbox ?? master?.tolerance_checkbox,
                                value: saved.value !== "" ? saved.value : (master?.value || ""),
                                // Keep this row's own saved tolerance checkbox
                                // state — each Drum/Coil group carries its
                                // own, independent of any other group.
                                tolerance_applied: saved.tolerance_applied,
                            };
                        });
                    } else {
                        // Brand-new row with no saved characteristics at all
                        // yet — fall back to the master list (single default
                        // group) so the modal still shows the full field set.
                        modalData = masterChars.map((mc) => ({
                            characteristic: mc.characteristic,
                            calculation_type: mc.calculation_type || "",
                            tolerance_checkbox: mc.tolerance_checkbox,
                            value: mc.value || "",
                            tolerance_applied: mc.tolerance_applied === true || mc.tolerance_applied === 1 || mc.tolerance_applied === "1",
                        }));
                    }
                }
            } catch (e) {
                console.error("Error fetching master characteristics:", e);
            }
        }

        setSelectedCharacteristics(
            withTypeOfPacking(
                modalData,
                normalizedCharList.find(c => (c.characteristic || "").trim() === TYPE_OF_PACKING_LABEL)?.value
                    || product.type_of_packing || ""
            )
        );
        setModalVisible(true);
    };

    const handleSaveCreateChars = (charsOverride) => {
        const charsToUse = charsOverride || selectedCharacteristics;

        const newQty = computeQtyBasedCalc(charsToUse);

        setNewProducts(prev => prev.map((row, i) => {
            if (i !== selectedRowIndex) return row;
            const updatedRow = {
                ...row,
                characteristics: charsToUse,
            };
            if (newQty > 0) {
                updatedRow.quantity = newQty;
            }
            const baseQty = newQty > 0 ? newQty : (parseFloat(row.quantity) || 0);
            updatedRow.range = computeRangeText(charsToUse, baseQty);
            return updatedRow;
        }));

        setModalVisible(false);
    };

    const onModalCharChange = (idx, val) => {
        const currentChar = selectedCharacteristics[idx];
        const charName = (currentChar.characteristic || "").toLowerCase();
        // Any characteristic that feeds the Quantity Based Calculation (e.g.
        // "No. of Drum/Coil", "Length of the Drum/Coil") must be numeric —
        // previously only the exact names 'factor' / 'length' were checked,
        // which meant these differently-named fields silently accepted text.
        const isQtyCalcType = (currentChar.calculation_type || "")
            .split(",")
            .map(s => s.trim())
            .includes("Quantity Based Calculation");
        const isNumericOnlyField =
            isQtyCalcType ||
            charName === 'factor' ||
            charName === 'length' ||
            charName.includes('drum') ||
            charName.includes('coil');

        // Over/Under Tolerances % must also be numeric.
        const isToleranceField = charName === 'over tolerance' || charName === 'under tolerance';

        if (isNumericOnlyField || isToleranceField) {
            if (val !== "" && !/^\d*\.?\d*$/.test(val)) {
                toast.info(isToleranceField ? "Only numeric percentages allowed." : "Only positive numbers allowed.");
                return;
            }
        }

        const updated = [...selectedCharacteristics];
        updated[idx] = { ...updated[idx], value: val };
        setSelectedCharacteristics(updated);

        if (createModalOpen) {
            // Keep newQty in sync live (not just on close) using the freshly
            // computed array, so the qty shown is never based on a stale value.
            // null = no Quantity Based Calculation characteristics at all
            // (leave quantity untouched); computeQtyBasedCalc is group-aware
            // so it correctly sums across multiple "+ Add"-ed Drum/Coil
            // groups instead of flattening them into one product.
            const hasQtyBasedChars = updated.some(c => {
                if (!c.calculation_type) return false;
                return c.calculation_type
                    .split(",")
                    .map(s => s.trim())
                    .includes("Quantity Based Calculation");
            });
            const liveQty = hasQtyBasedChars ? computeQtyBasedCalc(updated) : null;

            // Range must stay in sync live too — this module recalculates
            // quantity live on every keystroke (unlike quotation's
            // save-on-close pattern), so Range needs the same live treatment.
            setNewProducts(prev => prev.map((row, i) => {
                if (i !== selectedRowIndex) return row;
                const baseQty = liveQty !== null ? liveQty : (parseFloat(row.quantity) || 0);
                const updatedRow = { ...row, characteristics: updated, range: computeRangeText(updated, baseQty) };
                if (liveQty !== null) updatedRow.quantity = liveQty;
                return updatedRow;
            }));
        } else {
            handleCharacteristicChange(idx, val);
        }
    };


    // Over/Under Tolerances checkboxes are linked — checking either one
    // checks both, since Range is only ever calculated using both together.
    // Mirrors enquiry's handleToggleTolerance, but also has to cover this
    // module's two separate flows: Create Direct SO (newProducts) and
    // Edit/View SO (selectedRfq.productDetails).
    const handleToggleTolerance = (index) => {
        const target = selectedCharacteristics[index];
        const name = (target?.characteristic || '').trim().toLowerCase();
        if (name !== 'over tolerance' && name !== 'under tolerance') return;

        const newChecked = !target.tolerance_applied;
        const updated = selectedCharacteristics.map((c) => {
            const cName = (c.characteristic || '').trim().toLowerCase();
            if (cName === 'over tolerance' || cName === 'under tolerance') {
                return { ...c, tolerance_applied: newChecked };
            }
            return c;
        });
        setSelectedCharacteristics(updated);

        if (createModalOpen) {
            setNewProducts(prev => prev.map((row, i) => {
                if (i !== selectedRowIndex) return row;
                const baseQty = parseFloat(row.quantity) || 0;
                return { ...row, characteristics: updated, range: computeRangeText(updated, baseQty) };
            }));
        } else {
            setSelectedRfq(prevRfq => {
                if (!prevRfq) return prevRfq;
                const updatedProductDetails = [...prevRfq.productDetails];
                const currentProduct = { ...updatedProductDetails[selectedRowIndex] };
                currentProduct.characteristics = updated;
                const baseQty = parseFloat(currentProduct.quantity) || 0;
                currentProduct.range = computeRangeText(updated, baseQty);
                updatedProductDetails[selectedRowIndex] = currentProduct;
                return { ...prevRfq, productDetails: updatedProductDetails };
            });
        }
    };

    const handleNewProductChange = async (index, field, value) => {
        const updated = [...newProducts];
        updated[index][field] = value;

        if (field === 'product_code') {
            try {
                // 🆕 Pass Order Type + Customer so the GST preview shown while
                // adding a product already matches what Save will persist
                // (Export -> zero tax, instead of always computing domestic).
                const gstParams = `&orderType=${soOrderType === "export" ? "Export" : "Domestic"}&customer=${encodeURIComponent(newSO.customer?.label || "")}&companyLocation=${encodeURIComponent(newSO.salesOrganization || "")}&customerCountry=${encodeURIComponent(soOrderType === "export" ? (newSO.customerCountry || "") : "")}`;
                const res = await fetch(`${ORDER_API}/sales-order/fetch-product-details?productCode=${value}${gstParams}`, { headers: authHeaders() });
                const data = await res.json();
                if (res.ok && data.data[0]) {
                    const p = data.data[0];
                    const productChars = p.Characteristics || [];
                    const newQty = computeQtyBasedCalc(productChars);
                    updated[index] = {
                        ...updated[index],
                        description: p.Description,
                        uom: p.UOM,
                        unit_price: p.Standard_Price,
                        unit_cost: p.Product_Cost,
                        product_class: p.Product_Class,
                        currency: p.Currency || p.currency || "",
                        characteristics: productChars,
                        // GST — resolved via HSN as soon as the product is picked,
                        // so CGST/SGST/IGST preview correctly before Save instead
                        // of only appearing after the row is saved to the DB.
                        hsn_code: p.hsn_code || "",
                        cgst: p.cgst ?? 0,
                        sgst: p.sgst ?? 0,
                        igst: p.igst ?? 0,
                        // Quantity — auto-filled from Quantity-Based-Calculation
                        // characteristics on the product master, if any.
                        ...(newQty > 0 ? { quantity: newQty } : {}),
                        // Over/Under Tolerance Range — auto-calculated from the
                        // product master's default characteristics as soon as
                        // the product is picked, instead of only after manually
                        // opening + saving the Fast Entry (▶) modal.
                        range: computeRangeText(productChars, newQty > 0 ? newQty : (parseFloat(updated[index].quantity) || 0))
                    };
                }
            } catch (e) {/* */ }
        }
        setNewProducts(updated);

        // Discount vs Markup check — a line item's Discount (%) should never
        // exceed its effective Markup (%) (row's own Markup if set, else the
        // header Markup (%)). Warn immediately so the user notices while
        // still editing the row, instead of only finding out after Save.
        // toastId keys this per row so rapid keystrokes don't stack/hide it.
        if (field === 'discount' || field === 'markup') {
            const row = updated[index];
            const rowMarkupNum = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                ? (parseFloat(row.markup) || 0)
                : (parseFloat(markup) || 0);
            const discountVal = parseFloat(row.discount) || 0;
            if (discountVal > rowMarkupNum) {
                toast.warning(
                    `Discount (${discountVal}%) exceeds Markup (${rowMarkupNum}%) for product code ${row.product_code || `row ${index + 1}`}`,
                    { toastId: `disc-markup-warn-new-${row.id || index}` }
                );
            }
        }

        // Markup: same converge rule as Planning Block / Sales Block below —
        // a line-level edit only pushes up to the header once every line item
        // has converged on the same value (trivially true for a single-line
        // SO, giving it 2-way sync). Header -> line cascade is the reverse
        // direction, handled by handleDirectHeaderMarkupChange.
        if (field === 'markup') {
            const first = updated[0]?.markup;
            const firstNum = (first !== undefined && first !== null && first !== "") ? parseFloat(first) || 0 : null;
            const allSame = firstNum !== null && updated.every(p => {
                const v = (p.markup !== undefined && p.markup !== null && p.markup !== "") ? (parseFloat(p.markup) || 0) : null;
                return v === firstNum;
            });
            if (allSame) {
                setMarkup(String(firstNum));
            }
        }

        if (field === 'planning_block') {
            // Same rule as the Edit SO screen: a line-level change never forces
            // itself onto the header — it only syncs the header up once every
            // line item has converged on the same value.
            const first = updated[0]?.planning_block || 'Blocked';
            const allSame = updated.every(p => (p.planning_block || 'Blocked') === first);
            if (allSame) {
                setPlanningBlock(first);
            }
        }

        if (field === 'sales_block') {
            // Same rule as Planning Block above: a line-level edit never forces
            // itself onto the header — it only syncs the header up once every
            // line item has converged on the same value.
            const first = updated[0]?.sales_block || 'Blocked';
            const allSame = updated.every(p => (p.sales_block || 'Blocked') === first);
            if (allSame) {
                setSalesBlock(first);
            }
        }
    };

    // Header Planning Block change cascades to every line item in the
    // Create Direct SO product grid, same as the Edit SO screen.
    //eslint-disable-next-line
    const handleDirectHeaderPlanningBlockChange = (value) => {
        setPlanningBlock(value);
        setNewProducts(prev => prev.map(p => ({ ...p, planning_block: value })));
    };

    // Header Sales Block change cascades to every line item, same pattern
    // as handleDirectHeaderPlanningBlockChange above. Line -> header sync
    // (once all rows converge) is handled inside handleNewProductChange above.
    const handleDirectHeaderSalesBlockChange = (value) => {
        setSalesBlock(value);
        setNewProducts(prev => prev.map(p => ({ ...p, sales_block: value })));
    };

    // Header Markup (%) change cascades to every line item in the Create
    // Direct SO / Convert product grid — same pattern as Planning Block /
    // Sales Block above. For a single-line SO this also keeps the line
    // mirrored to the header (its one row is always "all converged"),
    // giving the 2-way sync a 1-line SO needs. Line -> header sync (once
    // all rows converge) is handled inside handleNewProductChange above.
    const handleDirectHeaderMarkupChange = (value) => {
        setMarkup(value);
        setNewProducts(prev => prev.map(p => ({ ...p, markup: value })));
    };

    // Header Req. Date ("Apply to all") for the Create / Convert product grid —
    // stamps the picked date on every line; each row can still be changed after.
    const handleDirectHeaderReqDateChange = (selectedDate) => {
        let value = '';
        if (selectedDate) {
            const yyyy = selectedDate.getFullYear();
            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const dd = String(selectedDate.getDate()).padStart(2, '0');
            value = `${yyyy}-${mm}-${dd}`;
        }
        setCreateHeaderReqDate(value);
        if (!value) return; // clearing the header never wipes the rows
        setNewProducts(prev => prev.map(p => ({ ...p, requirement_date: value })));
    };


    const clearGlobalFilters = () => {
        setGlobalFilters({
            construction: [], code: [], cableType: [], category: [],
            noOfCores: [], color: [], nominalCrossSection: [], referenceOfStandard: [], voltage: []
        });
        setFilteredProductList(null);
    };

    // Req. Date is mandatory on every product line item — enquiry, quotation
    // and sales order all enforce this the same way. getDate lets each call
    // site account for how that row's requirement date is currently stored
    // (plain field vs. the live requirementDate[id] map used while editing).
    const validateReqDatesFilled = (rows, getDate = (r) => r.requirement_date) => {
        const missingRows = (rows || [])
            .map((r, i) => ({ ok: !!getDate(r), item: i + 1 }))
            .filter(r => !r.ok);
        if (missingRows.length > 0) {
            toast.error(
                missingRows.length === (rows || []).length
                    ? "Req. Date is mandatory for all line items."
                    : `Req. Date is mandatory. Missing for item(s): ${missingRows.map(r => r.item).join(", ")}.`
            );
            return false;
        }
        return true;
    };

    const handleSaveDirectSO = async () => {
        if (!quotationType) { toast.error("Quotation Type is mandatory!"); setCreateActiveTab("basic"); return; }
        if (isRcDriven(quotationType) && !String(rcNumber).trim()) { toast.error("RC Number is mandatory!"); setCreateActiveTab("basic"); return; }
        // RFQ ID is only mandatory when Quotation Type is Quotation Driven.
        if (!isRcDriven(quotationType) && !newSO.rfqId) { toast.error("RFQ ID is mandatory when Quotation Type is Quotation Driven!"); setCreateActiveTab("basic"); return; }
        if (!newSO.purchaseOrder) { toast.error("Purchase Order is mandatory!"); setCreateActiveTab("basic"); return; }
        if (!newSO.purchaseOrderDate) { toast.error("PO Date is mandatory!"); setCreateActiveTab("basic"); return; }
        if (!newSO.customer || !newSO.salesOrganization) { toast.error("Please fill all master data fields."); return; }
        if (soOrderType === "export" && !String(newSO.customerCountry || "").trim()) { toast.error("Customer Country is mandatory for an Export order!"); setCreateActiveTab("basic"); return; }
        if (!customerInspection || !String(customerInspection).trim()) { toast.error("Customer Inspection is mandatory!"); setCreateActiveTab("basic"); return; }
        if (!salesBlock || !String(salesBlock).trim()) { toast.error("Sales Block is mandatory!"); setCreateActiveTab("basic"); return; }
        if (newProducts.length === 0) { toast.error("Please add at least one product."); return; }

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is mandatory!");
            setCreateActiveTab("basic");
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Terms and Conditions (Payment Terms) is mandatory!");
            setCreateActiveTab("basic");
            return;
        }

        if (!validateReqDatesFilled(newProducts, p => p.requirement_date)) return;

        // Every line item must have a Qty greater than 0 — a 0/blank quantity
        // is not a valid order line, so block Save until it's fixed.
        const zeroQtyItems = newProducts.filter(p => !(parseFloat(p.quantity) > 0));
        if (zeroQtyItems.length > 0) {
            toast.error("Qty must be greater than 0 for all products before saving.");
            return;
        }

        // Every line item must have a Unit Price greater than 0 — a 0/blank
        // unit price is not a valid order line, so block Save until it's fixed.
        const zeroPriceItems = newProducts.filter(p => !(parseFloat(p.unit_price) > 0));
        if (zeroPriceItems.length > 0) {
            toast.error("Unit Price must be greater than 0 for all products before saving.");
            return;
        }

        // Checklist is optional (see note below) but we still load the blank
        // template if the user never opened the Checklist tab, so there's
        // something to save against.
        let itemsToValidate = checklistItems;
        if (itemsToValidate.length === 0) {
            try {
                const res = await fetch(`${ORDER_API}/sales-order/NEW/checklist`, { headers: authHeaders() });
                const data = await res.json();
                if (data.success) {
                    itemsToValidate = data.data || [];
                    setChecklistItems(itemsToValidate);
                }
            } catch (err) {
                console.error("Checklist fetch error:", err);
            }
        }

        // Checklist is optional at Save time — no longer blocks. Whatever's
        // filled in (including nothing) gets saved as-is; it can be
        // completed later.

        setLoading(true);

        const payload = {
            ...newSO,
            customer: newSO.customer.label,
            createdBy: getCurrentUser(),
            // 🆕 orderType was never sent at all before (soOrderType lives
            // outside newSO); customerCountry came along for the ride via
            // ...newSO but was never cleared for a Domestic order, and the
            // backend had no order_type field to check regardless.
            orderType: soOrderType === "export" ? "Export" : "Domestic",
            customerCountry: soOrderType === "export" ? (newSO.customerCountry || null) : null,
            products: newProducts.map(p => {
                const basePrice = parseFloat(p.unit_price) || 0;
                const rowMarkupVal = (p.markup !== undefined && p.markup !== null && p.markup !== "")
                    ? (parseFloat(p.markup) || 0)
                    : (parseFloat(markup) || 0);
                const rowRate = soOrderType === "export"
                    ? (Number(p.exchange_rate ?? soExchangeRate) || 1)
                    : 1;
                // ✅ FIX: for an Export SO, fold markup + exchange-rate
                // conversion into the price right here — the exact number
                // shown in the Create grid's Unit Price column — and send
                // THAT as unit_price with markup 0. Previously the raw base
                // (INR, pre-markup) price was sent and the backend applied
                // markup again on its own, so the number saved never
                // matched what was shown while creating. Domestic orders
                // are unaffected — they still send the raw price + real
                // markup% and let the backend apply markup as before.
                const finalUnitPrice = soOrderType === "export"
                    ? (basePrice + (basePrice * rowMarkupVal) / 100) * rowRate
                    : basePrice;
                return {
                    ...p,
                    product_code: p.product_code || p.product_number || "",
                    // 🔧 FIX: previously always saved whatever currency the
                    // product master returned (almost always "INR"), regardless
                    // of the Customer Country / Currency picked on the Basic
                    // tab. For an Export SO the line currency should be the
                    // SO's own currency; Domestic stays on the product's own
                    // currency (or INR).
                    currency: soOrderType === "export" ? (soCurrency || p.currency || "") : (p.currency || "INR"),
                    planning_block: p.planning_block || planningBlock || "Blocked",
                    sales_block: p.sales_block || salesBlock || "Blocked",
                    // Explicit for clarity/safety — `...p` already carries `range`
                    // since handleSaveCreateChars/onModalCharChange keep it live.
                    range: p.range || "",
                    unit_price: finalUnitPrice,
                    markup: soOrderType === "export" ? 0 : p.markup,
                    exchange_rate: rowRate
                };
            }),
            purchaseOrderDate: newSO.purchaseOrderDate || null,
            payments: advancePayments,
            advancePaymentFlag: advancePaymentOption,
            advanceAmount: advancePaymentOption === "Yes" ? newSO.advancePayment : null,
            markup: markup || 0,
            customerInspection: customerInspection,
            planningBlock: planningBlock,
            salesBlock: salesBlock,
            typeOfPacking: typeOfPacking,
            customerInspectionProposedDate: customerInspectionProposedDate || null,
            customerInspectionProposedTime: customerInspectionProposedTime || null,
            customerInspectionConfirmedDate: customerInspectionConfirmedDate || null,
            customerInspectionConfirmedTime: customerInspectionConfirmedTime || null,
            validityTerms: validityNumber ? `${validityNumber} ${validityUnit}` : null,
            paymentTerms: paymentTerms,
            quotationType: quotationType ? quotationType.value : null,
            rcNumber: isRcDriven(quotationType) ? rcNumber : null,
            quotationTypeLocked: quotationTypeLocked,
            // 🆕 Exchange rate — only meaningful for Export SOs; a Domestic
            // SO sends null so every line stays at its INR base price.
            exchange_rate: soOrderType === "export" ? (soExchangeRate || null) : null
        };

        try {
            const res = await fetch(`${ORDER_API}/sales-order/create-direct`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok) {
                if (data.so_id) {
                    await saveChecklistValuesTo(data.so_id, itemsToValidate);
                    await saveRcReviewStatusesTo(data.so_id, itemsToValidate);
                    await saveChecklistMandatoryStatusesTo(data.so_id, itemsToValidate);
                    await uploadChecklistDocsForSO(data.so_id, itemsToValidate);
                }
                toast.success(data.message);
                if (data.pdfUrl) {
                    const link = document.createElement('a');
                    link.download = `Sales_Order_${data.so_id || 'Document'}.pdf`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                setCreateModalOpen(false);
                fetchSummary();
            } else {
                toast.error(data.message || "Failed to create");
            }
        } catch (e) {
            toast.error("Server Error");
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = async (selectedOption) => {
        // Customer changed → any RFQ ID picked for the PREVIOUS customer no
        // longer applies, so clear it along with the dropdown's own options
        // list before loading the new customer's RFQs.
        setNewSO({ ...newSO, customer: selectedOption, rfqId: "" });
        setSelectedCreateRfq(null);
        // Only undo the "Quotation Driven" lock if it was set BY the RFQ
        // selection we're now clearing — an independently chosen RC Driven
        // quotation type shouldn't be wiped out just because the customer changed.
        if (quotationTypeLocked) {
            setQuotationTypeLocked(false);
            setQuotationType(null);
        }

        if (selectedOption) {
            fetchApprovedRfqsForCustomer(selectedOption.label);
            try {
                const res = await fetch(`${ORDER_API}/quotation/customer-details/${selectedOption.value}`, { headers: authHeaders() });
                const data = await res.json();
                const terms = data.terms_and_conditions || data.term_and_conditions || data.payment_terms || "";
                setPaymentTerms(terms);
            } catch (err) {
                console.error(err);
                setPaymentTerms("");
            }
        } else {
            setPaymentTerms("");
            // No customer selected anymore — fall back to the full approved-RFQ
            // list rather than clearing it out; picking a customer is optional,
            // it just narrows the list when used.
            fetchApprovedRfqsForCustomer(null);
        }
    };

    // RFQ ID dropdown for Create Sales Order — every RFQ that has reached
    // "Approved" (by customer) status and hasn't already been converted into
    // a real Sales Order. Selecting a Customer first is optional: with none
    // selected this shows every approved RFQ; picking a Customer narrows the
    // list down to just that customer's RFQs (and picking an RFQ still fills
    // in its Customer either way, via handleCreateRfqSelect).
    const fetchApprovedRfqsForCustomer = async (customerLabel) => {
        try {
            const url = customerLabel
                ? `${ORDER_API}/sales-order/rfqs-for-customer?customer=${encodeURIComponent(customerLabel)}`
                : `${ORDER_API}/sales-order/rfqs-for-customer`;
            const res = await fetch(url, { headers: authHeaders() });
            const data = await res.json();
            const options = (Array.isArray(data) ? data : [])
                .map(r => ({ value: r.rfq_id, label: `${r.rfq_id} — ${r.customer || ''}` }));
            setRfqRefList(options);
        } catch (err) {
            console.error("Failed to load approved RFQs for customer:", err);
            setRfqRefList([]);
        }
    };

    // 🆕 Auto-fill Currency + Exchange Rate the moment a Customer Country is
    // picked, using the same Exchange Rate Configuration table PO's
    // fetchExchangeRateForCountry reads (Home -> <country>, latest row).
    // Domestic SOs (no country / soOrderType === "domestic") clear both and
    // leave every line at its INR base price.
    const HOME_COUNTRY = "India";
    // ⚠️ ASSUMPTION: PurchaseOrder.js calls this same endpoint as
    // `${PURCHASE_API}/exchange-rate-config` — this file has no PURCHASE_API
    // constant, so MASTER_API is used here instead. Confirm which base URL
    // Exchangerateconfig.service.js is actually mounted under and adjust if
    // it isn't reachable via MASTER_API from this module.
    const fetchExchangeRateForCustomerCountry = async (customerCountry) => {
        if (!customerCountry || soOrderType !== "export") {
            setSoCurrency("");
            setSoExchangeRate("");
            return;
        }

        const url = `${PURCHASE_API}/exchange-rate-config?from_country=${encodeURIComponent(HOME_COUNTRY)}&to_country=${encodeURIComponent(customerCountry)}`;
        try {
            const res = await fetch(url, { headers: authHeaders() });

            // A wrong base path (this was pointed at MASTER_API as a guess —
            // see the assumption note above) 404s with an HTML error page,
            // not JSON. Catching that BEFORE calling res.json() turns a
            // silent JSON.parse crash into a clear, actionable message
            // instead of the generic "Failed to fetch exchange rate" toast.
            const contentType = res.headers.get("content-type") || "";
            if (!res.ok || !contentType.includes("application/json")) {
                console.error(
                    `Exchange rate config request failed: ${res.status} ${res.statusText} for ${url}. ` +
                    `Response was not JSON (content-type: ${contentType}). ` +
                    `This almost always means the route isn't mounted at this base URL — ` +
                    `check where Exchangerateconfig.routes.js is actually app.use()'d and ` +
                    `point MASTER_API (or a dedicated constant) at that path instead.`
                );
                toast.error(
                    `Exchange rate service returned ${res.status}. The "/exchange-rate-config" ` +
                    `route may not exist under this API base — check the browser console/Network ` +
                    `tab for the exact URL, or enter Currency / Rate manually.`
                );
                setSoCurrency("");
                setSoExchangeRate("");
                return;
            }

            const data = await res.json();
            const rows = Array.isArray(data) ? data : (data.data || []);
            if (!rows.length) {
                toast.error(
                );
                setSoCurrency("");
                setSoExchangeRate("");
                return;
            }

            const match = rows[0]; // latest available (sorted year/month DESC server-side)
            setSoCurrency(match.to_currency || "");
            setSoExchangeRate(Number(match.exchange_rate) || "");
        } catch (err) {
            console.error(`Error fetching exchange rate config from ${url}:`, err);
            toast.error("Failed to fetch exchange rate for the selected Customer Country. See console for the failing URL.");
        }
    };

    // Re-fetch whenever Order Type or Customer Country changes.
    useEffect(() => {
        fetchExchangeRateForCustomerCountry(newSO.customerCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSO.customerCountry, soOrderType]);


    useEffect(() => {
        fetchSummary();
        fetchExpiredValidity();
    }, []);

    // Backs the Navbar alert bell (and the "Sales Order Validity Expired"
    // highlight/filter below) — GET /sales-order/validity/expired, keyed by
    // so_id where one exists, else rfq_id (mirrors how table rows fall back
    // to rfq_id for not-yet-converted rows elsewhere on this page).
    const fetchExpiredValidity = async () => {
        try {
            const res = await fetch(`${ORDER_API}/sales-order/validity/expired`, { headers: authHeaders() });
            const data = await res.json();
            const rows = Array.isArray(data) ? data : (data.data || []);
            setExpiredKeys(new Set(rows.map(r => r.so_id || r.rfq_id)));
        } catch (err) {
            console.error("Failed to load expired validity list:", err);
        }
    };

    const fetchSummary = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${ORDER_API}/sales-order/summary/accepted`, { headers: authHeaders() });
            const data = await res.json();
            const uniqueRfqData = data.filter((rfq, index, self) => {
                const rfqIdExists = self.some((item) => item.rfq_id === rfq.rfq_id && item.so_id !== null);
                const hasNoSoId = rfq.so_id === null;
                if (rfqIdExists && hasNoSoId) return true;
                return !hasNoSoId || !rfqIdExists;
            });

            setRfqSummary(uniqueRfqData);
            const soSeen = new Set();
            const soRefList = uniqueRfqData
                .filter(r => r.so_id && !soSeen.has(r.so_id) && soSeen.add(r.so_id))
                .map(r => ({ value: r.so_id, label: `${r.so_id} — ${r.customer || ''}` }));
            setRefSoList(soRefList);
            // NOTE: the Create Sales Order modal's "RFQ ID" dropdown (rfqRefList)
            // is no longer populated from this summary endpoint — it used to only
            // ever include an RFQ if a leftover so_id-less row already existed in
            // `sales_order`, which is why just one RFQ was showing up. It's now
            // fetched per-selected-Customer via fetchApprovedRfqsForCustomer(),
            // straight from the rfq table's approved-by-customer RFQs.
        } catch (err) {
            console.error("Failed to load RFQ summary:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSO = (soId) => {
        setSoToDelete(soId);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteSO = async () => {
        const soId = soToDelete;
        setShowDeleteConfirm(false);
        if (!soId) return;

        try {
            const res = await fetch(`${ORDER_API}/sales-order/delete/${encodeURIComponent(soId)}`, {
                headers: authHeaders(), method: "PUT"
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to delete");
            toast.success(data.message || "Sales Order deleted successfully");
            fetchSummary();
        } catch (err) {
            toast.error(err.message || "Failed to delete Sales Order");
        } finally {
            setSoToDelete(null);
        }
    };

    const dropdownOptions = useMemo(() => {
        const getUnique = (key) => [...new Set(rfqSummary.map(item => item[key]).filter(Boolean))];
        return {
            salesOrgs: getUnique('sales_organization'),
            customerSegments: getUnique('customer_segment'),
            channels: getUnique('distribution_channel'),
            customers: getUnique('customer'),
            rfqIds: getUnique('rfq_id'),
            soIds: getUnique('so_id'),
            statuses: getUnique('so_status')
        };
    }, [rfqSummary]);

    const filteredData = useMemo(() => {
        // Compare on the "YYYY-MM-DD" string form (formatDate) rather than
        // Date objects directly — avoids timezone round-trip shifts and lets
        // from/to bounds be simple string comparisons.
        const fromStr = creationDateFrom ? formatDate(creationDateFrom) : "";
        const toStr = creationDateTo ? formatDate(creationDateTo) : "";

        return rfqSummary.filter((item) => {
            const itemDateStr = item.created_at ? formatDate(item.created_at) : "";
            return (
                (!showExpiredOnly || expiredKeys.has(item.so_id || item.rfq_id)) &&
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (customerSegment === "" || item.customer_segment === customerSegment) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === null || item.customer === selectedCustomer.label || (item.customer && item.customer.includes(selectedCustomer.label))) &&
                (rfqId === "" || (item.rfq_id && item.rfq_id.includes(rfqId))) &&
                (soIdFilter === "" || (item.so_id && item.so_id === soIdFilter)) &&
                (statusFilter === "" || item.so_status === statusFilter) &&
                (fromStr === "" || (itemDateStr && itemDateStr >= fromStr)) &&
                (toStr === "" || (itemDateStr && itemDateStr <= toStr))
            );
        });
    }, [rfqSummary, showExpiredOnly, expiredKeys, salesOrganization, customerSegment, dChannel, selectedCustomer, rfqId, soIdFilter, statusFilter, creationDateFrom, creationDateTo]);

    const limit = 10;
    const totalPages = Math.max(1, Math.ceil(filteredData.length / limit));

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page]);

    useEffect(() => {
        setPage(1);
    }, [salesOrganization, customerSegment, dChannel, selectedCustomer, rfqId, soIdFilter, statusFilter, creationDateFrom, creationDateTo]);

    // Live-recalculate Validity Date on the client the moment Validity Terms
    // (number/unit) change, so the value updates instantly without waiting
    // for Save + a full refetch. Mirrors the exact same day/month/year logic
    // the backend uses (see quotation.service.js / sales_order.service.js),
    // and uses local Date getters only — never toISOString() — so IST never
    // shifts the calendar date by a day.
    useEffect(() => {
        const num = parseInt(validityNumber, 10);
        if (!validityNumber || isNaN(num) || num <= 0 || !createdDate) return;

        // createdDate is stored as "DD/MM/YYYY" (display format) — parse it
        // back into a real Date to use as the base for the calculation.
        const [dd, mm, yyyy] = createdDate.split('/').map(Number);
        if (!dd || !mm || !yyyy) return;

        const baseDate = new Date(yyyy, mm - 1, dd);
        const unit = (validityUnit || 'days').toLowerCase();

        if (unit.includes('day')) baseDate.setDate(baseDate.getDate() + num);
        else if (unit.includes('month')) baseDate.setMonth(baseDate.getMonth() + num);
        else if (unit.includes('year')) baseDate.setFullYear(baseDate.getFullYear() + num);

        setValidityDate(formatDate(baseDate));
    }, [validityNumber, validityUnit, createdDate]);

    const loadCustomerOptions = async (inputValue) => {
        try {
            const res = await fetch(`${ORDER_API}/sales-order/customers`, { headers: authHeaders() });
            const data = await res.json();
            const options = data.map(c => ({
                value: c.partner_code,
                label: `${c.partner_code} - ${c.vendor_name}`,
            }));
            if (!inputValue) return options;
            const q = inputValue.toLowerCase();
            return options.filter(opt => opt.label.toLowerCase().includes(q));
        } catch (err) {
            console.error("Customer fetch failed:", err);
            return [];
        }
    };

    function formatDate(date) {
        if (!date) return '';
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date.trim())) return date.trim();
        if (typeof date === 'string') {
            // Read the calendar date straight off the string instead of
            // round-tripping through `new Date()` — that round-trip is what
            // shifts the day back a day in some timezones.
            const clean = date.substring(0, 10);
            if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
        }
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return String(date).substring(0, 10);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Display-only formatter: same safe parsing as formatDate(), but returns
    // "DD/MM/YYYY" for read-only text instead of the "YYYY-MM-DD" that
    // <input type="date"> requires.
    function formatDateDisplay(date) {
        const iso = formatDate(date);
        if (!iso) return '';
        const [year, month, day] = iso.split('-');
        if (!year || !month || !day) return '';
        return `${day}/${month}/${year}`;
    }

    // Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
    // (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
    // UTC value sent by the server. Used for Change Log / Docs "Date & Time".
    const formatChangeLogDateTime = (dateInput) => {
        if (!dateInput) return "-";
        const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(d.getTime())) return "-";
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        const hh = parts.hour === '24' ? '00' : parts.hour;
        return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
    };

    // PO Date helpers — state holds "YYYY-MM-DD"; DatePicker works with Date
    // objects and shows dd/MM/yyyy. Local-time parse/format so IST never
    // shifts the day.
    const poDateToObj = (iso) => {
        if (!iso) return null;
        const [y, m, d] = String(iso).substring(0, 10).split('-').map(Number);
        return (y && m && d) ? new Date(y, m - 1, d) : null;
    };
    const poDateFromObj = (dt) => {
        if (!dt) return '';
        return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };

    const populateModalState = async (data) => {
        setSelectedRfq(data);
        setChecklistItems([]);
        setExpandedChecklistProduct(null);

        if (data.purchaseOrder) setPurchaseOrder(data.purchaseOrder);
        setPurchaseOrderDate(data.purchase_order_date ? formatDate(data.purchase_order_date) : "");
        setAdvancePayment(data.advanceAmount || "");
        setAdvancePaymentFlag(data.advancePaymentFlag || (data.advanceAmount ? "Yes" : "No"));
        setCreatedBy(data.created_by || data.createdBy || "");
        setCreatedDate(
            data.creation_date
                ? formatDateDisplay(data.creation_date)
                : data.created_at
                    ? formatDateDisplay(data.created_at)
                    : ""
        );
        setApprovedBy(data.approved_by || data.internal_action_by || "");
        setApprovedDate(
            data.approved_at
                ? formatDateDisplay(data.approved_at)
                : ""
        ); const firstProd = data.productDetails && data.productDetails.length > 0 ? data.productDetails[0] : {};

        const pTerms = data.payment_terms || firstProd.payment_terms;
        if (pTerms) {
            setPaymentTerms(String(pTerms));
        } else {
            setPaymentTerms("");
        }

        // Header Markup (%) is stored in its own `header_markup` column —
        // separate from each product row's own `markup` column — so it no
        // longer gets confused with (or overwritten by) any single row's
        // markup. It's still just a convenience default for brand-new
        // "+ Add Product" rows; editing it never touches existing rows'
        // per-product markup.
        setMarkup(data.header_markup !== undefined && data.header_markup !== null ? data.header_markup : "");

        // Header Req. Date — saved value if present, otherwise show the date only
        // when every line item already shares one (else leave blank).
        {
            const rowDates = (data.productDetails || []).map(p => (p.requirement_date ? formatDate(p.requirement_date) : ""));
            const allSameRowDate = rowDates.length > 0 && rowDates[0] && rowDates.every(d => d === rowDates[0]);
            setHeaderReqDate(
                data.header_req_date ? formatDate(data.header_req_date) : (allSameRowDate ? rowDates[0] : "")
            );
        }

        // 🆕 Exchange rate + currency — header-level, denormalized onto
        // every line row on the backend, so read it off either the header
        // or the first product row (same fallback pattern used above for
        // payment_terms/validity_terms).
        setSoExchangeRate(
            data.exchange_rate !== undefined && data.exchange_rate !== null
                ? data.exchange_rate
                : (firstProd.exchange_rate ?? "")
        );
        setSoCurrency(data.currency || firstProd.currency || "");
        // ✅ FIX: soOrderType/newSO.customerCountry were only ever set by the
        // Order Type dropdown's own onChange — never initialized from the
        // fetched SO. So every SO, Export included, opened with soOrderType
        // stuck at its useState default ("domestic"), which hid the Customer
        // Country field and any Export-specific display/logic even though
        // the SO was genuinely an Export order in the database.
        const fetchedOrderType = data.order_type || firstProd.order_type || "Domestic";
        setSoOrderType(String(fetchedOrderType).toLowerCase() === "export" ? "export" : "domestic");
        setNewSO(prev => ({
            ...prev,
            customerCountry: data.customer_country || firstProd.customer_country || ""
        }));
        setCustomerInspection(data.customer_inspection || firstProd.customer_inspection || "No");
        setPlanningBlock(data.planning_block || firstProd.planning_block || "Blocked");
        setSalesBlock(data.sales_block || firstProd.sales_block || "Blocked");
        setTypeOfPacking(data.type_of_packing || firstProd.type_of_packing || "");
        const soQuotationType = data.quotation_type || firstProd.quotation_type || "";
        setQuotationType(soQuotationType ? { value: soQuotationType, label: soQuotationType } : null);
        setRcNumber(data.rc_number || firstProd.rc_number || "");
        setQuotationTypeLocked(!!(data.quotation_type_locked ?? firstProd.quotation_type_locked));
        setCustomerInspectionProposedDate(data.customer_inspection_proposed_date ? String(data.customer_inspection_proposed_date).slice(0, 10) : "");
        setCustomerInspectionProposedTime(data.customer_inspection_proposed_time || "");
        setCustomerInspectionProposedSource(data.customer_inspection_proposed_source || "");
        setCustomerInspectionAssignedTo(data.customer_inspection_assigned_to || "");
        setCustomerInspectionConfirmedDate(data.customer_inspection_confirmed_date ? String(data.customer_inspection_confirmed_date).slice(0, 10) : "");
        setCustomerInspectionConfirmedTime(data.customer_inspection_confirmed_time || "");

        const vTerms = data.validity_terms || firstProd.validity_terms;
        if (vTerms) {
            const match = String(vTerms).match(/^(\d+)\s*(.*)$/);
            setValidityNumber(match ? match[1] : vTerms);
            setValidityUnit(match && match[2] ? match[2].toLowerCase() : "days");
        } else {
            setValidityNumber("");
            setValidityUnit("days");
        }

        const vDate = data.validity_date || firstProd.validity_date;
        setValidityDate(vDate ? formatDate(vDate) : null);

        const mapToObj = (field, formatter = (v) => v) => {
            const obj = {};
            if (data.productDetails) {
                data.productDetails.forEach(p => {
                    obj[p.id] = formatter(p[field]);
                });
            }
            return obj;
        };

        setOrderQty(mapToObj('quantity'));
        setRequirementDate(mapToObj('requirement_date', formatDate));
        setTentativeConfDate(mapToObj('tentative_conf_date', formatDate));
        setTentativeConfQty(mapToObj('tentative_conf_qty', v => v || ""));
        setConfirmationQty(mapToObj('confirmation_qty', v => v || ""));
        setConfirmationDate(mapToObj('confirmation_date', formatDate));
        setConfirmationTime(mapToObj('confirmation_time', v => v || ""));
        setDeliveryQty(mapToObj('delivery_qty', v => v || ""));
        setDeliveryDate(mapToObj('delivery_date', formatDate));
        setRecievingDate(mapToObj('receiving_date', formatDate));
        setTransportationDate(mapToObj('transportation_date', formatDate));
        setPackingDate(mapToObj('packing_date', formatDate));
        setPickingDate(mapToObj('picking_date', formatDate));
        setMad(mapToObj('mad', formatDate));


        let updatedProducts = [];

        if (data.productDetails && data.productDetails.length > 0) {
            updatedProducts = data.productDetails.map((product) => {
                const productCharacteristics = Array.isArray(product.characteristics)
                    ? product.characteristics
                    : Array.isArray(product.Characteristics)
                        ? product.Characteristics
                        : [];

                // Don't trust the stored range_qty/range value as-is — it may
                // have been saved before the multi-group "Refer Table" fix
                // existed, so an older order can still have a stale combined
                // "min - max" string on disk even though it has more than one
                // Drum/Coil group. Recompute from the live characteristics
                // (same computeRangeText used everywhere else) so multi-group
                // items always show "Refer Table" and single-group items
                // always show their actual range, regardless of what was
                // persisted.
                const baseQty = parseFloat(product.quantity) || 0;
                const recomputedRange = computeRangeText(productCharacteristics, baseQty);

                return {
                    ...product,
                    product_class: product.product_class || product.Product_Class || product.class || "",
                    range: recomputedRange || collapseIfSameRange(product.range_qty || product.range || ""),
                    characteristics: productCharacteristics
                };
            });
        }

        setSelectedRfq({ ...data, productDetails: updatedProducts });

        if (data.so_id) {
            fetchAdvancePayments(data.so_id);
        }

        if (data.documents && data.documents.length > 0) {
            setSoDocFiles(data.documents.map(f => ({
                name: f.file_name,
                filename: f.file_name,
                file_url: f.file_url,
                size: f.size || null,
                type: f.type || null,
                uploadedAt: f.uploadedAt || null,
                soId: data.so_id || data.rfq_id
            })));
        } else {
            setSoDocFiles([]);
        }
    };

    const handleOpenModal = async (rfqId, mode) => {
        try {
            const endpoint = mode === 'convert'
                ? `${ORDER_API}/sales-order/accepted-convert/${rfqId}`
                : `${ORDER_API}/sales-order/accepted-convert/${rfqId}`;

            const res = await fetch(endpoint, { headers: authHeaders() });
            const data = await res.json();

            await populateModalState(data);

            if (data.so_id) {
                fetchLogs(data.so_id);
            } else {
                setChangeLogs([]);
            }
            setModalMode(mode);
            setActiveTab("basic");
            setIsEditing(false);
            setShowModal(true);
        } catch (error) {
            console.error("Error fetching RFQ data:", error);
            toast.error("Failed to fetch data");
        }
    };

    const handleCancelCheckbox = (e, id) => {
        if (e.target.checked) {
            setSelectedCancelItems((prev) => [...prev, id]);
        } else {
            setSelectedCancelItems((prev) => prev.filter((item) => item !== id));
        }
    };

    const submitCancelRequest = async () => {
        if (!cancelReasonType) {
            toast.warning("Please select Cancelled by Customer or SBEE.");
            return;
        }
        try {
            const identifier = selectedRfq.so_id || selectedRfq.rfq_id;
            const res = await fetch(`${ORDER_API}/sales-order/${identifier}/request-cancel`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    itemIds: selectedCancelItems,
                    cancelReason: cancelReasonType,
                    requestedBy: getCurrentUser()
                })
            });

            const data = await res.json();
            if (res.ok) {
                toast.success(data.message || "Cancellation requested successfully!");
                setShowCancelPrompt(false);
                setSelectedCancelItems([]);
                fetchSummary();
                handleCloseModal();
            } else {
                toast.error(data.message || "Failed to request cancellation.");
            }
        } catch (err) {
            toast.error("Error connecting to server.");
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRfq(null);

        if (location.state?.returnTo) {
            navigate(location.state.returnTo, {
                state: { returnParams: location.state.returnParams }
            });
        }
    };

    const handleGenerateSO = async () => {
        if (!quotationType) {
            toast.error("Quotation Type is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (isRcDriven(quotationType) && !String(rcNumber).trim()) {
            toast.error("RC Number is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (soOrderType === "export" && !String(newSO.customerCountry || "").trim()) {
            toast.error("Customer Country is mandatory for an Export order!");
            setActiveTab("basic");
            return;
        }
        if (!purchaseOrder || purchaseOrder.trim() === "") {
            toast.error("Purchase Order is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (!customerInspection || !String(customerInspection).trim()) {
            toast.error("Customer Inspection is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (!salesBlock || !String(salesBlock).trim()) {
            toast.error("Sales Block is mandatory!");
            setActiveTab("basic");
            return;
        }

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is mandatory!");
            setActiveTab("basic");
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Terms and Conditions (Payment Terms) is mandatory!");
            setActiveTab("basic");
            return;
        }

        if (!validateReqDatesFilled(selectedRfq.productDetails, p => requirementDate[p.id] || p.requirement_date)) {
            return;
        }

        // Every line item must have a Unit Price greater than 0 — a 0/blank
        // unit price is not a valid order line, so block generation until fixed.
        const zeroPriceItemsGen = (selectedRfq.productDetails || []).filter(p => !(parseFloat(p.unit_price) > 0));
        if (zeroPriceItemsGen.length > 0) {
            toast.error("Unit Price must be greater than 0 for all products before creating the Sales Order.");
            setActiveTab("basic");
            return;
        }

        // Checklist is optional (see note below) but we still load the blank
        // template if the user never opened the Checklist tab, so there's
        // something to save against.
        let itemsToValidate = checklistItems;
        if (itemsToValidate.length === 0) {
            try {
                const res = await fetch(`${ORDER_API}/sales-order/NEW/checklist`, { headers: authHeaders() });
                const data = await res.json();
                if (data.success) {
                    itemsToValidate = data.data || [];
                    setChecklistItems(itemsToValidate);
                }
            } catch (err) {
                console.error("Checklist fetch error:", err);
            }
        }

        // Checklist is optional at Sales Order creation — no longer blocks.
        // Whatever's filled in (including nothing) gets saved as-is against
        // the SO; it can be completed later from the Checklist tab.

        setLoading(true);

        const soData = {
            purchaseOrder,
            purchaseOrderDate: purchaseOrderDate || null,
            // 🆕 Order Type (Domestic/Export) + Customer Country — Basic tab
            // fields that were only ever kept in local React state
            // (soOrderType / newSO.customerCountry) and never sent to the
            // backend, so getGstByHsn() had no way to know an SO was an
            // export and always computed domestic CGST/SGST/IGST.
            orderType: soOrderType === "export" ? "Export" : "Domestic",
            customerCountry: soOrderType === "export" ? (newSO.customerCountry || null) : null,
            advancePaymentFlag,
            advanceAmount: advancePaymentFlag === "Yes" ? advancePayment : null,
            payments: advancePayments,
            createdBy: getCurrentUser(),
            customerName: selectedRfq.customer,
            // Inspection Schedule fields set via the modal before this SO
            // existed only live in local state until now — without sending
            // them here, Convert was silently dropping whatever was set in
            // the modal pre-conversion.
            customerInspection,
            customerInspectionProposedDate: customerInspectionProposedDate || null,
            customerInspectionProposedTime: customerInspectionProposedTime || null,
            customerInspectionProposedSource: customerInspectionProposedSource || null,
            customerInspectionAssignedTo: customerInspectionAssignedTo || null,
            customerInspectionConfirmedDate: customerInspectionConfirmedDate || null,
            customerInspectionConfirmedTime: customerInspectionConfirmedTime || null,
            products: selectedRfq.productDetails.map((product) => ({
                id: product.id,
                product_class: product.product_class || product.Product_Class || product.class,
                characteristics: Array.isArray(product.characteristics)
                    ? product.characteristics
                    : Array.isArray(product.Characteristics)
                        ? product.Characteristics
                        : [],
                productNumber: product.product_code || product.product_number,
                description: product.description,
                uom: product.uom,
                unitPrice: product.unit_price,
                requirementDate: requirementDate[product.id] || product.requirement_date || null,
                tentativeConfDate: tentativeConfDate[product.id] || null,
                tentativeConfQty: tentativeConfQty[product.id] || 0,
                discount: product.discount,
                quantity: product.quantity,
                range: product.range || "",
                confirmationQty: confirmationQty[product.id] || 0,
                confirmationDate: confirmationDate[product.id] || null,
                deliveryQty: deliveryQty[product.id] || 0,
                deliveryDate: deliveryDate[product.id] || null,
                recievingDate: recievingDate[product.id] || null,
                transportationDate: transportationDate[product.id] || null,
                packingDate: packingDate[product.id] || null,
                pickingDate: pickingDate[product.id] || null,
                mad: mad[product.id] || null,
            }))
        };

        try {
            const res = await fetch(`${ORDER_API}/sales-order/createSO/${selectedRfq.rfq_id}`, {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(soData),
            });

            const data = await res.json();

            if (res.ok) {
                if (data.so_id) {
                    await saveChecklistValuesTo(data.so_id, itemsToValidate);
                    await saveRcReviewStatusesTo(data.so_id, itemsToValidate);
                    await saveChecklistMandatoryStatusesTo(data.so_id, itemsToValidate);
                    await uploadChecklistDocsForSO(data.so_id, itemsToValidate);
                }
                toast.success(data.message);
                if (data.pdfUrl) {
                    const link = document.createElement('a');
                    link.download = `Sales_Order_${data.so_id || 'Document'}.pdf`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                setShowModal(false);
                fetchSummary();
            } else {
                toast.error("Failed to generate SO ID.");
            }
        } catch (error) {
            console.error("Error creating SO:", error);
            toast.error("Error creating SO ID.");
        } finally {
            setLoading(false);
        }
    };

    const executeUpdateSO = async () => {
        // Every line item must have a Qty greater than 0 — check before we
        // even set loading, using whichever value would actually be sent
        // (a live edit in orderQty wins over the saved/calculated quantity).
        const hasZeroQtyLine = (selectedRfq.productDetails || []).some(product => {
            const pid = product.id;
            const effectiveQty = orderQty[pid] !== undefined ? orderQty[pid] : product.quantity;
            return !(parseFloat(effectiveQty) > 0);
        });
        if (hasZeroQtyLine) {
            toast.error("Qty must be greater than 0 for all products before saving.");
            return;
        }

        // Every line item must have a Unit Price greater than 0 — a 0/blank
        // unit price is not a valid order line, so block Save until it's fixed.
        const hasZeroPriceLine = (selectedRfq.productDetails || []).some(product => {
            return !(parseFloat(product.unit_price) > 0);
        });
        if (hasZeroPriceLine) {
            toast.error("Unit Price must be greater than 0 for all products before saving.");
            return;
        }

        if (!quotationTypeLocked) {
            if (!quotationType) {
                toast.error("Quotation Type is mandatory!");
                return;
            }
            if (isRcDriven(quotationType) && !String(rcNumber).trim()) {
                toast.error("RC Number is mandatory!");
                return;
            }
            if (!isRcDriven(quotationType) && !selectedRfq?.rfq_id) {
                toast.error("RFQ ID is mandatory when Quotation Type is Quotation Driven!");
                return;
            }
        }

        setLoading(true);
        try {
            const mergedProductDetails = selectedRfq.productDetails.map(product => {
                const pid = product.id;
                const qtyBasedChars = (product.characteristics || []).filter(c => {
                    if (!c.calculation_type) return false;
                    return c.calculation_type
                        .split(",")
                        .map(s => s.trim())
                        .includes("Quantity Based Calculation");
                });

                let calculatedQty = product.quantity;
                if (qtyBasedChars.length > 0) {
                    const allFilled = qtyBasedChars.every(c => parseFloat(c.value) > 0);
                    if (allFilled) {
                        calculatedQty = qtyBasedChars.reduce(
                            (product, c) => product * parseFloat(c.value), 1
                        );
                    }
                }

                return {
                    ...product,
                    id: pid,
                    quantity: orderQty[pid] !== undefined ? orderQty[pid] : calculatedQty,
                    // Explicit for clarity/safety — the `...product` spread above
                    // already carries `range` through since handleCharacteristicChange
                    // keeps it live on the row, but spelling it out here matches the
                    // other explicit fields and guards against a future refactor.
                    range: product.range || "",
                    // FIX: product.requirement_date must win — it's kept live by
                    // handleProductChange on every edit. requirementDate[pid] is a
                    // stale snapshot taken when the modal first opened, so checking
                    // it first was silently discarding any Req. Date edit on save.
                    requirement_date: product.requirement_date || requirementDate[pid],
                    tentative_conf_date: tentativeConfDate[pid] || product.tentative_conf_date || null,
                    tentative_conf_qty: tentativeConfQty[pid] || product.tentative_conf_qty || 0,
                    confirmation_qty: confirmationQty[pid] || product.confirmation_qty || 0,
                    confirmation_date: confirmationDate[pid] || product.confirmation_date || null,
                    delivery_qty: deliveryQty[pid] || product.delivery_qty || 0,
                    delivery_date: deliveryDate[pid] || product.delivery_date || null,
                    receiving_date: recievingDate[pid] || product.receiving_date || null,
                    transportation_date: transportationDate[pid] || product.transportation_date || null,
                    packing_date: packingDate[pid] || product.packing_date || null,
                    picking_date: pickingDate[pid] || product.picking_date || null,
                    mad_date: mad[pid] || product.mad_date || null
                };
            });

            const updateSoUrl = `${ORDER_API}/sales-order/update-sales-order/${selectedRfq.so_id}`;
            console.log("[SO SAVE] →", { url: updateSoUrl, method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() } });

            let response;
            try {
                response = await fetch(updateSoUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify({
                    productDetails: mergedProductDetails,
                    updatedBy: getCurrentUser(),
                    purchaseOrder,
                    purchaseOrderDate: purchaseOrderDate || null,
                    advancePaymentFlag,
                    advanceAmount: advancePaymentFlag === "Yes" ? advancePayment : "",
                    markup,
                    headerReqDate: headerReqDate || null,
                    customerInspection,
                    planningBlock,
                    salesBlock,
                    typeOfPacking,
                    customerInspectionProposedDate: customerInspectionProposedDate || null,
                    customerInspectionProposedTime: customerInspectionProposedTime || null,
                    customerInspectionConfirmedDate: customerInspectionConfirmedDate || null,
                    customerInspectionConfirmedTime: customerInspectionConfirmedTime || null,
                    // FIX: payment_terms (Terms and Conditions) was never included
                    // in this request body at all, so any edit made in the "View"
                    // popup textarea was silently discarded on Save. Backend
                    // destructures `payment_terms` (snake_case) directly, so send
                    // that exact key — matching how validity_terms is already sent.
                    payment_terms: paymentTerms,
                    validity_terms: validityNumber ? `${validityNumber} ${validityUnit}` : null,
                    validityDate: validityDate || null,
                    quotationType: quotationType ? quotationType.value : null,
                    rcNumber: isRcDriven(quotationType) ? rcNumber : null,
                    // 🆕 Exchange rate — editable from the view/edit modal too
                    // (see the Exchange Rate field added near Payment Terms).
                    exchange_rate: soExchangeRate || null
                    }),
                });
                console.log("[SO SAVE] ← status:", response.status, response.statusText, "type:", response.type, "ok:", response.ok);
            } catch (fetchErr) {
                console.error("[SO SAVE] fetch() itself threw:", fetchErr.name, fetchErr.message, fetchErr);
                throw fetchErr;
            }

            const result = await response.json();
            if (response.ok) {
                // Checklist tab has its own values but no separate save button
                // anymore — save it here alongside the rest of the SO so one
                // "Save" click persists everything.
                const checklistOk = await saveChecklist(true);

                if (checklistOk) {
                    toast.success(result.message || "Sales Order updated successfully!");
                }
                setIsEditing(false);
                // FIX: fetchSummary() only refreshes the background list — the
                // modal's own selectedRfq state (and thus the visible Unit
                // Price/Total/etc columns) was left stale until the modal was
                // closed and reopened, so a markup edit looked like it "did
                // nothing" even though the backend recalculated it correctly.
                // Re-fetch this SO's full detail and repopulate the modal so
                // the recalculated values show immediately.
                try {
                    const refreshed = await fetch(
                        `${ORDER_API}/sales-order/accepted-convert/${selectedRfq.so_id || selectedRfq.rfq_id}`,
                        { headers: authHeaders() }
                    );
                    const refreshedData = await refreshed.json();
                    await populateModalState(refreshedData);
                    // populateModalState always clears checklistItems (it doesn't
                    // know the checklist itself) — without re-fetching here, the
                    // Checklist tab shows "No checklist parameters configured yet"
                    // right after Save, until the user clicks away and back.
                    await fetchChecklist(refreshedData.so_id || selectedRfq.so_id);
                } catch (refreshErr) {
                    console.error("Failed to refresh sales order after save:", refreshErr);
                }
                fetchSummary();
            } else {
                toast.error(result.message || "Failed to update");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    // Opens the Inspection Schedule modal fresh: clears the "did the user
    // manually edit Confirmed" flag from any previous open, re-pulls the
    // Sales Order's own current inspection fields, and loads the
    // proposed-date history trail for this SO (old + replaced slots).
    const openInspectionModal = async () => {
        setConfirmedTouched(false);
        const soId = selectedRfq?.so_id;

        // Re-fetch the SO's own proposed/confirmed inspection fields before
        // showing anything. The Audit Calendar (a drag-and-drop reschedule,
        // possibly done in another tab/session) can change the current
        // proposed date/time/source/assignedTo after this Sales Order was
        // first loaded — state from that original load would otherwise show
        // a stale "current" row here even though history refreshes fine.
        //
        // IMPORTANT: only these inspection-schedule fields are re-synced
        // from the server — NOT a full populateModalState(). This modal can
        // be opened mid-edit (e.g. right after flipping Customer Inspection
        // to "Yes" but before hitting the main Save button), and a full
        // re-sync would silently overwrite that and every other unsaved
        // field on the form back to whatever's already in the DB.
        let freshProposedDate = customerInspectionProposedDate;
        if (soId) {
            try {
                const res = await fetch(`${ORDER_API}/sales-order/accepted-convert/${soId}`, { headers: authHeaders() });
                const refreshedData = await res.json();
                freshProposedDate = refreshedData.customer_inspection_proposed_date
                    ? String(refreshedData.customer_inspection_proposed_date).slice(0, 10)
                    : "";
                setCustomerInspectionProposedDate(freshProposedDate);
                setCustomerInspectionProposedTime(refreshedData.customer_inspection_proposed_time || "");
                setCustomerInspectionProposedSource(refreshedData.customer_inspection_proposed_source || "");
                setCustomerInspectionAssignedTo(refreshedData.customer_inspection_assigned_to || "");
                setCustomerInspectionConfirmedDate(refreshedData.customer_inspection_confirmed_date ? String(refreshedData.customer_inspection_confirmed_date).slice(0, 10) : "");
                setCustomerInspectionConfirmedTime(refreshedData.customer_inspection_confirmed_time || "");
            } catch (err) {
                console.error("Failed to refresh sales order before opening inspection modal:", err);
                // Fall back to whatever was already in state rather than
                // blocking the modal from opening at all.
            }
        }

        // If there's no current proposed slot yet, skip the empty-state
        // ("No proposed slots yet.") and open straight into the editable
        // add-row so the user isn't forced to click "+ Add proposed date"
        // first.
        const hasCurrentProposed = !!freshProposedDate;
        setShowAddProposedRow(!hasCurrentProposed);
        // Pre-fill today's date on the auto-opened row so the user only
        // has to pick/confirm a time; they can still change the date.
        if (!hasCurrentProposed) {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            setNewProposedDate(`${yyyy}-${mm}-${dd}`);
        } else {
            setNewProposedDate("");
        }
        setNewProposedTime("");
        setShowInspectionModal(true);
        if (!soId) {
            setInspectionHistory([]);
            return;
        }
        setInspectionHistoryLoading(true);
        fetch(`${ORDER_API}/audit-calendar/history/${soId}`, { headers: authHeaders() })
            .then((res) => res.json())
            .then((data) => {
                const history = (data && data.success !== false) ? (data.history || []) : [];
                setInspectionHistory(history);
                // History can load after the modal is already open, so
                // re-check: if it turns out there IS a past proposed slot
                // (even though there's no "current" one), don't force the
                // add-row open on top of it.
                if (!hasCurrentProposed) {
                    const hasProposedHistory = history.some(h => h.eventType === "proposed");
                    if (hasProposedHistory) setShowAddProposedRow(false);
                }
            })
            .catch(() => { /* history is supplementary — a failed fetch shouldn't block the modal */ })
            .finally(() => setInspectionHistoryLoading(false));
    };

    // Confirm button that now lives inline, next to the current proposed
    // slot in the Proposed Date History list. Confirms that exact
    // date/time — copies it into Confirmed Date/Time.
    //  - Existing SO (has so_id): writes straight to the confirmed columns
    //    via the Audit Calendar's own confirm endpoint (also logs a
    //    'confirmed' row into sales_order_inspection_history).
    //  - Not-yet-created SO (create modal, no so_id yet): there's nothing to
    //    save to yet, so this just holds the values in state — they go out
    //    with the normal Create Sales Order call.
    const handleConfirmProposedSlot = async (date, time) => {
        if (!date || !time) return;
        const soId = selectedRfq?.so_id;
        // Person Responsible is always whoever is logged in and clicking
        // Confirm right now — never typed in.
        const confirmedBy = getCurrentUser();
        if (!soId) {
            setCustomerInspectionConfirmedDate(date);
            setCustomerInspectionConfirmedTime(time);
            setCustomerInspectionAssignedTo(confirmedBy);
            setConfirmedTouched(true);
            setShowInspectionModal(false);
            toast.success("Inspection confirmed — will be saved with this Sales Order.");
            return;
        }

        setConfirmingProposed(true);
        try {
            const response = await fetch(`${ORDER_API}/audit-calendar/confirm/${soId}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmedDate: date, confirmedTime: time, assignedTo: confirmedBy }),
            });
            const result = await response.json();
            if (response.ok && result.success !== false) {
                toast.success("Inspection confirmed!");
                setCustomerInspectionConfirmedDate(date);
                setCustomerInspectionConfirmedTime(time);
                setCustomerInspectionAssignedTo(confirmedBy);
                setConfirmedTouched(true);
                setShowInspectionModal(false);
                try {
                    const refreshed = await fetch(`${ORDER_API}/sales-order/accepted-convert/${soId}`, { headers: authHeaders() });
                    const refreshedData = await refreshed.json();
                    await populateModalState(refreshedData);
                } catch (refreshErr) {
                    console.error("Failed to refresh sales order after inspection confirm:", refreshErr);
                }
                fetchSummary();
            } else {
                toast.error(result.message || "Failed to confirm inspection");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Server error");
        } finally {
            setConfirmingProposed(false);
        }
    };

    // "+" then "Save" in the Inspection Schedule modal: adds a brand-new
    // proposed date/time as a new line. Sends it through the audit-calendar
    // endpoint (not the plain SO update), because that's the one that logs
    // the slot being replaced into sales_order_inspection_history — the
    // current slot drops down into the read-only history list above.
    const [pushingProposed, setPushingProposed] = useState(false);
    const handleSaveNewProposedSlot = async () => {
        if (!newProposedDate) {
            toast.error("Set a proposed date first.");
            return;
        }
        const soId = selectedRfq?.so_id;
        // Person Responsible is always whoever is logged in right now —
        // never a free-text field.
        const proposedBy = getCurrentUser();
        if (!soId) {
            // Not-yet-created SO — nothing to push to the calendar yet, just
            // hold the value locally; it goes out with the Create call.
            setCustomerInspectionProposedDate(newProposedDate);
            setCustomerInspectionProposedTime(newProposedTime);
            setCustomerInspectionProposedSource("sales_order");
            setCustomerInspectionAssignedTo(proposedBy);
            setCustomerInspectionConfirmedDate("");
            setCustomerInspectionConfirmedTime("");
            setConfirmedTouched(false);
            setNewProposedDate("");
            setNewProposedTime("");
            setShowAddProposedRow(false);
            toast.success("Proposed slot set — will be saved with this Sales Order.");
            return;
        }
        setPushingProposed(true);
        try {
            const response = await fetch(`${ORDER_API}/audit-calendar/reschedule/${soId}`, {
                method: 'PUT',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    date: newProposedDate,
                    time: newProposedTime,
                    source: 'sales_order',
                    assignedTo: proposedBy
                }),
            });
            const result = await response.json();
            if (response.ok && result.success !== false) {
                toast.success("New proposed slot saved — the previous one is kept in history.");
                // The backend now also persists customer_inspection = 'Yes'
                // as part of this same write (a proposed date can't exist
                // without inspection being required) — mirror that here so
                // the dropdown doesn't drift back to "No" until a page
                // reload catches up.
                setCustomerInspection("Yes");
                setCustomerInspectionProposedDate(newProposedDate);
                setCustomerInspectionProposedTime(newProposedTime);
                setCustomerInspectionProposedSource("sales_order");
                setCustomerInspectionAssignedTo(proposedBy);
                // The slot just confirmed no longer matches the new proposal —
                // clear Confirmed so it reads as "not yet confirmed" until the
                // user actually hits Confirm on this new slot.
                setCustomerInspectionConfirmedDate("");
                setCustomerInspectionConfirmedTime("");
                setConfirmedTouched(false);
                setNewProposedDate("");
                setNewProposedTime("");
                setShowAddProposedRow(false);
                // Refresh the history list so the just-replaced slot shows up
                // immediately, without needing to close/reopen the modal.
                fetch(`${ORDER_API}/audit-calendar/history/${soId}`, { headers: authHeaders() })
                    .then((res) => res.json())
                    .then((data) => { if (data && data.success !== false) setInspectionHistory(data.history || []); })
                    .catch(() => { /* history is supplementary */ });
            } else {
                toast.error(result.message || "Failed to save proposed slot");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Server error");
        } finally {
            setPushingProposed(false);
        }
    };


    const handleSaveSalesOrder = async () => {
        // ✅ Every field checked here carries a red-star "mandatory" marker
        // in the Basic tab (Quotation Type, RC Number, Customer Country,
        // Purchase Order, Customer Inspection, Sales Block, Validity,
        // Terms and Conditions/Payment Terms) — previously several of
        // those stars weren't backed by any actual save-time check, so a
        // user could leave them blank and still save. Each check below
        // names the exact field in its toast and jumps to the Basic tab
        // so the user can see and fill it in immediately.
        if (!quotationType) {
            toast.error("Quotation Type is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (isRcDriven(quotationType) && !String(rcNumber).trim()) {
            toast.error("RC Number is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (soOrderType === "export" && !String(newSO.customerCountry || "").trim()) {
            toast.error("Customer Country is mandatory for an Export order!");
            setActiveTab("basic");
            return;
        }
        if (!purchaseOrder || !String(purchaseOrder).trim()) {
            toast.error("Purchase Order is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (!purchaseOrderDate) {
            toast.error("PO Date is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (!customerInspection || !String(customerInspection).trim()) {
            toast.error("Customer Inspection is mandatory!");
            setActiveTab("basic");
            return;
        }
        if (!salesBlock || !String(salesBlock).trim()) {
            toast.error("Sales Block is mandatory!");
            setActiveTab("basic");
            return;
        }

        if (!validateReqDatesFilled(selectedRfq?.productDetails || [], p => p.requirement_date || requirementDate[p.id])) {
            return;
        }

        if (!validityNumber || !String(validityNumber).trim()) {
            toast.error("Validity is mandatory!");
            setActiveTab("basic");
            return;
        }

        if (!paymentTerms || !String(paymentTerms).trim()) {
            toast.error("Terms and Conditions (Payment Terms) is mandatory!");
            setActiveTab("basic");
            return;
        }

        const currentStatus = selectedRfq?.so_status || "";
        if (currentStatus === "Approved" || currentStatus === "Partially Approved") {
            const snap = preEditSnapshotRef.current;
            const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());
            const normNum = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

            let realChangeDetected = !snap; // no snapshot captured -> be safe, warn as before

            if (snap) {
                if (
                    norm(purchaseOrder) !== norm(snap.purchaseOrder) ||
                    norm(purchaseOrderDate) !== norm(snap.purchaseOrderDate) ||
                    norm(advancePaymentFlag) !== norm(snap.advancePaymentFlag) ||
                    norm(advancePayment) !== norm(snap.advancePayment) ||
                    normNum(markup) !== normNum(snap.markup) ||
                    norm(customerInspection) !== norm(snap.customerInspection) ||
                    norm(validityNumber ? `${validityNumber} ${validityUnit}` : null) !== norm(snap.validity_terms) ||
                    norm(customerInspectionProposedDate) !== norm(snap.customerInspectionProposedDate) ||
                    norm(customerInspectionProposedTime) !== norm(snap.customerInspectionProposedTime) ||
                    norm(customerInspectionConfirmedDate) !== norm(snap.customerInspectionConfirmedDate) ||
                    norm(customerInspectionConfirmedTime) !== norm(snap.customerInspectionConfirmedTime)
                ) {
                    realChangeDetected = true;
                }

                const currentProducts = selectedRfq?.productDetails || [];
                if (currentProducts.length !== snap.productDetails.length) {
                    realChangeDetected = true;
                } else {
                    const oldById = {};
                    snap.productDetails.forEach(p => { oldById[p.id] = p; });
                    for (const p of currentProducts) {
                        const old = oldById[p.id];
                        if (!old) { realChangeDetected = true; break; }
                        if (
                            normNum(p.quantity) !== normNum(old.quantity) ||
                            normNum(p.unit_price) !== normNum(old.unit_price) ||
                            normNum(p.unit_cost) !== normNum(old.unit_cost) ||
                            normNum(p.discount) !== normNum(old.discount) ||
                            norm(p.description) !== norm(old.description) ||
                            norm(p.uom) !== norm(old.uom) ||
                            norm(p.requirement_date) !== norm(old.requirement_date)
                        ) {
                            realChangeDetected = true;
                            break;
                        }
                    }
                }
            }

            if (realChangeDetected) {
                toast.info("Note: Editing an Approved SO will reset its status to 'Pending for Approval' for re-review.");
            }
        }

        // Checklist is optional here too — if the user never opened the
        // Checklist tab, checklistItems is still empty, so pull it fresh
        // rather than trusting stale local state, just so it can be saved.
        let itemsToValidate = checklistItems;
        if (selectedRfq?.so_id && itemsToValidate.length === 0) {
            setChecklistLoading(true);
            try {
                const res = await fetch(`${ORDER_API}/sales-order/${selectedRfq.so_id}/checklist`, { headers: authHeaders() });
                const data = await res.json();
                if (data.success) {
                    itemsToValidate = data.data || [];
                    setChecklistItems(itemsToValidate);
                }
            } catch (err) {
                console.error("Checklist fetch error:", err);
            } finally {
                setChecklistLoading(false);
            }
        }

        // Checklist is optional — no longer blocks Save.

        executeUpdateSO();
    };

    // const handleViewProductDetails = async (id) => {
    //     try {
    //         setLoading(true);
    //         const response = await fetch(`${ORDER_API}/sales-order/product-details/${id}`, { headers: authHeaders() });

    //         if (response.ok) {
    //             const data = await response.json();

    //             // FIX: Safely map backend data whether it returns camelCase or snake_case
    //             const cgstAmount = Number(data.cgstAmount || data.cgst_amount || 0);
    //             const sgstAmount = Number(data.sgstAmount || data.sgst_amount || 0);
    //             const igstAmount = Number(data.igstAmount || data.igst_amount || 0);
    //             const netPrice = Number(data.netPrice || data.net_price_after_discount || 0);
    //             const unitPrice = Number(data.unit_price || data.unitPrice || 0);
    //             const quantity = Number(data.quantity || 0);

    //             setProductDetails({
    //                 ...data,
    //                 hsnCode: data.hsn_code || data.hsnCode,
    //                 cgst: data.cgst || 0,
    //                 sgst: data.sgst || 0,
    //                 igst: data.igst || 0,
    //                 netPrice: netPrice,
    //                 cgstAmount: cgstAmount,
    //                 sgstAmount: sgstAmount,
    //                 igstAmount: igstAmount,
    //                 totalGST: cgstAmount + sgstAmount + igstAmount,
    //                 finalPriceAfterGST: netPrice + cgstAmount + sgstAmount + igstAmount,
    //                 totalPrice: data.totalPrice || (unitPrice * quantity)
    //             });

    //             setShowProductModal(true);
    //         } else {
    //             const err = await response.json();
    //             toast.error(err.message || "Failed to fetch details.");
    //         }
    //     } catch (error) {
    //         console.error("Error:", error);
    //         toast.error("Network error.");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const toggleRowExpansion = async (index, product) => {

        setSelectedRowIndex(index);

        const existingCharList =
            Array.isArray(product.characteristics)
                ? product.characteristics
                : Array.isArray(product.Characteristics)
                    ? product.Characteristics
                    : [];

        // Normalize tolerance_applied on every saved row (0/1/"1" -> real
        // boolean) — keep ALL rows, including duplicate Drum/Coil groups.
        const normalizedCharList = existingCharList.map((char) => {
            const rawTA = char.tolerance_applied !== undefined ? char.tolerance_applied : char.Tolerance_Applied;
            return {
                characteristic: char.characteristic || char.Characteristic || "",
                value: char.value || char.Value || "",
                calculation_type: char.calculation_type || char.Calculation_Type || "",
                tolerance_checkbox: char.tolerance_checkbox ?? char.Tolerance_Checkbox,
                tolerance_applied: rawTA === true || rawTA === 1 || rawTA === "1",
            };
        });

        let modalData = normalizedCharList;

        // IMPORTANT: the merge below maps over the SAVED rows
        // (normalizedCharList), not over the master list — mapping over the
        // master list (one entry per characteristic name) would collapse any
        // Drum/Coil "+ Add" groups down to a single row, since a by-name
        // lookup only ever keeps the LAST group's value. Mapping over the
        // saved rows instead preserves every group, however many there are
        // — this was the bug where converting/opening a Sales Order line
        // with two Drum/Coil groups (e.g. 8×100 and 2×200) silently kept
        // only the last group.
        const productCode = product.product_code || product.product_number || "";
        if (productCode) {
            try {
                const res = await fetch(`${ORDER_API}/sales-order/fetch-product-details?productCode=${productCode}`, { headers: authHeaders() });
                const data = await res.json();
                if (res.ok && data.data && data.data[0] && Array.isArray(data.data[0].Characteristics)) {
                    const masterChars = data.data[0].Characteristics;
                    const masterByName = {};
                    masterChars.forEach(mc => {
                        const key = (mc.characteristic || "").trim().toLowerCase();
                        masterByName[key] = mc;
                    });

                    if (normalizedCharList.length > 0) {
                        modalData = normalizedCharList.map((saved) => {
                            const key = (saved.characteristic || "").trim().toLowerCase();
                            const master = masterByName[key];
                            return {
                                characteristic: saved.characteristic,
                                calculation_type: saved.calculation_type || master?.calculation_type || "",
                                // Same gate CharacteristicsModal checks —
                                // without this the checkbox never renders
                                // even when the name matches.
                                tolerance_checkbox: saved.tolerance_checkbox ?? master?.tolerance_checkbox,
                                value: saved.value !== "" ? saved.value : (master?.value || ""),
                                // Carry each group's own saved Over/Under
                                // Tolerances checkbox state across reopen —
                                // independent of any other group on the row.
                                tolerance_applied: saved.tolerance_applied,
                            };
                        });
                    } else {
                        // No previously-saved characteristics yet — fall back
                        // to the product master's own default checked state.
                        modalData = masterChars.map((mc) => ({
                            characteristic: mc.characteristic,
                            calculation_type: mc.calculation_type || "",
                            tolerance_checkbox: mc.tolerance_checkbox,
                            value: mc.value || "",
                            tolerance_applied: mc.tolerance_applied === true || mc.tolerance_applied === 1 || mc.tolerance_applied === "1",
                        }));
                    }
                }
            } catch (e) {
                console.error("Error fetching master characteristics:", e);
            }
        }

        setSelectedCharacteristics(
            withTypeOfPacking(
                modalData,
                normalizedCharList.find(c => (c.characteristic || "").trim() === TYPE_OF_PACKING_LABEL)?.value
                    || product.type_of_packing || ""
            )
        );
        setModalVisible(true);
    };

    const handleCharacteristicChange = (charIdx, value) => {
        if (!canEdit) {
            toast.error("You don't have Edit access. Please contact admin.");
            return;
        }
        if (modalMode === 'convert') {
            toast.error("Characteristics cannot be edited during conversion.");
            return;
        }

        if (modalMode === 'view' && !isEditing) {
            toast.error("Please click the 'Edit' button to modify characteristics.");
            return;
        }

        const charName = selectedCharacteristics[charIdx].characteristic.toLowerCase();

        if (charName === 'factor' || charName === 'length') {
            if (value !== "") {
                if (!/^\d*\.?\d*$/.test(value)) {
                    toast.info("Only positive numbers allowed.");
                    return;
                }
            }
        } else if (charName.includes('tolerance')) {
            if (value !== "") {
                if (!/^\d*\.?\d*$/.test(value)) {
                    toast.info("Only numeric percentages allowed.");
                    return;
                }
            }
        }

        const updatedCharacteristics = [...selectedCharacteristics];
        updatedCharacteristics[charIdx] = { ...updatedCharacteristics[charIdx], value: value };
        setSelectedCharacteristics(updatedCharacteristics);

        setSelectedRfq(prevRfq => {
            const updatedProductDetails = [...prevRfq.productDetails];
            const currentProduct = { ...updatedProductDetails[selectedRowIndex] };
            currentProduct.characteristics = updatedCharacteristics;

            // Group-aware: correctly sums across multiple "+ Add"-ed
            // Drum/Coil groups instead of flattening them into one product.
            const hasQtyBasedChars = updatedCharacteristics.some(c => {
                if (!c.calculation_type) return false;
                return c.calculation_type
                    .split(",")
                    .map(s => s.trim())
                    .includes("Quantity Based Calculation");
            });

            if (hasQtyBasedChars) {
                const integerQty = computeQtyBasedCalc(updatedCharacteristics);
                currentProduct.quantity = integerQty;
                setOrderQty(prev => ({ ...prev, [currentProduct.id]: integerQty }));
            }

            // Range must recalculate live in edit mode too — this module
            // recalculates quantity live on every keystroke (unlike
            // quotation's save-on-close pattern), so Range needs the same.
            const baseQtyForRange = orderQty[currentProduct.id] !== undefined
                ? orderQty[currentProduct.id]
                : currentProduct.quantity;
            currentProduct.range = computeRangeText(updatedCharacteristics, baseQtyForRange);

            updatedProductDetails[selectedRowIndex] = currentProduct;
            return { ...prevRfq, productDetails: updatedProductDetails };
        });
    };

    // A row whose price was typed directly (via Spl. Price) keeps following Markup (%):
    // work the pre-markup base back out of its current price, then apply the new markup.
    const repriceForMarkup = (row, prevEffMarkup, newEffMarkup) => {
        const curPrice = parseFloat(row.unit_price) || 0;
        const base = curPrice / (1 + prevEffMarkup / 100);
        return String(Math.round(base * (1 + newEffMarkup / 100) * 1e6) / 1e6);
    };

    // Spl. Price typed on an existing SO line: the typed figure is the price per unit after
    // Discount (%), so the line's final unit price = typed / (1 - discount%). Goes through
    // handleProductChange('unit_price') so it is treated exactly like a typed price already
    // was (marks the row as manual, and the backend saves it as-is).
    const handleSplPriceChange = (index, text) => {
        const row = (selectedRfq?.productDetails || [])[index] || {};
        const discPct = parseFloat(row.discount) || 0;
        const typed = parseFloat(text);
        // A line added during this edit and not saved yet has no id. The backend treats its
        // unit_price as the RAW base and applies Markup (%) on top of it, so for those lines the
        // price is worked back through the markup as well, and it is NOT marked as a manual
        // price (markup keeps applying on top of it, exactly like the backend does).
        if (!row.id && row.id !== 0) {
            const effMarkup = (row.markup !== undefined && row.markup !== null && row.markup !== '')
                ? (parseFloat(row.markup) || 0) : (parseFloat(markup) || 0);
            const newRowFactor = (1 + effMarkup / 100) * (1 - discPct / 100);
            const rawPrice = (text === "" || isNaN(typed))
                ? ""
                : String(Math.round((newRowFactor > 0 ? typed / newRowFactor : typed) * 1e6) / 1e6);
            const updatedRows = [...(selectedRfq?.productDetails || [])];
            updatedRows[index] = { ...updatedRows[index], unit_price: rawPrice };
            setSelectedRfq({ ...selectedRfq, productDetails: updatedRows });
            return;
        }
        const factor = 1 - discPct / 100;
        const finalUnit = (text === "" || isNaN(typed))
            ? ""
            : String(Math.round((factor > 0 ? typed / factor : typed) * 1e6) / 1e6);
        handleProductChange(index, 'unit_price', finalUnit);
    };

    const handleProductChange = (index, field, value) => {
        const updatedDetails = [...selectedRfq.productDetails];
        updatedDetails[index] = { ...updatedDetails[index], [field]: value };
        // Price typed via Spl. Price + Markup (%) edited afterwards -> re-derive the price from
        // its base so Spl. Price still changes with markup (backend saves the typed price as-is).
        if (field === 'markup' && manualPriceEdits[index]) {
            const prevRow = (selectedRfq?.productDetails || [])[index] || {};
            const prevEff = (prevRow.markup !== undefined && prevRow.markup !== null && prevRow.markup !== '')
                ? (parseFloat(prevRow.markup) || 0) : (parseFloat(markup) || 0);
            const newEff = (value !== undefined && value !== null && value !== '')
                ? (parseFloat(value) || 0) : (parseFloat(markup) || 0);
            updatedDetails[index] = { ...updatedDetails[index], unit_price: repriceForMarkup(prevRow, prevEff, newEff) };
        }
        setSelectedRfq({ ...selectedRfq, productDetails: updatedDetails });
        if (field === 'unit_price') {
            setManualPriceEdits(prev => ({ ...prev, [index]: true }));
        }

        // Discount vs Markup check — same rule as the "new products" table:
        // a line item's Discount (%) should never exceed its effective
        // Markup (%) (row's own Markup if set, else the header Markup (%)).
        // toastId keys this to the row so rapid keystrokes (typing a
        // multi-digit number) don't stack duplicate toasts on top of each
        // other — react-toastify just keeps the one already showing instead
        // of silently dropping/burying it. A persistent inline indicator on
        // the Discount cell (see renderProductTab) backs this up so the
        // warning doesn't depend on catching the toast at all.
        if (field === 'discount' || field === 'markup') {
            const row = updatedDetails[index];
            const rowMarkupNum = (row.markup !== undefined && row.markup !== null && row.markup !== "")
                ? (parseFloat(row.markup) || 0)
                : (parseFloat(markup) || 0);
            const discountVal = parseFloat(row.discount) || 0;
            if (discountVal > rowMarkupNum) {
                toast.warning(
                    `Discount (${discountVal}%) exceeds Markup (${rowMarkupNum}%) for product code ${row.product_code || `row ${index + 1}`}`,
                    { toastId: `disc-markup-warn-${row.id || index}` }
                );
            }
        }

        // Markup: same converge rule as Planning Block / Sales Block below —
        // a line-level edit never forces itself onto the header, EXCEPT once
        // every line item (which, for a single-line SO, is trivially always
        // true after this edit) has converged on the same value, at which
        // point the header follows. Header -> line cascade is the reverse
        // direction, handled by handleHeaderMarkupChange.
        if (field === 'markup') {
            const first = updatedDetails[0]?.markup;
            const firstNum = (first !== undefined && first !== null && first !== "") ? parseFloat(first) || 0 : null;
            const allSame = firstNum !== null && updatedDetails.every(p => {
                const v = (p.markup !== undefined && p.markup !== null && p.markup !== "") ? (parseFloat(p.markup) || 0) : null;
                return v === firstNum;
            });
            if (allSame) {
                setMarkup(String(firstNum));
            }
        }
        if (field === 'requirement_date') {
            // Header shows the date only while every line shares it; otherwise blank.
            const first = updatedDetails[0]?.requirement_date || '';
            const allSame = !!first && updatedDetails.every(p => (p.requirement_date || '') === first);
            setHeaderReqDate(allSame ? first : '');
        }
        if (field === 'planning_block') {
            // A line-level edit must NEVER push its value up to the header on its
            // own. It only syncs the header when every line item now happens to
            // share the same Planning Block value (i.e. they've all converged).
            const first = updatedDetails[0]?.planning_block || 'Blocked';
            const allSame = updatedDetails.every(p => (p.planning_block || 'Blocked') === first);
            if (allSame) {
                setPlanningBlock(first);
            }
        }

        if (field === 'sales_block') {
            // Same rule as Planning Block above: a line-level edit never forces
            // itself onto the header — it only syncs the header up once every
            // line item has converged on the same value.
            const first = updatedDetails[0]?.sales_block || 'Blocked';
            const allSame = updatedDetails.every(p => (p.sales_block || 'Blocked') === first);
            if (allSame) {
                setSalesBlock(first);
            }
        }
    };

    // Header Planning Block changes must cascade down to every line item
    // immediately (not just visually) so the saved data actually reflects it.
    //eslint-disable-next-line
    const handleHeaderPlanningBlockChange = (value) => {
        setPlanningBlock(value);
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: (prev.productDetails || []).map(p => ({ ...p, planning_block: value }))
        }));
    };

    // Same cascade for Sales Block — header -> line items (this function),
    // and line items -> header once all rows converge on one value (handled
    // inside handleProductChange above).
    const handleHeaderSalesBlockChange = (value) => {
        setSalesBlock(value);
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: (prev.productDetails || []).map(p => ({ ...p, sales_block: value }))
        }));
    };

    // Header Markup (%) changes must cascade down to every line item —
    // same pattern as Planning Block / Sales Block above. For a single-line
    // SO this also means the line always mirrors the header (since the one
    // row is, trivially, always "all converged"), giving the 2-way sync a
    // 1-line SO needs. Line -> header sync (once all rows converge) is
    // handled inside handleProductChange above.
    // Header Req. Date -> every line item (same cascade pattern as Sales Block /
    // Planning Block). Cancelled lines are left alone. A row edited by hand later
    // keeps its own date (see handleProductChange, which blanks the header when
    // the rows no longer agree).
    const handleHeaderReqDateChange = (selectedDate) => {
        let value = '';
        if (selectedDate) {
            const yyyy = selectedDate.getFullYear();
            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const dd = String(selectedDate.getDate()).padStart(2, '0');
            value = `${yyyy}-${mm}-${dd}`;
        }
        setHeaderReqDate(value);
        if (!value) return; // clearing the header never wipes the rows
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: (prev.productDetails || []).map(p =>
                String(p.item_status || p.status || '').toUpperCase().includes('CANCEL')
                    ? p
                    : { ...p, requirement_date: value }
            )
        }));
    };

    const handleHeaderMarkupChange = (value) => {
        setMarkup(value);
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: (prev.productDetails || []).map((p, i) => {
                // Rows priced via Spl. Price keep following the markup (see handleProductChange).
                if (!manualPriceEdits[i]) return { ...p, markup: value };
                const prevEff = (p.markup !== undefined && p.markup !== null && p.markup !== '')
                    ? (parseFloat(p.markup) || 0) : (parseFloat(markup) || 0);
                return { ...p, markup: value, unit_price: repriceForMarkup(p, prevEff, parseFloat(value) || 0) };
            })
        }));
    };

    const handleProductCodeChangeEdit = async (index, code) => {
        if (!code) { handleProductChange(index, 'product_code', ''); return; }
        try {
            const res = await fetch(`${ORDER_API}/sales-order/fetch-product-details?productCode=${code}`, { headers: authHeaders() });
            const data = await res.json();
            const p = Array.isArray(data?.data) ? data.data[0] : data?.data;
            setSelectedRfq(prev => {
                const updatedDetails = prev.productDetails.map((row, i) => {
                    if (i !== index) return row;
                    const base = { ...row, product_code: code };
                    if (res.ok && p) {
                        const productChars = p.Characteristics || p.characteristics || [];
                        const newQty = computeQtyBasedCalc(productChars);
                        const finalQty = newQty > 0 ? newQty : (parseFloat(row.quantity) || 0);
                        return {
                            ...base,
                            description: p.Description || '',
                            uom: p.UOM || '',
                            unit_cost: p.Product_Cost || 0,
                            unit_price: p.Standard_Price || 0,
                            product_class: p.Product_Class || '',
                            currency: p.Currency || p.currency || '',
                            characteristics: productChars,
                            Characteristics: productChars,
                            // GST — resolved via HSN as soon as the product is picked,
                            // so CGST/SGST/IGST show the correct preview before Save
                            // instead of reading as 0 until the row round-trips
                            // through the database.
                            hsn_code: p.hsn_code || '',
                            cgst: p.cgst ?? 0,
                            sgst: p.sgst ?? 0,
                            igst: p.igst ?? 0,
                            // Over/Under Tolerance Range — auto-calculated from the
                            // product master's default characteristics as soon as
                            // the product is picked, instead of only after manually
                            // opening + saving the Fast Entry (▶) modal.
                            range: computeRangeText(productChars, finalQty),
                            ...(newQty > 0 ? { quantity: finalQty } : {}),
                        };
                    }
                    return base;
                });
                return { ...prev, productDetails: updatedDetails };
            });
        } catch (e) {
            console.error('handleProductCodeChangeEdit error:', e);
            handleProductChange(index, 'product_code', code);
        }
    };

    const handleDeleteProduct = (index) => {
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: prev.productDetails.filter((_, i) => i !== index)
        }));
    };

    const handleAddRowEdit = () => {
        setSelectedRfq(prev => ({
            ...prev,
            productDetails: [
                ...prev.productDetails,
                {
                    product_code: '', description: '', uom: '',
                    unit_price: 0, unit_cost: 0, quantity: 0,
                    discount: 0, requirement_date: '',
                    product_class: '', currency: '',
                    planning_block: 'Blocked',
                    characteristics: [], Characteristics: []
                }
            ]
        }));
    };

    const renderBasicTab = () => (
        <div className="row g-3">
            <div className="col-md-8">
                <label style={styles.compactLabel}>Terms and Conditions <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="d-flex gap-2">
                    <div className="form-control bg-light" style={styles.compactInput}>
                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('payment_terms') ? <MaskedValue /> : (paymentTerms || "")}
                    </div>
                    <button
                        className="btn btn-dark btn-sm"
                        onClick={() => setShowPaymentModal(true)}
                    >
                        View
                    </button>
                </div>
            </div>
        </div>
    );

    const renderProductTab = () => {
        // prod.planning_block is now always kept in sync with reality:
        // - handleHeaderPlanningBlockChange writes the new value into every row
        //   the moment the header changes.
        // - handleProductChange writes it per-row on a line-level edit.
        // So the row simply reads its own value, falling back to the header
        // only for a brand-new row that hasn't been touched yet.
        //eslint-disable-next-line
        const getRowPlanningBlockDefault = (prod) =>
            prod.planning_block || planningBlock || "Blocked";

        // Same pattern as getRowPlanningBlockDefault above, for Sales Block.
        const getRowSalesBlockDefault = (prod) =>
            prod.sales_block || salesBlock || "Blocked";

        // 🆕 FULLY DYNAMIC TAX COLUMNS
        // Union of every tax type actually configured across all lines on
        // this SO — this is what decides how many/which tax columns the
        // grid draws, instead of a fixed CGST/SGST/IGST/"Other Tax" set.
        // CGST/SGST/IGST are pinned first (so a normal domestic SO still
        // looks exactly like it always did); anything else configured on
        // the GST Configuration screen (BCD, Cess, a custom Import type,
        // etc.) is appended in the order it's first seen. A line with no
        // dynamic taxes[] yet (old row saved before sales_order_tax
        // existed) falls back to its legacy cgst/sgst/igst columns so old
        // data keeps rendering instead of going blank.
        const getRowTaxTypes = (prod) => {
            if (Array.isArray(prod.taxes) && prod.taxes.length) {
                return prod.taxes.map(t => String(t.type || "").trim()).filter(Boolean);
            }
            const hasLegacyRate = prod.cgst !== undefined && prod.cgst !== null && prod.cgst !== '';
            return hasLegacyRate ? ["CGST", "SGST", "IGST"] : [];
        };

        const PRIORITY_TAX_ORDER = ["CGST", "SGST", "IGST"];
        const taxColumnSet = new Set();
        selectedRfq.productDetails.forEach((prod) => {
            getRowTaxTypes(prod).forEach((t) => taxColumnSet.add(t));
        });
        const dynamicTaxColumns = [
            ...PRIORITY_TAX_ORDER.filter(t => taxColumnSet.has(t)),
            ...[...taxColumnSet].filter(t => !PRIORITY_TAX_ORDER.includes(t))
        ];
        // Nothing resolved anywhere yet (brand-new SO, no HSN matched) —
        // show the historical CGST/SGST/IGST headings as the empty default
        // rather than rendering a tax section with zero columns.
        const effectiveTaxColumns = dynamicTaxColumns.length ? dynamicTaxColumns : PRIORITY_TAX_ORDER;

        // Shared per-row calculator used for both the TOTALS strip and each
        // table row below, so they can never disagree. While editing, Unit
        // Price reacts live to the Markup (%) field (reversing the row's
        // snapshotted old markup and reapplying the new one — the same math
        // the backend runs on Save) and Total/Discounted/Net/GST cascade from
        // that. When not editing, everything is read straight off the DB
        // values on the row, exactly as saved.
        const computeRow = (prod, idx) => {
            const quantity = parseFloat(prod.quantity) || 0;
            const disc = parseFloat(prod.discount) || 0;

            const storedUnitPrice = parseFloat(prod.unit_price) || 0;
            const storedNet = parseFloat(prod.net_price_after_discount) || 0;
            const storedCgstAmt = parseFloat(prod.cgst_amount) || 0;
            const storedSgstAmt = parseFloat(prod.sgst_amount) || 0;
            const storedIgstAmt = parseFloat(prod.igst_amount) || 0;

            let newUnitPrice = storedUnitPrice;
            if (isEditing && !manualPriceEdits[idx]) {
                // A brand-new row added via "+ Add Product" during this edit
                // session has no entry here (the snapshot was only taken once,
                // for the rows that already existed when Edit was clicked).
                // Fall back to its own current unit price with 0 prior markup
                // so the live recalculation below still applies — otherwise
                // Markup (%) typed into a new row's own column had no effect.
                const snap = editPriceSnapshot[idx] || { unitPrice: storedUnitPrice, markup: 0 };
                // FIX: previously always used the single header-level `markup`
                // field for every row. Now each row has its own editable
                // Markup (%) column (prod.markup) which drives this row's
                // Unit Price independently — editing it here never writes
                // back to the header field. The header value is only used
                // as a fallback for a row that has no markup of its own yet
                // (e.g. a brand-new "+ Add Product" row).
                const markupVal = (prod.markup !== undefined && prod.markup !== null && prod.markup !== '')
                    ? (parseFloat(prod.markup) || 0)
                    : (parseFloat(markup) || 0);
                const oldMarkupVal = parseFloat(snap.markup) || 0;
                const basePrice = snap.unitPrice > 0
                    ? snap.unitPrice / (1 + oldMarkupVal / 100)
                    : 0;
                newUnitPrice = Math.round((basePrice + (basePrice * markupVal) / 100) * 100) / 100;
            }

            // Unit Price column = the REFERENCE price (before Markup and Discount). It must not
            // move when Markup (%) or Discount (%) are applied — only Spl. Price does. While
            // editing it comes from the snapshot taken at Edit-click (the same base the live
            // markup maths above uses); otherwise from the saved price and the markup saved on
            // the row.
            let baseUnitPrice;
            if (isEditing) {
                const snapForBase = editPriceSnapshot[idx] || { unitPrice: storedUnitPrice, markup: 0 };
                const snapMk = parseFloat(snapForBase.markup) || 0;
                baseUnitPrice = snapForBase.unitPrice > 0 ? snapForBase.unitPrice / (1 + snapMk / 100) : 0;
            } else {
                const savedMk = (prod.markup !== undefined && prod.markup !== null && prod.markup !== '')
                    ? (parseFloat(prod.markup) || 0)
                    : (parseFloat(markup) || 0);
                baseUnitPrice = storedUnitPrice > 0 ? storedUnitPrice / (1 + savedMk / 100) : 0;
            }

            const grossPrice = isEditing
                ? newUnitPrice * quantity
                : (parseFloat(prod.total_price) || 0);
            const discountedPriceRow = isEditing
                ? grossPrice - (disc * grossPrice) / 100
                : (parseFloat(prod.discounted_price) || 0);
            const netPrice = isEditing ? discountedPriceRow : storedNet;

            // Spl. Price = the price per unit AFTER Markup (%) and Discount (%) — the same
            // figure as the Spl. Price column on the quotation. Net Price / Qty, so
            // Spl. Price x Qty always equals the Net Price shown beside it.
            const splPrice = quantity > 0
                ? netPrice / quantity
                : newUnitPrice - (newUnitPrice * disc) / 100;

            // Rate implied by the last-saved amounts. Held constant through
            // the live preview so it doesn't need a fresh HSN lookup on every
            // keystroke — except for a brand-new row that hasn't been saved
            // yet, where storedNet is 0 and this division would always give
            // 0% regardless of the product's real GST rate. For that case
            // (and any row that already carries a rate % straight from the
            // product master lookup), use the rate % directly instead.
            const hasRatePct = prod.cgst !== undefined && prod.cgst !== null && prod.cgst !== '';

            // 🆕 FULLY DYNAMIC per-row tax breakdown — one { type: amount }
            // entry for every tax type actually configured for THIS line's
            // HSN (via prod.taxes: [{type, percent, value}], see
            // getGstByHsn / getAcceptedConvert / getProductDetails), not
            // just an assumed CGST/SGST/IGST split. A row whose HSN doesn't
            // use a given column (from effectiveTaxColumns, computed across
            // all rows above) simply has no key for it — the grid cell
            // renders 0 for that column instead of a made-up value.
            const rowTaxes = (Array.isArray(prod.taxes) && prod.taxes.length)
                ? prod.taxes
                : (hasRatePct
                    // Legacy row saved before sales_order_tax existed —
                    // synthesize from the fixed columns so old data still
                    // renders under the CGST/SGST/IGST columns.
                    ? [
                        { type: "CGST", percent: parseFloat(prod.cgst) || 0, value: storedCgstAmt },
                        { type: "SGST", percent: parseFloat(prod.sgst) || 0, value: storedSgstAmt },
                        { type: "IGST", percent: parseFloat(prod.igst) || 0, value: storedIgstAmt },
                    ]
                    : []
                );

            const taxBreakdown = {};
            rowTaxes.forEach((t) => {
                const type = String(t.type || "").trim();
                if (!type) return;
                const pct = parseFloat(t.percent) || 0;
                const amount = isEditing
                    ? netPrice * pct / 100
                    : (t.value !== undefined && t.value !== null ? parseFloat(t.value) || 0 : netPrice * pct / 100);
                taxBreakdown[type] = (taxBreakdown[type] || 0) + amount;
            });

            // Kept for anything that still expects the old named fields
            // (the single-product "View" popup handoff below) — now just
            // read straight out of the dynamic breakdown.
            const liveCgst = taxBreakdown["CGST"] || 0;
            const liveSgst = taxBreakdown["SGST"] || 0;
            const liveIgst = taxBreakdown["IGST"] || 0;
            const liveOtherTax = Object.entries(taxBreakdown)
                .filter(([type]) => !["CGST", "SGST", "IGST"].includes(type))
                .reduce((sum, [, v]) => sum + v, 0);

            const liveBreakdownTotal = Object.values(taxBreakdown).reduce((s, v) => s + v, 0);
            const totalGstRow = isEditing
                ? liveBreakdownTotal
                : (parseFloat(prod.total_gst) || liveBreakdownTotal);

            // ✅ FIX: don't trust the stored final_price_after_gst column —
            // it was computed on the backend from only cgst/sgst/igst and
            // silently dropped any dynamic tax type (Basic Custom Duty,
            // Surge Duty, Export Duty, Other charges), so Export/custom-tax
            // rows saved before the backend fix still carry a stale
            // "net price only" value. totalGstRow above is already derived
            // from the real per-row tax breakdown (prod.taxes), so always
            // recompute the grand total from it instead of preferring the
            // column.
            const grandTotalRow = netPrice + totalGstRow;

            return {
                quantity, disc, newUnitPrice, grossPrice, discountedPriceRow,
                netPrice, splPrice, baseUnitPrice, liveCgst, liveSgst, liveIgst, liveOtherTax,
                taxBreakdown, rowTaxes,
                totalGstRow, grandTotalRow
            };
        };

        const totals = selectedRfq.productDetails.reduce((acc, row, idx) => {
            const r = computeRow(row, idx);
            const taxByType = { ...acc.taxByType };
            effectiveTaxColumns.forEach((t) => {
                taxByType[t] = (taxByType[t] || 0) + (r.taxBreakdown[t] || 0);
            });
            return {
                gross: acc.gross + r.grossPrice,
                discounted: acc.discounted + r.discountedPriceRow,
                net: acc.net + r.netPrice,
                taxByType,
                totalGst: acc.totalGst + r.totalGstRow,
                grand: acc.grand + r.grandTotalRow
            };
        }, { gross: 0, discounted: 0, net: 0, taxByType: {}, totalGst: 0, grand: 0 });

        const eligibleCancelIds = selectedRfq.productDetails
            .filter(prod => {
                const actionStr = prod.so_action ? prod.so_action.toLowerCase() : "";
                const isAllowed = actionStr === 'approved' ||
                    (
                        ['pending for approval', 'partial cancellation initiated'].includes(selectedRfq.so_status?.toLowerCase()) &&
                        (actionStr === '' || actionStr === 'pending' || actionStr === 'cancel_rejected')
                    );
                const isAlreadyCancelled = ['cancel initiated', 'cancel_requested', 'cancellation approved', 'cancelled'].includes(actionStr);
                return isAllowed && !isAlreadyCancelled;
            })
            .map(prod => prod.id);

        const isAllSelected = eligibleCancelIds.length > 0 && selectedCancelItems.length === eligibleCancelIds.length;

        const handleSelectAllCancel = (e) => {
            if (e.target.checked) {
                setSelectedCancelItems(eligibleCancelIds);
            } else {
                setSelectedCancelItems([]);
            }
        };

        return (
            <div>
                {isEditing && (
                    <div className="d-flex justify-content-end mb-2">
                        <button className="btn btn-sm btn-dark" onClick={handleAddRowEdit}>+ Add Product</button>
                    </div>
                )}

                {isEditing && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}></span>
                            {[
                                { key: 'category', opts: filterMasterData.categories, label: 'Category' },
                                { key: 'cableType', opts: filterMasterData.cableTypes, label: 'Cable Type' },
                                { key: 'code', opts: filterMasterData.codes, label: 'Code' },
                                { key: 'color', opts: filterMasterData.colours, label: 'Color' },
                                { key: 'noOfCores', opts: filterMasterData.cores, label: 'Cores' },
                                { key: 'nominalCrossSection', opts: filterMasterData.nominalCrossSections, label: 'Cross Section' },
                                { key: 'referenceOfStandard', opts: filterMasterData.referenceOdStandards, label: 'Std. Ref' },
                                { key: 'voltage', opts: filterMasterData.voltages, label: 'Voltage' },
                                { key: 'construction', opts: filterMasterData.constructions, label: 'Construction' },
                            ].map(({ key, opts, label }) => (
                                <div
                                    key={key}
                                    title={
                                        globalFilters[key] && globalFilters[key].length > 0
                                            ? globalFilters[key].map(o => o.label).join(', ')
                                            : undefined
                                    }
                                    style={{ minWidth: 120, flex: '1 1 110px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}
                                >
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#64748b',
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '0.02em',
                                        paddingLeft: 2
                                    }}>
                                        {label}
                                    </span>
                                    <Select
                                        options={getFilterOptions(opts)}
                                        value={globalFilters[key] && globalFilters[key].length > 0 ? globalFilters[key] : null}
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        formatOptionLabel={(opt) => (
                                            <span title={opt.label}>{opt.label}</span>
                                        )}
                                        onChange={selectedOpts => {
                                            const selected = selectedOpts || [];
                                            const updated = { ...globalFilters, [key]: selected };
                                            setGlobalFilters(updated);
                                            const anyActive = Object.values(updated).some(v => Array.isArray(v) ? v.length > 0 : v !== '');
                                            if (!anyActive) { setFilteredProductList(null); return; }
                                            const matchesFilter = (productVal, selectedVals) => {
                                                if (!selectedVals || selectedVals.length === 0) return true;
                                                return selectedVals.some(s => String(productVal || '').trim().toLowerCase() === s.value.toLowerCase());
                                            };
                                            setFilteredProductList(productList.filter(p =>
                                                matchesFilter(p.construction, updated.construction) &&
                                                matchesFilter(p.code, updated.code) &&
                                                matchesFilter(p.cableType, updated.cableType) &&
                                                matchesFilter(p.category, updated.category) &&
                                                matchesFilter(p.noOfCores, updated.noOfCores) &&
                                                matchesFilter(p.color, updated.color) &&
                                                matchesFilter(p.nominalCrossSection, updated.nominalCrossSection) &&
                                                matchesFilter(p.referenceOfStandard, updated.referenceOfStandard) &&
                                                matchesFilter(p.voltage, updated.voltage)
                                            ));
                                        }}
                                        isClearable
                                        placeholder={label}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            control: (base) => ({ ...base, minHeight: 30, fontSize: '12px' }),
                                            valueContainer: (base) => ({ ...base, padding: '2px 6px', flexWrap: 'wrap' }),
                                            input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                                            indicatorsContainer: (base) => ({ ...base, alignSelf: 'flex-start', paddingTop: 2 }),
                                            dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                            clearIndicator: (base) => ({ ...base, padding: 2 }),
                                            placeholder: (base) => ({ ...base, fontSize: '12px', color: '#94a3b8', fontWeight: 400 }),
                                            option: (base, state) => ({
                                                ...base, fontSize: '12px', fontWeight: 400,
                                                color: state.isSelected ? '#fff' : '#334155',
                                                backgroundColor: state.isSelected ? '#475569' : state.isFocused ? '#f1f5f9' : '#fff',
                                                padding: '6px 10px',
                                            }),
                                            multiValue: (base) => ({ ...base, fontSize: '11px', backgroundColor: '#e2e8f0', borderRadius: 4 }),
                                            multiValueLabel: (base) => ({ ...base, fontSize: '11px', color: '#334155', padding: '1px 4px' }),
                                            multiValueRemove: (base) => ({ ...base, color: '#64748b', ':hover': { backgroundColor: '#cbd5e1', color: '#1e293b' } }),
                                            menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                            menu: (base) => ({ ...base, fontSize: '12px' }),
                                        }}
                                    />
                                </div>
                            ))}
                            {filteredProductList && (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    style={{ fontSize: 11, whiteSpace: 'nowrap', height: 30, padding: '0 10px' }}
                                    onClick={clearGlobalFilters}
                                >
                                    ✕ Clear ({filteredProductList.length})
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {modalMode !== 'convert' && !isEditing && (
                    <div className="d-flex justify-content-end mb-2">
                        <button
                            className="btn btn-sm"
                            disabled={selectedCancelItems.length === 0}
                            onClick={() => {
                                if (!canEdit) {
                                    toast.error("You don't have Edit access. Please contact admin.");
                                    return;
                                }
                                setCancelReasonType("");
                                setShowCancelPrompt(true);
                            }}
                            style={{
                                backgroundColor: selectedCancelItems.length === 0 ? '#e2e8f0' : '#212529',
                                borderColor: selectedCancelItems.length === 0 ? '#cbd5e1' : '#212529',
                                color: selectedCancelItems.length === 0 ? '#94a3b8' : 'white',
                                cursor: selectedCancelItems.length === 0 ? 'not-allowed' : 'pointer',
                                fontWeight: '600'
                            }}
                        >
                            Cancel Selected
                        </button>
                    </div>
                )}

                <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto' }}>
                    <table className="table mb-0" style={styles.erpTable}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}>
                                    {modalMode !== 'convert' && !isEditing && (
                                        <input
                                            type="checkbox"
                                            disabled={eligibleCancelIds.length === 0}
                                            checked={isAllSelected}
                                            onChange={handleSelectAllCancel}
                                        />
                                    )}
                                </th>
                                <th style={styles.erpTh}>Item</th>
                                <th style={{ ...styles.erpTh, minWidth: '150px' }}>Product Code</th>
                                <th style={styles.erpTh}>Description</th>
                                <th style={styles.erpTh}>Fast entry</th>
                                <th style={{ ...styles.erpTh, minWidth: '110px' }}>Sales Block</th>
                                <th style={{ ...styles.erpTh, minWidth: '140px' }}>Sales Released At</th>
                                <th style={styles.erpTh}>UOM</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Range</th>
                                <th style={{ ...styles.erpTh, textAlign: 'center' }}>Currency</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Unit Price</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Spl. Price</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Markup (%)</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total Price</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Disc (%)</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Net Price</th>
                                {/* 🆕 One column per tax type actually configured across this
                                    SO's lines (effectiveTaxColumns, computed above) — 3 for a
                                    normal domestic order, 4/5/etc. for Import or any HSN with
                                    extra duties (BCD, Cess, ...), instead of a fixed CGST/SGST/
                                    IGST/"Other Tax" set that silently dropped anything past 3. */}
                                {effectiveTaxColumns.map((taxType) => (
                                    <th key={taxType} style={{ ...styles.erpTh, textAlign: 'right' }}>{taxType}</th>
                                ))}
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total Tax</th>
                                <th style={{ ...styles.erpTh, textAlign: 'right' }}>
                                    {/* unit_price for an Export line is already the final,
                                        converted-to-order-currency selling price (fixed at
                                        Create/Save time) — so there is exactly one Grand Total,
                                        always in the SO's real currency, no separate converted
                                        column needed. */}
                                    Grand Total ({soCurrency || 'INR'})
                                </th>
                                <th style={{ ...styles.erpTh, minWidth: '150px' }}>
                                    Req. Date <span style={{ color: '#dc2626' }}>*</span>
                                    {isEditing && (
                                        <div style={{ marginTop: 4, fontWeight: 400 }}>
                                            <DatePicker
                                                className="form-control form-control-sm"
                                                wrapperClassName="w-100"
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="Apply to all"
                                                title="Pick a date to apply to all products (you can still change each product)"
                                                selected={headerReqDate ? new Date(headerReqDate + 'T00:00:00') : null}
                                                onChange={handleHeaderReqDateChange}
                                                isClearable
                                            />
                                        </div>
                                    )}
                                </th>
                                <th style={{ ...styles.erpTh, width: 60, textAlign: 'center' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedRfq.productDetails.map((prod, idx) => {
                                const {
                                    quantity, disc, newUnitPrice, grossPrice,
                                    //eslint-disable-next-line
                                    discountedPriceRow, netPrice, liveCgst,
                                    liveSgst, liveIgst, liveOtherTax,
                                    taxBreakdown, rowTaxes, totalGstRow,
                                    grandTotalRow, splPrice, baseUnitPrice
                                } = computeRow(prod, idx);

                                // unit_price is already the final value that was saved
                                // (converted into the order's currency at Create/Save time
                                // for an Export line) — shown as-is, no further conversion.
                                // Unit Price shows the reference (pre-markup) price; Spl. Price carries the change.
                                const displayUnitPrice = baseUnitPrice;

                                const actionStr = prod.so_action ? prod.so_action.toLowerCase() : "";
                                const isCancelAllowed = actionStr === 'approved' ||
                                    (
                                        ['pending for approval', 'partial cancellation initiated'].includes(selectedRfq.so_status?.toLowerCase()) &&
                                        (actionStr === '' || actionStr === 'pending' || actionStr === 'cancel_rejected')
                                    );
                                const isAlreadyCancelled = ['cancel initiated', 'cancel_requested', 'cancellation approved', 'cancelled'].includes(actionStr);

                                // Persistent (non-toast) indicator: true whenever this row's
                                // Discount (%) exceeds its effective Markup (%) — same rule
                                // used everywhere else (row's own Markup if set, else header
                                // Markup). Drives the red outline/label on the Discount cell
                                // below so the warning stays visible regardless of whether the
                                // transient toast was seen while typing.
                                const effectiveRowMarkup = (prod.markup !== undefined && prod.markup !== null && prod.markup !== '')
                                    ? (parseFloat(prod.markup) || 0)
                                    : (parseFloat(markup) || 0);
                                const isDiscOverMarkup = disc > effectiveRowMarkup;

                                return (
                                    <tr key={prod.id || prod.product_number || idx}>
                                        <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                            {modalMode !== 'convert' && !isEditing && (
                                                <input
                                                    type="checkbox"
                                                    disabled={!isCancelAllowed || isAlreadyCancelled}
                                                    checked={selectedCancelItems.includes(prod.id)}
                                                    onChange={(e) => handleCancelCheckbox(e, prod.id)}
                                                />
                                            )}
                                        </td>

                                        <td style={styles.erpTd}>{!isEditing && !isFieldVisible('item_no') ? <MaskedValue /> : idx + 1}</td>
                                        <td style={{ ...styles.erpTd, overflow: 'visible', minWidth: 160 }}>
                                            {isEditing ? (
                                                <CreatableSelect
                                                    classNamePrefix="react-select"
                                                    options={filteredProductList !== null ? filteredProductList : productList}
                                                    value={
                                                        (prod.product_code || prod.product_number)
                                                            ? {
                                                                value: prod.product_code || prod.product_number,
                                                                label: prod.description
                                                                    ? `${prod.product_code || prod.product_number} - ${prod.description}`
                                                                    : (prod.product_code || prod.product_number)
                                                            }
                                                            : null
                                                    }
                                                    onChange={(opt) => handleProductCodeChangeEdit(idx, opt ? opt.value : '')}
                                                    placeholder="Select or Type"
                                                    isSearchable
                                                    isClearable
                                                    backspaceRemovesValue={false}
                                                    closeMenuOnSelect={false}
                                                    blurInputOnSelect={false}
                                                    openMenuOnFocus
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{
                                                        control: (base) => ({ ...base, minHeight: '30px', height: '30px', fontSize: '13px', minWidth: '140px' }),
                                                        valueContainer: (base) => ({ ...base, height: '30px', padding: '0 8px' }),
                                                        input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                                        indicatorsContainer: (base) => ({ ...base, height: '30px' }),
                                                        dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                                        option: (base, state) => ({
                                                            ...base,
                                                            fontSize: '13px',
                                                            fontWeight: 400,
                                                            color: state.isSelected ? '#fff' : '#334155',
                                                            backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : '#fff',
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                        }),
                                                        menu: (base) => ({ ...base, fontSize: '13px' })
                                                    }}
                                                />
                                            ) : (!isFieldVisible('product_code') ? <MaskedValue /> : (prod.product_code || prod.product_number))}
                                        </td>
                                        <td style={styles.erpTd}>{!isEditing && !isFieldVisible('item_description') ? <MaskedValue /> : prod.description}</td>
                                        <td style={styles.erpTd}>
                                            {prod.product_class || prod.Product_Class || prod.class || "-"}
                                            <span style={{ cursor: 'pointer', marginLeft: 8 }} onClick={() => toggleRowExpansion(idx, prod)}>
                                                {expandedRows[idx] ? '▼' : '▶'}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.erpTd, minWidth: '110px' }}>
                                            {isEditing ? (
                                                <select
                                                    className="form-control form-control-sm"
                                                    style={{ minWidth: '95px' }}
                                                    value={getRowSalesBlockDefault(prod)}
                                                    onChange={(e) => handleProductChange(idx, 'sales_block', e.target.value)}
                                                >
                                                    <option value="Blocked">Blocked</option>
                                                    <option value="Released">Released</option>
                                                </select>
                                            ) : (prod.sales_block || "Blocked")}
                                        </td>
                                        <td style={{ ...styles.erpTd, minWidth: '140px', whiteSpace: 'nowrap' }}>
                                            {(prod.sales_block === 'Released' && prod.sales_released_at) ? prod.sales_released_at : '—'}
                                        </td>
                                        <td style={styles.erpTd}>{!isEditing && !isFieldVisible('uom') ? <MaskedValue /> : prod.uom}</td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                            {!isEditing && !isFieldVisible('quantity') ? <MaskedValue /> : Math.round(quantity)}
                                        </td>

                                        {/* Range — read-only, calculated from Over/Under Tolerances via Characteristics (▶) */}
                                        <td style={{ ...styles.erpTd, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: prod.range ? '#16a34a' : '#94a3b8' }}>
                                                {prod.range || '—'}
                                            </span>
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 3, padding: '2px 6px' }}>
                                                {/* 🔧 FIX: same currency-follows-header behavior as the Add
                                                    Product grid — an Export line shows the SO's own currency
                                                    instead of the product master's (usually INR). */}
                                                {soOrderType === "export"
                                                    ? (soCurrency || prod.currency || prod.Currency || "")
                                                    : (prod.currency || prod.Currency || "INR")}
                                            </span>
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 80, textAlign: 'right', MozAppearance: 'textfield', backgroundColor: '#e9ecef' }}
                                                    value={Math.round(displayUnitPrice * 100) / 100} disabled={true} title="Unit Price is the reference price (before Markup and Discount) - read-only" />
                                            ) : (!isFieldVisible('unit_price') ? <MaskedValue /> : formatCurrency(displayUnitPrice))}
                                        </td>

                                        {/* 🆕 Spl. Price — the price per unit AFTER Markup (%) and Discount (%). It
                                            recalculates as either changes, and can also be typed directly: the typed figure
                                            is the final unit price, and the row's price is worked back from it. */}
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 80, textAlign: 'right', MozAppearance: 'textfield' }}
                                                    value={splEdit && splEdit.grid === 'main' && splEdit.idx === idx ? splEdit.text : Math.round(splPrice * 100) / 100}
                                                    onFocus={() => setSplEdit({ grid: 'main', idx, text: String(Math.round(splPrice * 100) / 100) })}
                                                    onBlur={() => setSplEdit(null)}
                                                    onChange={(e) => {
                                                        setSplEdit({ grid: 'main', idx, text: e.target.value });
                                                        handleSplPriceChange(idx, e.target.value);
                                                    }}
                                                    title="Spl. Price (after Markup and Discount). Type a price to set it directly." />
                                            ) : (!isFieldVisible('unit_price') ? <MaskedValue /> : formatCurrency(splPrice))}
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                            {isEditing ? (
                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 60, textAlign: 'right', MozAppearance: 'textfield' }}
                                                    value={prod.markup ?? ''}
                                                    onChange={(e) => handleProductChange(idx, 'markup', e.target.value)}
                                                    title="Markup for this product only. If this is the only line item, it stays in sync with the header Markup (%)."
                                                />
                                            ) : (!isFieldVisible('line_markup') ? <MaskedValue /> : (prod.markup !== undefined && prod.markup !== null && prod.markup !== '' ? prod.markup : '—'))}
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 500 }}>
                                            {!isFieldVisible('total_price') ? <MaskedValue /> : formatCurrency(grossPrice)}
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                            {isEditing ? (
                                                <>
                                                    <input type="number" className="form-control form-control-sm no-spinner"
                                                        style={{
                                                            width: 60, textAlign: 'right', MozAppearance: 'textfield',
                                                            ...(isDiscOverMarkup ? { borderColor: '#dc2626', boxShadow: '0 0 0 1px #dc2626' } : {})
                                                        }}
                                                        value={prod.discount} onChange={(e) => handleProductChange(idx, 'discount', e.target.value)}
                                                        title={isDiscOverMarkup ? `Discount (${disc}%) exceeds Markup (${effectiveRowMarkup}%)` : undefined}
                                                    />
                                                    {isDiscOverMarkup && (
                                                        <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                            &gt; Markup
                                                        </div>
                                                    )}
                                                </>
                                            ) : (!isFieldVisible('discount') ? <MaskedValue /> : prod.discount)}
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{!isEditing && !isFieldVisible('net_price_after_discount') ? <MaskedValue /> : formatCurrency(netPrice)}</td>
                                        {/* 🆕 One cell per column in effectiveTaxColumns — this row's
                                            amount for that tax type, or 0 if this line's HSN doesn't
                                            use that particular type at all. */}
                                        {effectiveTaxColumns.map((taxType) => (
                                            <td key={taxType} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                {!isEditing && !isFieldVisible('total_gst') ? <MaskedValue /> : formatCurrency(taxBreakdown[taxType] || 0)}
                                            </td>
                                        ))}
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{!isEditing && !isFieldVisible('total_gst') ? <MaskedValue /> : formatCurrency(totalGstRow)}</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{!isEditing && !isFieldVisible('final_price_after_gst') ? <MaskedValue /> : formatCurrency(grandTotalRow)}</td>

                                        <td style={styles.erpTd}>
                                            {isEditing ? (
                                                <DatePicker
                                                    className="form-control form-control-sm"
                                                    wrapperClassName="w-100"
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="dd/mm/yyyy"
                                                    selected={prod.requirement_date ? new Date(formatDate(prod.requirement_date)) : null}
                                                    onChange={(selectedDate) => {
                                                        let newVal = '';
                                                        if (selectedDate) {
                                                            const yyyy = selectedDate.getFullYear();
                                                            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                            const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                            newVal = `${yyyy}-${mm}-${dd}`;
                                                        }
                                                        handleProductChange(idx, 'requirement_date', newVal);
                                                    }}
                                                />
                                            ) : (!isFieldVisible('requirement_date') ? <MaskedValue /> : (prod.requirement_date ? formatDateDisplay(prod.requirement_date) : "N/A"))}
                                        </td>

                                        <td style={{ ...styles.erpTd, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {!isEditing && (
                                                <button className="btn btn-sm btn-outline-dark py-0" style={{ fontSize: 12 }}
                                                    onClick={() => {
                                                        // ✅ FIX: previously this called handleViewProductDetails(prod.id),
                                                        // which re-fetched the row from the DB and showed the RAW
                                                        // unit_price (e.g. 88) with no markup applied — while this same
                                                        // row, one column over, was already showing the marked-up
                                                        // unit_price (e.g. 96.80). Same line item, two different
                                                        // numbers on the same screen. Reusing the values already
                                                        // computed for this row guarantees the popup always matches
                                                        // what's on screen, with no second source of truth.
                                                        setProductDetails({
                                                            ...prod,
                                                            unit_price: newUnitPrice,
                                                            quantity,
                                                            discount: disc,
                                                            totalPrice: grossPrice,
                                                            netPrice,
                                                            cgstAmount: liveCgst,
                                                            sgstAmount: liveSgst,
                                                            igstAmount: liveIgst,
                                                            otherTaxAmount: liveOtherTax,
                                                            // 🆕 Full dynamic breakdown (every type, not just CGST/
                                                            // SGST/IGST + one lumped "Other Tax") for the view popup
                                                            // to render as its own row per type.
                                                            taxes: Object.entries(taxBreakdown).map(([type, value]) => ({
                                                                type,
                                                                value,
                                                                percent: rowTaxes.find(t => String(t.type || "").trim() === type)?.percent || 0
                                                            })),
                                                            totalGST: totalGstRow,
                                                            finalPriceAfterGST: grandTotalRow,
                                                            exchangeRate: prod.exchange_rate ?? soExchangeRate,
                                                            currency: prod.currency || soCurrency
                                                        });
                                                        setShowProductModal(true);
                                                    }}>View</button>
                                            )}
                                            {isEditing && (
                                                <button
                                                    title="Delete product"
                                                    onClick={() => handleDeleteProduct(idx)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px', lineHeight: 1, padding: '2px 6px' }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {selectedRfq.productDetails.length > 0 && (
                                <>
                                    <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: '700' }}>
                                        <td colSpan={14} style={{ ...styles.erpTd, textAlign: 'right', color: '#64748b' }}>TOTALS:</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.gross)}</td>
                                        <td style={{ ...styles.erpTd }}></td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.net)}</td>
                                        {/* 🆕 Same effectiveTaxColumns set as the header/rows above,
                                            so a TOTALS cell always lines up under its own column. */}
                                        {effectiveTaxColumns.map((taxType) => (
                                            <td key={taxType} style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                {formatCurrency(totals.taxByType[taxType] || 0)}
                                            </td>
                                        ))}
                                        <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalGst)}</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '14px' }}>{formatCurrency(totals.grand)}</td>
                                        <td colSpan={2}></td>
                                    </tr>

                                    <tr style={{ backgroundColor: '#fff', fontWeight: '600' }}>
                                        <td colSpan={22} style={{ ...styles.erpTd, textAlign: 'right' }}>Less: Total Paid:</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right', }}>
                                            - {formatCurrency(advancePayments.reduce((sum, p) => sum + Number(p.advance || p.amount || 0), 0))}
                                        </td>
                                        <td colSpan={2}></td>
                                    </tr>
                                    <tr style={{ backgroundColor: '#e2e8f0', borderTop: '2px solid #cbd5e1', fontWeight: '700', fontSize: '15px' }}>
                                        <td colSpan={22} style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a' }}>REMAINING BALANCE:</td>
                                        <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a' }}>
                                            {formatCurrency(totals.grand - advancePayments.reduce((sum, p) => sum + Number(p.advance || p.amount || 0), 0))}
                                        </td>
                                        <td colSpan={2}></td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderConfirmationTab = () => {
        // Once line items have gone through Review, the Confirmation form
        // should only cover the ones actually approved — a rejected or
        // still-pending product code has no business getting delivery dates.
        const approvedCodes = getApprovedProductCodes();
        const confirmationProducts = approvedCodes
            ? selectedRfq.productDetails.filter((p) => approvedCodes.has(p.product_code || p.product_number))
            : selectedRfq.productDetails;

        if (approvedCodes && !confirmationProducts.length) {
            return (
                <div style={styles.tableContainer}>
                    <div className="text-center text-muted p-3">
                        No approved products yet — Confirmation details will appear here once a product is approved in Review.
                    </div>
                </div>
            );
        }

        return (
            <div style={{ ...styles.tableContainer, overflowX: 'auto' }}>
                <table className="table mb-0" style={{ ...styles.erpTable, whiteSpace: 'nowrap' }}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>Item</th>
                            <th style={{ ...styles.erpTh, minWidth: '120px' }}>Product Code</th>
                            <th style={{ ...styles.erpTh, minWidth: '200px' }}>Description</th>
                            <th style={styles.erpTh}>Order Qty</th>
                            <th style={styles.erpTh}>Req. Date <span style={{ color: '#dc2626' }}>*</span></th>
                            <th style={styles.erpTh}>Tentative Conf. Date</th>
                            <th style={styles.erpTh}>Tentative Conf. Qty</th>
                            <th style={styles.erpTh}>Final Conf. Date</th>
                            <th style={styles.erpTh}>Final Conf. Time</th>
                            <th style={styles.erpTh}>Qty</th>
                            <th style={styles.erpTh}>Delivery Date</th>
                            <th style={styles.erpTh}>Qty</th>
                        </tr>
                    </thead>
                    <tbody>
                        {confirmationProducts.map((prod, idx) => {
                            const pid = prod.id;
                            const disabled = true;

                            const inputStyle = { minWidth: '100px' };
                            // const dateStyle = { minWidth: '130px' };

                            return (
                                <tr key={pid}>
                                    <td style={styles.erpTd}>{idx + 1}</td>
                                    <td style={styles.erpTd}>{prod.product_code || prod.product_number}</td>
                                    <td style={{ ...styles.erpTd, whiteSpace: 'normal' }}>{prod.description}</td>
                                    <td style={styles.erpTd}>
                                        <input type="number" className="form-control form-control-sm" disabled={disabled} style={inputStyle}
                                            value={orderQty[pid] || prod.quantity}
                                            onChange={(e) => setOrderQty({ ...orderQty, [pid]: e.target.value })} />
                                    </td>
                                    <td style={styles.erpTd}>
                                        {prod.requirement_date ? formatDateDisplay(prod.requirement_date) : "N/A"}
                                    </td>
                                    <td style={styles.erpTd}>
                                        <DatePicker
                                            className="form-control form-control-sm"
                                            wrapperClassName="w-100"
                                            disabled={disabled}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="dd/mm/yyyy"
                                            selected={tentativeConfDate[pid] ? new Date(tentativeConfDate[pid]) : null}
                                            onChange={(selectedDate) => {
                                                let newVal = '';
                                                if (selectedDate) {
                                                    const yyyy = selectedDate.getFullYear();
                                                    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                    const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                    newVal = `${dd}-${mm}-${yyyy}`;
                                                }
                                                setTentativeConfDate({ ...tentativeConfDate, [pid]: newVal });
                                            }}
                                        />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <input type="number" className="form-control form-control-sm" disabled={disabled} style={inputStyle}
                                            value={tentativeConfQty[pid] || ''}
                                            onChange={(e) => setTentativeConfQty({ ...tentativeConfQty, [pid]: e.target.value })} />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <DatePicker
                                            className="form-control form-control-sm"
                                            wrapperClassName="w-100"
                                            disabled={disabled}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="dd/mm/yyyy"
                                            selected={confirmationDate[pid] ? new Date(confirmationDate[pid]) : null}
                                            onChange={(selectedDate) => {
                                                let newVal = '';
                                                if (selectedDate) {
                                                    const yyyy = selectedDate.getFullYear();
                                                    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                    const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                    newVal = `${yyyy}-${mm}-${dd}`;
                                                }
                                                setConfirmationDate({ ...confirmationDate, [pid]: newVal });
                                            }}
                                        />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <input type="time" className="form-control form-control-sm" disabled={disabled} style={inputStyle}
                                            value={confirmationTime[pid] || ''}
                                            onChange={(e) => setConfirmationTime({ ...confirmationTime, [pid]: e.target.value })} />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <input type="number" className="form-control form-control-sm" disabled={disabled} style={inputStyle}
                                            value={confirmationQty[pid] || ''}
                                            onChange={(e) => setConfirmationQty({ ...confirmationQty, [pid]: e.target.value })} />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <DatePicker
                                            className="form-control form-control-sm"
                                            wrapperClassName="w-100"
                                            disabled={disabled}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="dd/mm/yyyy"
                                            selected={deliveryDate[pid] ? new Date(deliveryDate[pid]) : null}
                                            onChange={(selectedDate) => {
                                                let newVal = '';
                                                if (selectedDate) {
                                                    const yyyy = selectedDate.getFullYear();
                                                    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                    const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                    newVal = `${yyyy}-${mm}-${dd}`;
                                                }
                                                setDeliveryDate({ ...deliveryDate, [pid]: newVal });
                                            }}
                                        />
                                    </td>
                                    <td style={styles.erpTd}>
                                        <input type="number" className="form-control form-control-sm" disabled={disabled} style={inputStyle}
                                            value={deliveryQty[pid] || ''}
                                            onChange={(e) => setDeliveryQty({ ...deliveryQty, [pid]: e.target.value })} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const getGrandTotal = () => {
        // ✅ FIX: for an existing/converting RFQ (selectedRfq.productDetails),
        // sum the raw final_price_after_gst already stored on each row —
        // don't recompute "unitPrice + markup%" here either, same reasoning
        // as renderProductTab above. The create-direct-SO flow (createModalOpen)
        // still has to calculate live, since those rows have no DB record yet.
        if (!createModalOpen) {
            const allItems = selectedRfq?.productDetails || [];
            // Payment History's "Total Price" (and the balance/remaining
            // calculations derived from it) should only reflect line items
            // that have actually been approved in Review — pending/rejected
            // lines shouldn't inflate what the customer is expected to pay.
            const approvedCodes = getApprovedProductCodes();
            const items = approvedCodes
                ? allItems.filter((p) => approvedCodes.has(p.product_code || p.product_number))
                : allItems;
            return items.reduce((grand, prod) => {
                // ✅ FIX: don't prefer the stored final_price_after_gst — it
                // was computed on the backend from only cgst/sgst/igst and
                // silently dropped dynamic tax types (Basic Custom Duty,
                // Surge Duty, Export Duty, Other charges), so Export/custom-
                // tax rows saved before the backend fix still carry a stale
                // "net price only" value. Always derive from net price + the
                // real per-row tax breakdown (prod.taxes, which is what's
                // actually persisted correctly in sales_order_tax).
                const net = parseFloat(prod.net_price_after_discount) || 0;
                const dynamicTaxes = Array.isArray(prod.taxes) ? prod.taxes : [];
                const totalGst = dynamicTaxes.length
                    ? dynamicTaxes.reduce((s, t) => s + (parseFloat(t.value) || 0), 0)
                    : (parseFloat(prod.total_gst) ||
                        ((parseFloat(prod.cgst_amount) || 0) + (parseFloat(prod.sgst_amount) || 0) + (parseFloat(prod.igst_amount) || 0)));
                return grand + net + totalGst;
            }, 0);
        }

        const items = newProducts;
        const markupVal = parseFloat(markup) || 0;
        let grand = 0;

        items.forEach(prod => {
            const unitPrice = parseFloat(prod.unit_price) || 0;
            const quantity = parseFloat(prod.quantity) || 0;
            const disc = parseFloat(prod.discount) || 0;

            // Markup applied on Unit Price -> newUnitPrice; Total/Discounted/Net derive from it.
            const newUnitPrice = unitPrice + (unitPrice * markupVal) / 100;
            const gross = newUnitPrice * quantity;
            const discounted = gross - (disc * gross) / 100;
            const net = discounted;

            const cgstRate = (parseFloat(prod.cgst) || 0) / 100;
            const sgstRate = (parseFloat(prod.sgst) || 0) / 100;
            const igstRate = (parseFloat(prod.igst) || 0) / 100;

            const cgst = net * cgstRate;
            const sgst = net * sgstRate;
            const igst = net * igstRate;

            grand += (net + cgst + sgst + igst);
        });
        return grand;
    };

    useEffect(() => {
        const isPaymentTabOpen =
            (showModal && (activeTab === "Payment History" || activeTab === "payment history" || activeTab === "payment_history")) ||
            (createModalOpen && createActiveTab === "payment_history");

        if (isPaymentTabOpen) {
            setPaymentHistoryTabAmount('');
            setPaymentHistoryTabDescription('');
            setPaymentHistoryTabReference('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, createActiveTab, showModal, createModalOpen, advancePayments, newProducts, selectedRfq, markup]);

    // Populates the form above the table with an existing payment row's
    // values so the user can edit it, and switches the form into "edit mode"
    // — same pattern as the Purchase Order screen.
    const handleEditPaymentClick = (idx, row) => {
        setEditingPaymentIdx(idx);
        setPaymentHistoryTabType(row.payment_type || row.paymentType || "Advance");
        setPaymentHistoryTabDate(
            row.payment_date ? String(row.payment_date).slice(0, 10) : paymentHistoryTabDate
        );
        setPaymentHistoryTabAmount(String(row.advance ?? row.amount ?? ""));
        setPaymentHistoryTabDescription(row.payment_description || row.paymentDescription || "");
        setPaymentHistoryTabReference(row.reference_number || row.referenceNumber || "");
    };

    // Leaves edit mode without saving changes.
    const handleCancelEditPayment = () => {
        setEditingPaymentIdx(null);
        setPaymentHistoryTabAmount('');
        setPaymentHistoryTabDescription('');
        setPaymentHistoryTabReference('');
    };

    const handleAddPaymentRow = async () => {
        if (!paymentHistoryTabAmount) return toast.error("Please enter an amount.");
        const totalGrand = getGrandTotal();

        // When editing, exclude the row being edited from "already paid" so
        // the remaining-balance check reflects the amount being replaced,
        // not double-counted against itself.
        const currentTotalPaid = advancePayments.reduce((sum, p, i) => {
            if (editingPaymentIdx !== null && i === editingPaymentIdx) return sum;
            return sum + Number(p.advance || p.amount || 0);
        }, 0);
        const remainingBalance = totalGrand - currentTotalPaid;

        if (remainingBalance <= 0) return toast.error("No balance remaining. Payment is already complete.");
        if (Number(paymentHistoryTabAmount) > remainingBalance) return toast.error(`Amount cannot exceed remaining balance of ₹${formatCurrency(remainingBalance)}.`);

        const isEditing = editingPaymentIdx !== null;
        const editingRow = isEditing ? advancePayments[editingPaymentIdx] : null;

        if (selectedRfq?.so_id) {
            try {
                const res = isEditing && editingRow?.id
                    ? await fetch(`${ORDER_API}/sales-order/advance-payments/update/${editingRow.id}`, {
                        method: 'PUT',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            amount: Number(paymentHistoryTabAmount),
                            paymentType: paymentHistoryTabType,
                            paymentDate: paymentHistoryTabDate,
                            paymentDescription: paymentHistoryTabDescription,
                            referenceNumber: paymentHistoryTabReference,
                            updatedBy: getCurrentUser()
                        })
                    })
                    : await fetch(`${ORDER_API}/sales-order/advance-payments/${selectedRfq.so_id}`, {
                        method: 'POST',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            paymentType: paymentHistoryTabType,
                            amount: paymentHistoryTabAmount,
                            paymentDate: paymentHistoryTabDate,
                            paymentDescription: paymentHistoryTabDescription,
                            referenceNumber: paymentHistoryTabReference,
                            total: totalGrand,
                            createdBy: getCurrentUser()
                        })
                    });

                const data = await res.json();
                if (res.ok && data.success !== false) {
                    await fetchAdvancePayments(selectedRfq.so_id);
                    fetchLogs(selectedRfq.so_id);
                    // A payment reaching/crossing the Advance Amount can flip
                    // Sales Block to "Released" on the backend (see
                    // autoReleaseSalesBlockIfFullyPaid). Re-fetch and repopulate
                    // so that shows immediately instead of only after the modal
                    // is closed and reopened — same pattern used after Save,
                    // but without touching modalMode/activeTab so the user stays
                    // on the Payment History tab.
                    try {
                        const refreshed = await fetch(
                            `${ORDER_API}/sales-order/accepted-convert/${selectedRfq.so_id || selectedRfq.rfq_id}`,
                            { headers: authHeaders() }
                        );
                        const refreshedData = await refreshed.json();
                        await populateModalState(refreshedData);
                    } catch (refreshErr) {
                        console.error("Failed to refresh sales order after payment:", refreshErr);
                    }
                    setPaymentHistoryTabAmount('');
                    setPaymentHistoryTabDescription('');
                    setPaymentHistoryTabReference('');
                    toast.success(isEditing ? "Payment updated!" : "Payment recorded!");
                    setEditingPaymentIdx(null);
                } else {
                    toast.error(data.message || (isEditing ? "Failed to update payment." : "Failed to add payment."));
                }
            } catch (e) {
                console.error(e);
                toast.error(isEditing ? "Failed to update payment." : "Error adding payment.");
            }
        } else {
            // Not-yet-saved SO (create flow) — payments only live in local
            // state until the SO itself is saved.
            if (isEditing) {
                setAdvancePayments(prev => {
                    const updated = [...prev];
                    updated[editingPaymentIdx] = {
                        ...updated[editingPaymentIdx],
                        paymentType: paymentHistoryTabType,
                        payment_type: paymentHistoryTabType,
                        amount: Number(paymentHistoryTabAmount),
                        advance: Number(paymentHistoryTabAmount),
                        paymentDate: paymentHistoryTabDate,
                        payment_date: paymentHistoryTabDate,
                        paymentDescription: paymentHistoryTabDescription,
                        payment_description: paymentHistoryTabDescription,
                        referenceNumber: paymentHistoryTabReference,
                        reference_number: paymentHistoryTabReference,
                    };
                    let runningPaid = 0;
                    return updated.map(p => {
                        runningPaid += Number(p.advance || p.amount || 0);
                        return { ...p, pending: totalGrand - runningPaid };
                    });
                });
                setEditingPaymentIdx(null);
                setPaymentHistoryTabAmount('');
                setPaymentHistoryTabDescription('');
                setPaymentHistoryTabReference('');
            } else {
                const newPayment = {
                    paymentType: paymentHistoryTabType,
                    amount: paymentHistoryTabAmount,
                    paymentDate: paymentHistoryTabDate,
                    paymentDescription: paymentHistoryTabDescription,
                    payment_description: paymentHistoryTabDescription,
                    referenceNumber: paymentHistoryTabReference,
                    reference_number: paymentHistoryTabReference,
                    total: totalGrand
                };
                const pending = totalGrand - (currentTotalPaid + Number(newPayment.amount));
                setAdvancePayments([...advancePayments, { ...newPayment, pending }]);
                setPaymentHistoryTabAmount('');
                setPaymentHistoryTabDescription('');
                setPaymentHistoryTabReference('');
            }
        }
    };

    const renderPaymentHistoryTab = () => {
        return (
            <div style={styles.tableContainer}>
                {(isEditing || createModalOpen || modalMode === 'convert' || modalMode === 'view') && (
                    <div style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px', position: 'relative' }}>
                        {/* Small, raised "Add Payment" box — sits tucked into the top-right
                            corner of the entry panel instead of stretching full-height with
                            the inputs, per request to make it "little up in small box". */}
                        <div style={{ position: 'absolute', top: '-14px', right: '0px' }}>
                            {(() => {
                                const _totalPaid = advancePayments.reduce((sum, p, i) => {
                                    if (editingPaymentIdx !== null && i === editingPaymentIdx) return sum;
                                    return sum + Number(p.advance || p.amount || 0);
                                }, 0);
                                const _remaining = getGrandTotal() - _totalPaid;
                                return (
                                    <div style={{
                                        display: 'flex',
                                        gap: '6px',
                                        background: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                        padding: '6px'
                                    }}>
                                        <button
                                            className="btn btn-dark btn-sm"
                                            style={{
                                                height: '28px',
                                                fontSize: '12px',
                                                padding: '2px 10px',
                                                whiteSpace: 'nowrap',
                                                opacity: _remaining <= 0 ? 0.5 : 1,
                                                cursor: _remaining <= 0 ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={handleAddPaymentRow}
                                            disabled={_remaining <= 0}
                                        >
                                            {editingPaymentIdx !== null
                                                ? '✓ Update Payment'
                                                : _remaining <= 0
                                                    ? '✓ Fully Paid'
                                                    : '+ Add Payment'}
                                        </button>
                                        {editingPaymentIdx !== null && (
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                style={{ height: '28px', fontSize: '12px', padding: '2px 10px', whiteSpace: 'nowrap' }}
                                                onClick={handleCancelEditPayment}
                                                type="button"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="row g-3 mt-1">
                            <div className="col-md-2">
                                <label style={styles.compactLabel}>Payment Type</label>
                                <select className="form-control" style={styles.compactInput} value={paymentHistoryTabType} onChange={(e) => setPaymentHistoryTabType(e.target.value)}>
                                    <option value="Advance">Advance</option>
                                    <option value="Paid">Paid</option>
                                </select>
                                {/* Order's Total Price — derived only from product totals via
                                getGrandTotal(), never from advancePayments, so it stays fixed
                                no matter how many payments are added/edited/removed. */}
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                    Total Price: <span style={{ fontWeight: 600, color: '#334155' }}>₹{formatCurrency(getGrandTotal())}</span>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <label style={styles.compactLabel}>Date</label>
                                <DatePicker
                                    className="form-control"
                                    wrapperClassName="w-100"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/mm/yyyy"
                                    selected={paymentHistoryTabDate ? new Date(paymentHistoryTabDate) : null}
                                    onChange={(selectedDate) => {
                                        let newVal = '';
                                        if (selectedDate) {
                                            const yyyy = selectedDate.getFullYear();
                                            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                            const dd = String(selectedDate.getDate()).padStart(2, '0');
                                            newVal = `${yyyy}-${mm}-${dd}`;
                                        }
                                        setPaymentHistoryTabDate(newVal);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <label style={styles.compactLabel}>Amount (₹)</label>
                                <input type="number" className="form-control" style={styles.compactInput} value={paymentHistoryTabAmount} onChange={(e) => setPaymentHistoryTabAmount(e.target.value)} placeholder="0.00" />
                            </div>
                            <div className="col-md-3">
                                <label style={styles.compactLabel}>Payment Description</label>
                                <input type="text" className="form-control" style={styles.compactInput} value={paymentHistoryTabDescription} onChange={(e) => setPaymentHistoryTabDescription(e.target.value)} placeholder="e.g. NEFT advance against PO" />
                            </div>
                            <div className="col-md-3">
                                <label style={styles.compactLabel}>Reference Number</label>
                                <input type="text" className="form-control" style={styles.compactInput} value={paymentHistoryTabReference} onChange={(e) => setPaymentHistoryTabReference(e.target.value)} placeholder="e.g. UTR / Cheque No." />
                            </div>
                        </div>
                    </div>
                )}

                <table className="table mb-0" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>Date</th>
                            <th style={styles.erpTh}>Payment Type</th>
                            <th style={styles.erpTh}>Payment Description</th>
                            <th style={styles.erpTh}>Reference Number</th>
                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Amount</th>
                            <th style={{ ...styles.erpTh, textAlign: 'right' }}>Balance Remaining</th>
                        </tr>
                    </thead>
                    <tbody>
                        {advancePayments.length > 0 ? advancePayments.map((pay, idx) => (
                            <tr
                                key={pay.id || idx}
                                style={editingPaymentIdx === idx ? { background: '#fff8e1' } : undefined}
                            >
                                <td style={styles.erpTd}>{formatDateDisplay(pay.payment_date || pay.paymentDate)}</td>
                                <td style={styles.erpTd}>{pay.payment_type || pay.paymentType}</td>
                                <td style={styles.erpTd}>{pay.payment_description || pay.paymentDescription || '-'}</td>
                                <td style={styles.erpTd}>{pay.reference_number || pay.referenceNumber || '-'}</td>
                                <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(pay.advance || pay.amount)}</td>
                                <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 600 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                        {formatCurrency(pay.pending)}
                                        {(isEditing || createModalOpen || modalMode === 'convert' || modalMode === 'view') && (
                                            <button
                                                type="button"
                                                onClick={() => handleEditPaymentClick(idx, pay)}
                                                title="Edit payment"
                                                style={{
                                                    border: 'none',
                                                    background: 'transparent',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    lineHeight: 1,
                                                    fontSize: '14px',
                                                    color: editingPaymentIdx === idx ? '#b8860b' : '#6c757d',
                                                }}
                                            >
                                                ✎
                                            </button>
                                        )}
                                    </span>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="6" className="text-center text-muted p-3">No payments recorded yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderChecklistTab = () => {
        if (checklistLoading) {
            return (
                <div style={styles.tableContainer}>
                    <div className="text-center text-muted p-3">Loading checklist…</div>
                </div>
            );
        }

        // Checklist parameters are always shown for every product on the
        // order — no longer gated behind Review approval.
        const visibleChecklistItems = checklistItems;

        if (!checklistItems.length) {
            return (
                <div style={styles.tableContainer}>
                    <div className="text-center text-muted p-3">
                        No checklist parameters configured yet. Add them in Checklist Configuration.
                    </div>
                </div>
            );
        }

        // Product-wise, like a GTP datasheet: group rows by product_code first
        // (one block per product actually on this order, in the order the API
        // returned its products), then within each product's block, group by
        // Type of Analysis (TECHNICAL ANALYSIS / COMMERCIAL ANALYSIS), matching
        // how the Checklist Configuration module organizes them.
        const productGroups = {};
        const productOrder = [];
        visibleChecklistItems.forEach((item) => {
            const key = item.product_code || "__no_product__";
            if (!productGroups[key]) {
                productGroups[key] = {
                    product_code: item.product_code,
                    product_description: item.product_description,
                    rows: [],
                };
                productOrder.push(key);
            }
            productGroups[key].rows.push(item);
        });

        const canEditChecklist = isEditing || modalMode === 'convert' || createModalOpen;
        const rcReviewSoId = selectedRfq?.so_id || '';
        // The RC Review "Done" checkbox no longer requires a real so_id —
        // it can be marked before the Sales Order is created (tracked
        // locally and pushed to the server once the so_id exists).
        const canEditRcReview = canEditChecklist;

        return (
            <div style={styles.tableContainer}>
                {productOrder.map((key) => {
                    const group = productGroups[key];
                    const isExpanded = expandedChecklistProduct === key;
                    const rcStatus = rcReviewStatus[group.product_code] || "Pending";
                    const rcIsDone = rcStatus === "Done";
                    // Done can only be ticked once every checklist parameter value
                    // for this product has actually been filled in.
                    const allValuesFilled = group.rows.every(
                        (item) => String(item.value ?? "").trim() !== ""
                    );
                    const rcCheckboxDisabled =
                        !canEditRcReview || !!rcReviewSaving[group.product_code] || (!rcIsDone && !allValuesFilled);

                    // Within this product's block, group by Type of Analysis.
                    const analysisGroups = {};
                    group.rows.forEach((item) => {
                        const aKey = item.analysis_type || "Uncategorized";
                        if (!analysisGroups[aKey]) analysisGroups[aKey] = [];
                        analysisGroups[aKey].push(item);
                    });

                    // Checklist Mandatory — missing entry (never toggled)
                    // defaults to true, matching the column's own DEFAULT.
                    const isMandatory = checklistMandatory[group.product_code] !== false;
                    const mandatoryCheckboxDisabled =
                        !canEditChecklist || !!checklistMandatorySaving[group.product_code];

                    return (
                        <div
                            key={key}
                            style={{
                                marginBottom: 12,
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: isExpanded ? '16px 18px' : '0',
                                background: '#fff',
                                overflow: 'hidden',
                            }}
                        >
                            {group.product_code && (
                                <div
                                    onClick={() => setExpandedChecklistProduct(isExpanded ? null : key)}
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: '#021843',
                                        marginBottom: isExpanded ? 14 : 0,
                                        paddingBottom: isExpanded ? 10 : 0,
                                        borderBottom: isExpanded ? '2px solid #e2e8f0' : 'none',
                                        padding: isExpanded ? undefined : '14px 18px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        userSelect: 'none',
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                        {/* ── Checklist Mandatory — ticked (default): this
                                             product's checklist blocks approval as usual.
                                             Unticked: exempted from the approval gate for
                                             this SO. Stops the click from also toggling the
                                             expand/collapse. ── */}
                                        <input
                                            type="checkbox"
                                            checked={isMandatory}
                                            disabled={mandatoryCheckboxDisabled}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => handleToggleChecklistMandatory(rcReviewSoId, group.product_code, e.target.checked)}
                                            title={
                                                isMandatory
                                                    ? "Checklist is mandatory before approval — untick to make it optional for this product"
                                                    : "Checklist is optional for this product — won't block approval"
                                            }
                                        />
                                        <span>
                                            {group.product_code}
                                            {group.product_description ? ` — ${group.product_description}` : ''}
                                        </span>
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                        {/* ── RC Review — Pending/Done status + checkbox for this
                                             product, right-aligned in its own header row. Stops
                                             the click from also toggling the expand/collapse. ── */}
                                        <span
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 12.5 }}
                                        >
                                            <span style={{ color: rcIsDone ? '#16a34a' : '#b45309' }}>
                                                {rcStatus}
                                            </span>
                                            <input
                                                type="checkbox"
                                                checked={rcIsDone}
                                                disabled={rcCheckboxDisabled}
                                                onChange={(e) => handleToggleRcReview(rcReviewSoId, group.product_code, e.target.checked)}
                                                title={
                                                    !rcIsDone && !allValuesFilled
                                                        ? "Fill in all checklist values for this product before marking Done"
                                                        : "Mark RC Review done"
                                                }
                                            />
                                        </span>
                                        <span style={{ fontSize: 12, color: '#64748b', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                                            ▼
                                        </span>
                                    </span>
                                </div>
                            )}
                            {isExpanded && Object.entries(analysisGroups).map(([analysisType, rows]) => (
                                <div key={analysisType} style={{ marginBottom: 20 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                                        {analysisType}
                                    </div>
                                    <table className="table mb-0" style={{ ...styles.erpTable, tableLayout: 'fixed' }}>
                                        <colgroup>
                                            <col style={{ width: '55%' }} />
                                            <col style={{ width: '45%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th style={styles.erpTh}>Parameter</th>
                                                <th style={styles.erpTh}>Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((item) => (
                                                <tr key={`${group.product_code}-${item.checklist_number}-${item.parameter_name}`}>
                                                    <td style={{ ...styles.erpTd, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.parameter_name}{item.uom ? ` (${item.uom})` : ''}</td>
                                                    <td style={styles.erpTd}>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            style={{ ...styles.compactInput, width: '100%' }}
                                                            value={item.value || ""}
                                                            disabled={!canEditChecklist}
                                                            onChange={(e) =>
                                                                updateChecklistValue(item.product_code, item.checklist_number, item.parameter_name, e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        );
    };

    // --- SO document upload size limit — configurable via
    // Configuration → File Size Config (Module = "Sales Order").
    // Falls back to 50 KB (the old hardcoded value) if it can't be fetched.
    const DEFAULT_MAX_SO_DOC_SIZE_KB = 50;
    const [maxSoDocSizeKb, setMaxSoDocSizeKb] = useState(DEFAULT_MAX_SO_DOC_SIZE_KB);

    useEffect(() => {
        fetch(`${MASTER_API}/configuration/file-size-limit/${encodeURIComponent("Sales Order")}`, { headers: authHeaders() })
            .then(res => res.ok ? res.json() : Promise.reject())
            .then(data => {
                if (data && data.maxSizeKb) setMaxSoDocSizeKb(data.maxSizeKb);
            })
            .catch(() => { /* keep default limit if config fetch fails */ });
    }, []);

    // Splits a FileList/array into files within the limit and files that exceed it,
    // and toasts a warning listing any rejected files.
    const filterFilesWithinSizeLimit = (files) => {
        const maxBytes = maxSoDocSizeKb * 1024;
        const okFiles = [];
        const rejected = [];
        files.forEach(f => {
            if (f.size > maxBytes) {
                rejected.push(f.name);
            } else {
                okFiles.push(f);
            }
        });
        if (rejected.length) {
            toast.error(
                `${rejected.length} file(s) exceed the ${maxSoDocSizeKb} KB upload limit and were skipped: ${rejected.join(', ')}`
            );
        }
        return okFiles;
    };

    const getFileIcon = (name = '', type = '') => {
        const ext = (name.split('.').pop() || '').toLowerCase();
        if (['pdf'].includes(ext)) return '📄';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
        if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
        return '📎';
    };

    // ── CHECKLIST FORM (Docs tab) ───────────────────────────────────────
    // One "Purchase Order Checklist" form per product line that actually has
    // checklist parameters assigned. Groups the already-loaded checklistItems
    // by product_code (each item already carries its own product_description
    // from the API, so no separate lookup against productDetails is needed).
    const getChecklistDocGroups = () => {
        if (!checklistItems.length) return [];
        const approvedCodes = getApprovedProductCodes();
        const source = approvedCodes
            ? checklistItems.filter((item) => approvedCodes.has(item.product_code))
            : checklistItems;
        const byProduct = {};
        source.forEach((item) => {
            const code = item.product_code || "";
            if (!code) return;
            if (!byProduct[code]) byProduct[code] = [];
            byProduct[code].push(item);
        });
        return Object.entries(byProduct).map(([productCode, items]) => ({
            productCode,
            description: items[0]?.product_description || "",
            checklistNumber: items[0]?.checklist_number || "",
            items
        }));
    };

    // jsPDF's built-in "helvetica" font only supports WinAnsi/Latin-1 — swap
    // known problem characters before they go into the PDF.
    const checklistPdfSafeText = (val) => {
        if (val === null || val === undefined) return "";
        return String(val)
            .replace(/Ω/g, "Ohm")
            .replace(/[µμ]/g, "u")
            .replace(/°/g, " deg ");
    };

    // "Prepared by" / "Approved by" often just holds the shared mailbox
    // address (e.g. "orders@sbeecables.in") rather than a person's name —
    // drop the generic "orders@" mailbox prefix so the footer reads cleaner.
    const stripOrdersMailbox = (val) => checklistPdfSafeText(val).replace(/\borders@/gi, "");

    // Builds the "Purchase Order Checklist" form PDF for one product — layout
    // matches Checklist_format.xlsx: form-ref line, title, Order Checklist
    // No / Customer / Offer, PO details row, then one table section per
    // Type of Analysis (Technical Analysis, Commercial Analysis, etc.),
    // and a Prepared/Approved-by footer. Kept separate from buildChecklistPdf
    // (the auto-generated-per-save version) since this one is only ever
    // built on demand for View/Download/Print in the Docs tab.
    const buildChecklistFormPdf = (group) => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const contentW = pageWidth - margin * 2;

        doc.setDrawColor(51, 51, 51);
        doc.setLineWidth(0.75);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("MKG/F-08 Rev.No.00 Dtd: 08.08.18", margin, 30);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.text("PURCHASE ORDER CHECKLIST", pageWidth / 2, 50, { align: "center" });

        let y = 70;

        // -- Identity block: bordered box, one label/value row per line --
        const identityRows = [
            ["Order Checklist No:", group.checklistNumber],
            ["Name of the Customer:", selectedRfq?.customer],
            ["Product Code:", group.productCode],
            ["Description:", group.description],
            ["Sales Order No:", selectedRfq?.so_id],
            ["P.O. No / Date:", `${checklistPdfSafeText(purchaseOrder) || "-"} / ${checklistPdfSafeText(purchaseOrderDate ? formatDateDisplay(purchaseOrderDate) : createdDate) || "-"}`],
        ];
        const idRowH = 18;
        const idBoxTop = y;
        doc.setFontSize(9);
        identityRows.forEach(([label, value], i) => {
            const rowY = idBoxTop + i * idRowH;
            doc.setFont("helvetica", "bold");
            doc.text(label, margin + 8, rowY + 13);
            doc.setFont("helvetica", "normal");
            doc.text(checklistPdfSafeText(value) || "-", margin + 148, rowY + 13);
            if (i > 0) doc.line(margin, rowY, margin + contentW, rowY);
        });
        const idBoxBottom = idBoxTop + identityRows.length * idRowH;
        doc.rect(margin, idBoxTop, contentW, identityRows.length * idRowH);
        y = idBoxBottom + 16;

        // One bordered section per Type of Analysis, rows styled as
        // label (left) / value + UOM (right), matching the Excel form.
        const groups = {};
        const order = [];
        group.items.forEach((item) => {
            const key = item.analysis_type || "Uncategorized";
            if (!groups[key]) { groups[key] = []; order.push(key); }
            groups[key].push(item);
        });

        const bannerH = 20;
        const colHeadH = 16;
        const rowMinH = 18;
        const lineH = 10.5; // wrapped-text line height inside a row
        const col1W = contentW * 0.6; // a little narrower so long labels have room to wrap
        const col1X = margin;
        const col3X = margin + col1W;
        const col1TextW = col1W - 16; // usable width for the Parameter text
        const col2TextW = contentW - col1W - 16; // usable width for the Value text
        const bottomLimit = pageHeight - 90; // leave room for the signature footer

        // Draws the gray title banner + the Parameter/Value column header at
        // the current y, advancing y past both. Reused whenever a section
        // starts, and again if a section has to continue onto a new page.
        const drawSectionHead = (title) => {
            doc.setFillColor(217, 217, 217);
            doc.rect(margin, y, contentW, bannerH, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(checklistPdfSafeText(title).toUpperCase(), pageWidth / 2, y + 14, { align: "center" });
            y += bannerH;

            doc.setFillColor(241, 245, 249);
            doc.rect(margin, y, contentW, colHeadH, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("Parameter", col1X + 8, y + 11);
            doc.text("Value", col3X + 8, y + 11);
            doc.line(margin, y, margin + contentW, y);
            y += colHeadH;
        };

        order.forEach((analysisType) => {
            const rows = groups[analysisType];

            // Only force a page break before a section if there isn't even
            // room for its banner + header + a single row — short sections
            // no longer get bumped to the next page just because a later,
            // much bigger section wouldn't have fit.
            if (y + bannerH + colHeadH + rowMinH > bottomLimit) {
                doc.addPage();
                y = 40;
            }

            let sectionTop = y;
            drawSectionHead(analysisType);
            let rowsTop = y;

            doc.setFontSize(9);
            rows.forEach((item) => {
                const paramText = checklistPdfSafeText(item.uom ? `${item.parameter_name} (${item.uom})` : item.parameter_name);
                const valueText = checklistPdfSafeText(item.value) || "-";
                doc.setFont("helvetica", "normal");
                const paramLines = doc.splitTextToSize(paramText, col1TextW);
                doc.setFont("helvetica", "bold");
                const valueLines = doc.splitTextToSize(valueText, col2TextW);
                const nLines = Math.max(paramLines.length, valueLines.length, 1);
                const thisRowH = Math.max(rowMinH, nLines * lineH + 7);

                // If this row won't fit on the current page, close off the
                // section box drawn so far, start a fresh page, and repeat
                // the banner + column header so the table reads continuously
                // instead of leaving a big gap and jumping the whole section.
                if (y + thisRowH > bottomLimit) {
                    doc.line(col3X, rowsTop, col3X, y);
                    doc.rect(margin, sectionTop, contentW, y - sectionTop);

                    doc.addPage();
                    y = 40;
                    sectionTop = y;
                    drawSectionHead(`${analysisType} (contd.)`);
                    rowsTop = y;
                }

                doc.setFont("helvetica", "normal");
                doc.text(paramLines, col1X + 8, y + lineH);
                doc.setFont("helvetica", "bold");
                doc.text(valueLines, col3X + 8, y + lineH);
                doc.line(margin, y, margin + contentW, y);
                y += thisRowH;
            });

            // Vertical column divider + outer box for the final page segment
            // of this section.
            doc.line(col3X, rowsTop, col3X, y);
            doc.rect(margin, sectionTop, contentW, y - sectionTop);
            y += 16;
        });

        // -- Footer: bordered two-column box, matching the modal preview --
        if (y + 30 > pageHeight - 40) {
            doc.addPage();
            y = 40;
        }
        const footerH = 26;
        doc.rect(margin, y, contentW, footerH);
        doc.line(margin + contentW / 2, y, margin + contentW / 2, y + footerH);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Prepared by & Date:", margin + 8, y + 16);
        doc.text("Approved by & Date:", margin + contentW / 2 + 8, y + 16);
        doc.setFont("helvetica", "normal");
        doc.text(
            `${stripOrdersMailbox(createdBy) || "-"} / ${checklistPdfSafeText(createdDate) || "-"}`,
            margin + 8 + doc.getTextWidth("Prepared by & Date: "),
            y + 16
        );
        doc.text(
            `${stripOrdersMailbox(approvedBy) || "-"} / ${checklistPdfSafeText(approvedDate) || "-"}`,
            margin + contentW / 2 + 8 + doc.getTextWidth("Approved by & Date: "),
            y + 16
        );

        return doc;
    };

  /* GTP datasheet filenames always use the fixed "GN35" prefix
     (instead of whatever gtpNumber the master GTP Configuration record has),
     followed by the product code and then the product description. */
  const gtpFileName = (gtp) => `GN35_${gtp.productCode || ""}_${gtp.productDescription || ""}.pdf`;

  /* The GTP doc shown/printed here is always the SALES doc, never the full
     datasheet — i.e. only characteristics whose GTP Type includes "Sales"
     are included, regardless of what's ticked for Master Doc. gtpType can
     arrive either as a comma-separated string ("Sales,Master") or an array
     of strings/option objects, so normalize before checking. */
  const isSalesCharacteristic = (item) => {
      const raw = item.gtpType ?? item.gtp_type ?? "";
      const types = Array.isArray(raw)
          ? raw.map((t) => (typeof t === "string" ? t : t?.value || t?.label || ""))
          : String(raw).split(",");
      return types.map((t) => String(t).trim()).includes("Sales");
  };

  /* ── GTP Datasheet PDF builder — matches GTPConfiguration format exactly ──
     preparedBy/approvedBy come from the QUOTATION (created_by / approved_by),
     not from the GTP master record — GTP config has no meaningful signer of
     its own, and the old fallback to gtp.checkedBy was showing a hardcoded
     "Admin" on every datasheet. Approved By is left blank until the
     quotation is actually approved (no fallback). ── */
const buildGtpDatasheetPdf = (gtp, { preparedBy = "", approvedBy = "" } = {}) => {
    const doc       = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin    = 40;
    const contentW  = pageWidth - margin * 2;

    // jsPDF's built-in "helvetica" font only supports WinAnsi/Latin-1
    // characters. Symbols like Ω (ohm) fall outside that set and render as
    // garbled bytes — this only affects the PDF; the on-screen View uses
    // the browser's own font rendering, which handles full Unicode fine.
    // Swap known problem characters for ASCII-safe equivalents before any
    // text goes into the PDF.
    const pdfSafeText = (val) => {
        if (val === null || val === undefined) return "";
        return String(val)
            .replace(/Ω/g, "Ohm")
            .replace(/[µμ]/g, "u")
            .replace(/°/g, " deg ");
    };

    /* ── HEADER ── */
    const headerTopY = 28;
    const logoH = 40, logoW = 60;
    try { doc.addImage(sbeeLogo, "PNG", margin, headerTopY, logoW, logoH); } catch (e) { }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("SBEE CABLES INDIA LTD", pageWidth / 2, headerTopY + 15, { align: "center" });

    doc.setFontSize(10);
    doc.text(
        `GUARANTEED TECHNICAL PARTICULARS FOR ${(gtp.productType || "").toUpperCase()}`,
        pageWidth / 2, headerTopY + 30, { align: "center" }
    );

    /* ── PRODUCT CODE / DESCRIPTION BAR ── */
    const barTopY = headerTopY + logoH + 12;
    const barH    = 20;

    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.75);
    doc.rect(margin, barTopY, contentW, barH);

    doc.setFontSize(9);
    const ty = barTopY + barH / 2 + 3.5;

    // Measure each segment first (font changes affect getTextWidth), so we
    // can center the whole line within the bar instead of starting at a
    // fixed left offset.
    doc.setFont("helvetica", "bold");
    const seg1 = "Product Code:";
    const w1 = doc.getTextWidth(seg1);

    doc.setFont("helvetica", "normal");
    const seg2 = ` ${gtp.productCode || "-"}`;
    const w2 = doc.getTextWidth(seg2);

    doc.setFont("helvetica", "bold");
    const seg3 = "   Description:";
    const w3 = doc.getTextWidth(seg3);

    doc.setFont("helvetica", "normal");
    const seg4 = ` ${pdfSafeText(gtp.productDescription) || "-"}`;
    const w4 = doc.getTextWidth(seg4);

    const totalW = w1 + w2 + w3 + w4;
    let tx = margin + (contentW - totalW) / 2;

    doc.setFont("helvetica", "bold");
    doc.text(seg1, tx, ty);
    tx += w1;

    doc.setFont("helvetica", "normal");
    doc.text(seg2, tx, ty);
    tx += w2;

    doc.setFont("helvetica", "bold");
    doc.text(seg3, tx, ty);
    tx += w3;

    doc.setFont("helvetica", "normal");
    doc.text(seg4, tx, ty);

    /* ── TABLE ── */
    const tableStartY = barTopY + barH;
    const col0W = 45, col2W = 65, col1W = 185;
    const col3W = contentW - col0W - col1W - col2W;

    const tableBody = [];

    // GENERAL section
    tableBody.push([
        { content: "1", styles: { fontStyle: "bold", halign: "center" } },
        { content: "GENERAL", colSpan: 3, styles: { fontStyle: "bold" } },
    ]);
    tableBody.push(["1.1", "Name & Address of the manufacturer", "",
        manufacturerAddress
    ]);
    tableBody.push(["1.2", "Trade mark / Brand Name", "", ""]);
    const trademarkBodyRowIndex = 2;

    // Dynamic characteristics — only rows that actually have a specified
    // value are printed; blanks are dropped instead of showing an empty cell.
    let lastHeader = null, sectionCounter = 1, subCounter = 0;
    const gtpCharsWithValues = (gtp.characteristics || []).filter(
        (item) => item.standardValue !== undefined && item.standardValue !== null && String(item.standardValue).trim() !== "" && isSalesCharacteristic(item)
    );
    gtpCharsWithValues.forEach((item) => {
        const headerName = item.processName || "";
        if (headerName !== lastHeader) {
            sectionCounter += 1; subCounter = 0;
            tableBody.push([
                { content: String(sectionCounter), styles: { fontStyle: "bold", halign: "center" } },
                { content: pdfSafeText(headerName).toUpperCase(), colSpan: 3, styles: { fontStyle: "bold" } },
            ]);
            lastHeader = headerName;
        }
        subCounter += 1;
        tableBody.push([
            `${sectionCounter}.${subCounter}`,
            pdfSafeText(item.characteristic),
            pdfSafeText(item.uom),
            pdfSafeText(item.standardValue),
        ]);
    });

    autoTable(doc, {
        startY: tableStartY,
        head: [[
            { content: "S.No",             styles: { halign: "center" } },
            { content: "Parameters",       styles: { halign: "left"   } },
            { content: "UOM",              styles: { halign: "center" } },
            { content: "Specified Values", styles: { halign: "center" } },
        ]],
        body: tableBody,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 5, lineColor: [51, 51, 51], lineWidth: 0.75, textColor: 30 },
        headStyles: { fillColor: [220, 220, 220], textColor: 20, fontStyle: "bold", lineColor: [51, 51, 51], lineWidth: 0.75 },
        columnStyles: {
            0: { cellWidth: col0W, halign: "center" },
            1: { cellWidth: col1W, halign: "left"   },
            2: { cellWidth: col2W, halign: "center" },
            3: { cellWidth: col3W, halign: "center" },
        },
        margin: { left: margin, right: margin },
        didDrawCell: (data) => {
            if (
                data.section === "body" &&
                data.row.index === trademarkBodyRowIndex &&
                data.column.index === 3
            ) {
                try {
                    const imgW = 48, imgH = 19;
                    const imgX = data.cell.x + (data.cell.width  - imgW) / 2;
                    const imgY = data.cell.y + (data.cell.height - imgH) / 2;
                    doc.addImage(sbeeLogo, "PNG", imgX, imgY, imgW, imgH);
                } catch (e) {
                    doc.setFontSize(9);
                    doc.text("SBEE", data.cell.x + data.cell.width / 2,
                        data.cell.y + data.cell.height / 2 + 3, { align: "center" });
                }
            }
        },
    });

    /* ── FOOTER ── */
    const finalY     = doc.lastAutoTable.finalY + 50;
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY    = Math.min(finalY, pageHeight - 50);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Prepared By:", margin, footerY);
    doc.text("Approved By:", pageWidth - margin - 130, footerY);

    doc.setDrawColor(51, 51, 51);
    doc.setLineWidth(0.75);
    doc.line(margin + 75, footerY, margin + 220, footerY);
    doc.line(pageWidth - margin - 55, footerY, pageWidth - margin, footerY);

    if (preparedBy) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(preparedBy, margin + 80, footerY - 2);
    }

    // Approved By only shows once the quotation has actually been approved —
    // no fallback to Prepared By.
    if (approvedBy) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(approvedBy, pageWidth - margin - 50, footerY - 2);
    }

    return doc;
};

    const renderDocsTab = () => {
        // Order Confirmation is a Sales Order document — it must only appear
        // after this record has actually been converted into a real Sales
        // Order (so_id assigned). Previously this fell back to rfq_id, which
        // meant the doc card (and a blank "ORDER CONFIRMATION NO: -") showed
        // up even for RFQ/Quotation-stage rows that were never converted.
        const soIdentifier = selectedRfq?.so_id || '';
        const checklistDocGroups = getChecklistDocGroups();

        return (
            <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>

                {/* ── ORDER CONFIRMATION — the customer-facing PO confirmation letter
                     (MKG/F-02 format), rendered by SalesOrderView.js at /sales-order/:soId.
                     Same View/Download/Print pattern as the Quotation doc card in Home.js
                     and the Checklist cards below — hidden off-screen iframe for
                     Download/Print so html2canvas inside SalesOrderView still paints
                     (display:none would capture blank), visible iframe in a modal for View. ── */}
                {soIdentifier && (
                    <div
                        style={{ marginBottom: 16, padding: 15, border: '1px solid #d1d5db', borderRadius: 8, background: '#f8fafc' }}
                    >
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 12 }}>
                            {soIdentifier}_OrderConfirmation.pdf
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={() => setShowOrderConfirmationModal(true)}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                View
                            </button>
                            <button
                                onClick={() => {
                                    const iframe = document.createElement("iframe");
                                    iframe.style.position = "fixed";
                                    iframe.style.top = "0";
                                    iframe.style.left = "-10000px";
                                    iframe.style.width = "1050px";
                                    iframe.style.height = "2000px";
                                    iframe.style.border = "none";
                                    iframe.style.visibility = "hidden";
                                    iframe.src = "/sales-order/" + soIdentifier + "?download=true";
                                    document.body.appendChild(iframe);
                                    setTimeout(() => { document.body.removeChild(iframe); }, 10000);
                                }}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Download
                            </button>
                            <button
                                onClick={() => {
                                    const iframe = document.createElement("iframe");
                                    iframe.style.position = "fixed";
                                    iframe.style.top = "0";
                                    iframe.style.left = "-10000px";
                                    iframe.style.width = "1050px";
                                    iframe.style.height = "2000px";
                                    iframe.style.border = "none";
                                    iframe.style.visibility = "hidden";
                                    iframe.src = "/sales-order/" + soIdentifier + "?print=true";
                                    document.body.appendChild(iframe);
                                    setTimeout(() => { document.body.removeChild(iframe); }, 10000);
                                }}
                                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            >
                                Print
                            </button>
                        </div>

                        {/* VIEW MODAL */}
                        {showOrderConfirmationModal && (
                            <div
                                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
                                onClick={(e) => { if (e.target === e.currentTarget) setShowOrderConfirmationModal(false); }}
                            >
                                <div style={{ width: "90%", height: "90%", background: "#fff", borderRadius: "8px", position: "relative", display: "flex", flexDirection: "column" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #e2e8f0" }}>
                                        <span style={{ fontWeight: "600", fontSize: "14px" }}>
                                            {soIdentifier}_OrderConfirmation.pdf
                                        </span>
                                        <button
                                            onClick={() => setShowOrderConfirmationModal(false)}
                                            style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b", lineHeight: 1 }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <iframe
                                        src={"/sales-order/" + soIdentifier}
                                        title="Order Confirmation"
                                        width="100%"
                                        style={{ flex: 1, border: "none", borderRadius: "0 0 8px 8px" }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── GTP DATASHEET(S) — same form as in the Quotation ── */}
                                    {/* GTP DATASHEET(S) */}
                {gtpDocs.length > 0 && gtpDocs.map((gtp, gtpIdx) => (
                    <div
                        key={gtpIdx}
                        style={{ marginBottom: "16px", padding: "15px", border: "1px solid #d1d5db", borderRadius: "8px", background: "#f8fafc" }}
                    >
                        <div style={{ fontSize: "15px", fontWeight: "600", color: "#000", marginBottom: "12px" }}>
                            {gtpFileName(gtp)}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button
                                onClick={() => setShowGtpPreview(gtp)}
                                style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                            >
                                View
                            </button>
                            <button
                                onClick={() => { const doc = buildGtpDatasheetPdf(gtp, { preparedBy: stripOrdersMailbox(createdBy), approvedBy: stripOrdersMailbox(approvedBy) }); doc.save(gtpFileName(gtp)); }}
                                style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                            >
                                Download
                            </button>
                            <button
                                onClick={() => { const doc = buildGtpDatasheetPdf(gtp, { preparedBy: stripOrdersMailbox(createdBy), approvedBy: stripOrdersMailbox(approvedBy) }); doc.autoPrint(); window.open(doc.output("bloburl", { filename: gtpFileName(gtp) }), "_blank"); }}
                                style={{ padding: "6px 14px", fontSize: "13px", fontWeight: "500", background: "#000", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
                            >
                                Print
                            </button>
                        </div>
{/* INLINE VIEW POPUP */}
{showGtpPreview?.productCode === gtp.productCode && (
    <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={(e) => { if (e.target === e.currentTarget) setShowGtpPreview(null); }}
    >
        <div style={{ width: "90%", maxWidth: "950px", maxHeight: "90vh", background: "#fff", borderRadius: "10px", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
                <strong>{gtpFileName(gtp)}</strong>
                <button onClick={() => setShowGtpPreview(null)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "24px", background: "#fff" }}>

                {/* Header — logo left, text centered */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                    <img src={sbeeLogo} alt="SBEE" style={{ height: "40px", width: "60px", objectFit: "contain" }} />
                    <div style={{ flex: 1, textAlign: "center" }}>
                        <div style={{ fontSize: "15px", fontWeight: 700, fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif" }}>SBEE CABLES INDIA LTD</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", marginTop: "2px" }}>
                            {`GUARANTEED TECHNICAL PARTICULARS FOR ${(gtp.productType || "").toUpperCase()}`}
                        </div>
                    </div>
                </div>

                {/* Product Code / Description bordered bar */}
                <div style={{ border: "1px solid #333", padding: "5px 10px", fontSize: "13px", marginBottom: "0", textAlign: "center" }}>
                    <strong>Product Code:</strong> {gtp.productCode || "-"} &nbsp;&nbsp;
                    <strong>Description:</strong> {gtp.productDescription || "-"}
                </div>

                {/* Characteristics Table */}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", border: "1px solid #333" }}>
                    <thead>
                     <tr>
  {["S.No", "Parameters", "UOM", "Specified Values"].map(h => (
    <th
      key={h}
      style={{
        border: "1px solid #333",
        padding: "7px 10px",
        background: "#dcdcdc",
        fontWeight: 700,
        textAlign: h === "Specified Values" ? "center" : "left"
      }}
    >
      {h}
    </th>
  ))}
</tr>
                    </thead>
                    <tbody>
                        {(() => {
                            let lastH = null, sectionCounter = 1, subCounter = 0;
                            const rows = [];

                            // GENERAL section (fixed rows)
                            rows.push(
                                <tr key="gen-header">
                <td style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>1</td>
                <td colSpan={3} style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>GENERAL</td>
                                </tr>
                            );
                            rows.push(
                                <tr key="gen-1">
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}>1.1</td>
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}>Name & Address of the manufacturer</td>
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}></td>
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}>{manufacturerAddress}</td>
                                </tr>
                            );
                            rows.push(
                                <tr key="gen-2">
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}>1.2</td>
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}>Trade mark / Brand Name</td>
                <td style={{ border: "1px solid #333", padding: "6px 10px" }}></td>
                <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>
                    <img src={sbeeLogo} alt="SBEE" style={{ height: "22px" }} />
                </td>
                                </tr>
                            );

                            // Dynamic characteristics — only rows that actually have a
                            // specified value are shown; blanks are dropped.
                            const gtpCharsWithValues = (gtp.characteristics || []).filter(
                                (item) => item.standardValue !== undefined && item.standardValue !== null && String(item.standardValue).trim() !== "" && isSalesCharacteristic(item)
                            );
                            gtpCharsWithValues.forEach((item, i) => {
                                if (item.processName !== lastH) {
                sectionCounter += 1; subCounter = 0;
                rows.push(
                    <tr key={`gh-${i}`}>
                        <td style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700 }}>{sectionCounter}</td>
                        <td colSpan={3} style={{ border: "1px solid #333", padding: "7px 10px", fontWeight: 700, textTransform: "uppercase" }}>
                            {item.processName}
                        </td>
                    </tr>
                );
                lastH = item.processName;
                                }
                                subCounter++;
                                rows.push(
                <tr key={`gr-${i}`}>
                    <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{sectionCounter}.{subCounter}</td>
                    <td style={{ border: "1px solid #333", padding: "6px 10px" }}>{item.characteristic}</td>
                    <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{item.uom || "-"}</td>
                    <td style={{ border: "1px solid #333", padding: "6px 10px", textAlign: "center" }}>{item.standardValue || ""}</td>
                </tr>
                                );
                            });
                            return rows;
                        })()}
                    </tbody>
                </table>

                {/* Footer */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "50px", fontSize: "13px" }}>
                    <div>
                        <strong>Prepared By:</strong>
                        <span style={{ display: "inline-block", borderBottom: "1px solid #333", width: "150px", marginLeft: "8px" }}>
                            {stripOrdersMailbox(createdBy) || ""}
                        </span>
                    </div>
                    <div>
                        <strong>Approved By:</strong>
                        <span style={{ display: "inline-block", borderBottom: "1px solid #333", width: "150px", marginLeft: "8px" }}>
                            {stripOrdersMailbox(approvedBy) || ""}
                        </span>
                    </div>
                </div>

            </div>
        </div>
    </div>
)}
                    </div>
                ))}

                {/* ── PURCHASE ORDER CHECKLIST — one form per product that has ──
                     checklist parameters assigned. */}
                {checklistDocGroups.map((group) => {
                    const fileName = `${group.productCode}_${group.description || group.productCode}.pdf`;
                    return (
                        <div
                            key={group.productCode}
                            style={{ marginBottom: 16, padding: 15, border: '1px solid #d1d5db', borderRadius: 8, background: '#f8fafc' }}
                        >
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#000', marginBottom: 12 }}>
                                {fileName}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={() => setShowChecklistDocPreview(group.productCode)}
                                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    View
                                </button>
                                <button
                                    onClick={() => { buildChecklistFormPdf(group).save(fileName); }}
                                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Download
                                </button>
                                <button
                                    onClick={() => {
                                        const pdfDoc = buildChecklistFormPdf(group);
                                        pdfDoc.autoPrint();
                                        window.open(pdfDoc.output("bloburl", { filename: fileName }), "_blank");
                                    }}
                                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, background: '#000', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Print
                                </button>
                            </div>

                            {/* ── INLINE VIEW POPUP ── */}
                            {showChecklistDocPreview === group.productCode && (
                                <div
                                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={(e) => { if (e.target === e.currentTarget) setShowChecklistDocPreview(null); }}
                                >
                                    <div style={{ width: '90%', maxWidth: 950, maxHeight: '90vh', background: '#fff', borderRadius: 10, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 15px 40px rgba(0,0,0,0.25)' }}>
                                        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                                            <strong>{fileName}</strong>
                                            <button onClick={() => setShowChecklistDocPreview(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>✕</button>
                                        </div>
                                        <div style={{ flex: 1, overflowY: 'auto', padding: 24, background: '#fff' }}>
                                            {/* Header — mirrors the printed form */}
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                                <img src={sbeeLogo} alt="SBEE" style={{ height: 34, width: 50, objectFit: 'contain' }} />
                                                <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>
                                                    PURCHASE ORDER CHECKLIST
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 12 }}>
                                                MKG/F-08 Rev.No.00 Dtd: 08.08.18
                                            </div>

                                            <div style={{ border: '1px solid #333', marginBottom: 16, fontSize: 13 }}>
                                                {[
                                                    ['Order Checklist No:', group.checklistNumber || '-'],
                                                    ['Name of the Customer:', selectedRfq?.customer || '-'],
                                                    ['Product Code:', group.productCode],
                                                    ['Description:', group.description || '-'],
                                                    ['Sales Order No:', selectedRfq?.so_id || '-'],
                                                    ['P.O. No / Date:', `${purchaseOrder || '-'} / ${(purchaseOrderDate ? formatDateDisplay(purchaseOrderDate) : createdDate) || '-'}`],
                                                ].map(([label, value], i, arr) => (
                                                    <div
                                                        key={label}
                                                        style={{
                                                            display: 'flex',
                                                            padding: '6px 10px',
                                                            borderBottom: i === arr.length - 1 ? 'none' : '1px solid #333',
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 700, width: 190, flexShrink: 0 }}>{label}</span>
                                                        <span>{value}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {(() => {
                                                const groups = {};
                                                const order = [];
                                                group.items.forEach((item) => {
                                                    const key = item.analysis_type || "Uncategorized";
                                                    if (!groups[key]) { groups[key] = []; order.push(key); }
                                                    groups[key].push(item);
                                                });
                                                return order.map((analysisType) => (
                                                    <div key={analysisType} style={{ marginBottom: 18, border: '1px solid #333' }}>
                                                        <div style={{
                                                            fontWeight: 700, fontSize: 13, textAlign: 'center',
                                                            background: '#d9d9d9', padding: '6px 0', borderBottom: '1px solid #333',
                                                        }}>
                                                            {analysisType.toUpperCase()}
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '65% 35%', fontWeight: 700, fontSize: 12.5, background: '#f1f5f9', borderBottom: '1px solid #333' }}>
                                                            <div style={{ padding: '6px 10px', borderRight: '1px solid #333' }}>Parameter</div>
                                                            <div style={{ padding: '6px 10px' }}>Value</div>
                                                        </div>
                                                        {groups[analysisType].map((item, i, arr) => (
                                                            <div
                                                                key={`${item.product_code}-${item.checklist_number}-${item.parameter_name}`}
                                                                style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: '65% 35%',
                                                                    fontSize: 12.5,
                                                                    borderBottom: i === arr.length - 1 ? 'none' : '1px solid #333',
                                                                }}
                                                            >
                                                                <div style={{ padding: '7px 10px', borderRight: '1px solid #333' }}>{item.parameter_name}{item.uom ? ` (${item.uom})` : ''}</div>
                                                                <div style={{ padding: '7px 10px', fontWeight: 600 }}>{item.value || '-'}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ));
                                            })()}

                                            <div style={{ display: 'flex', border: '1px solid #333', fontSize: 12.5, marginTop: 20 }}>
                                                <div style={{ flex: 1, padding: '8px 10px', borderRight: '1px solid #333', fontWeight: 700 }}>
                                                    Prepared by & Date: <span style={{ fontWeight: 400 }}>{stripOrdersMailbox(createdBy) || '-'} / {createdDate || '-'}</span>
                                                </div>
                                                <div style={{ flex: 1, padding: '8px 10px', fontWeight: 700 }}>
                                                    Approved by & Date: <span style={{ fontWeight: 400 }}>{stripOrdersMailbox(approvedBy) || '-'} / {approvedDate || '-'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* ── Upload drop zone ── */}
                <div style={{ marginBottom: 16 }}>
                    <label
                        htmlFor="soDocFileInput"
                        style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: 8,
                            padding: '18px 12px', cursor: 'pointer', background: '#f8fafc',
                            transition: 'background 0.2s'
                        }}
                        onDragOver={e => e.preventDefault()}
                    >
                        <span style={{ fontSize: 22 }}>📁</span>
                        <span style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginTop: 4 }}>
                            Click to attach documents
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            PDF, Word, Excel, Images, etc. (max {maxSoDocSizeKb} KB per file)
                        </span>
                        <input
                            id="soDocFileInput"
                            type="file"
                            ref={soDocFileInputRef}
                            multiple
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const rawFiles = Array.from(e.target.files);
                                e.target.value = "";
                                if (!rawFiles.length) return;

                                const files = filterFilesWithinSizeLimit(rawFiles);
                                if (!files.length) return;

                                const formData = new FormData();
                                files.forEach(f => formData.append("docs", f));

                                try {
                                    const res = await fetch(
                                        `${ORDER_API}/sales-order/upload-docs/${soIdentifier}`,
                                        {
                                            headers: authHeaders(), method: "POST", body: formData
                                        }
                                    );
                                    if (!res.ok) throw new Error(await res.text());
                                    const result = await res.json();
                                    const saved = result.files.map(f => ({
                                        name: f.originalname,
                                        filename: f.filename,
                                        file_url: `/uploads/${f.filename}`,
                                        size: f.size,
                                        type: f.mimetype,
                                        uploadedAt: formatChangeLogDateTime(new Date()),
                                        soId: soIdentifier
                                    }));
                                    setSoDocFiles(prev => [...prev, ...saved]);
                                    toast.success(`${saved.length} file(s) uploaded`);
                                } catch (err) {
                                    toast.error("File upload failed: " + err.message);
                                }
                            }}
                        />
                    </label>
                </div>

                {/* ── File table ── */}
                {soDocFiles.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 13 }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                        No documents attached yet.
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={styles.erpTh}>#</th>
                                <th style={styles.erpTh}>File Name</th>
                                <th style={styles.erpTh}>Type</th>
                                <th style={styles.erpTh}>Size</th>
                                <th style={styles.erpTh}>Uploaded At</th>
                                <th style={styles.erpTh}>Download</th>
                            </tr>
                        </thead>
                        <tbody>
                            {soDocFiles.map((doc, idx) => (
                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={styles.erpTd}>{idx + 1}</td>
                                    <td style={styles.erpTd}>
                                        <span style={{ marginRight: 6 }}>{getFileIcon(doc.name, doc.type)}</span>
                                        <span style={{ color: '#1e293b', fontWeight: 500 }}>{doc.name}</span>
                                    </td>
                                    <td style={styles.erpTd}>
                                        <span style={{
                                            background: '#f1f5f9', borderRadius: 4, padding: '2px 7px',
                                            fontSize: 11, color: '#475569', fontWeight: 600
                                        }}>
                                            {doc.type
                                                ? doc.type.split('/').pop().toUpperCase()
                                                : (doc.name?.split('.').pop() || '—').toUpperCase()}
                                        </span>
                                    </td>
                                    <td style={styles.erpTd}>
                                        {doc.size
                                            ? doc.size > 1024 * 1024
                                                ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB`
                                                : `${(doc.size / 1024).toFixed(1)} KB`
                                            : '—'}
                                    </td>
                                    <td style={styles.erpTd}>{doc.uploadedAt || '—'}</td>
                                    <td style={styles.erpTd}>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (!doc.filename) return;
                                                try {
                                                    const res = await fetch(
                                                        `${ORDER_API}/sales-order/download-doc/${soIdentifier}/${doc.filename}`,
                                                        { headers: authHeaders() }
                                                    );
                                                    if (!res.ok) {
                                                        const err = await res.json().catch(() => ({}));
                                                        throw new Error(err.message || `Download failed (${res.status})`);
                                                    }
                                                    const blob = await res.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const link = document.createElement('a');
                                                    link.href = url;
                                                    link.download = doc.name || doc.filename;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();
                                                    window.URL.revokeObjectURL(url);
                                                } catch (err) {
                                                    toast.error(err.message || "Failed to download file");
                                                }
                                            }}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                color: '#2563eb', fontWeight: 600, fontSize: 12,
                                                textDecoration: 'none', padding: '3px 10px',
                                                border: '1px solid #bfdbfe', borderRadius: 5,
                                                background: '#eff6ff', cursor: 'pointer'
                                            }}
                                        >
                                            ⬇ Download
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    useEffect(() => {
        if (location.state?.openSoId) {
            const soId = location.state.openSoId;
            handleOpenModal(soId, 'view');
            window.history.replaceState({}, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // Payment History only makes sense once the SO has actually been
    // approved (fully or partially) — before that there's nothing to
    // record a payment against. Never shown in the convert-from-RFQ
    // flow either, since the SO doesn't exist yet at that point.
    const isPaymentHistoryVisible =
        modalMode !== 'convert' &&
        ['Approved', 'Partially Approved'].includes(selectedRfq?.so_status);

    // If the SO's status changes out from under an open Payment History
    // tab (e.g. an edit resets it back to "Pending for Approval"), fall
    // back to the Basic tab rather than leaving the user on a tab with
    // no button and no content.
    useEffect(() => {
        if (activeTab === "Payment History" && !isPaymentHistoryVisible) {
            setActiveTab("basic");
        }
    }, [activeTab, isPaymentHistoryVisible]);

    return (
        <Layout>
            <style>{`
                .no-spinner::-webkit-outer-spin-button,
                .no-spinner::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .no-spinner {
                    -moz-appearance: textfield;
                }
            `}</style>

            <div style={styles.pageTitleBar}>
    <div>
        <div style={styles.pageTitle}>Sales Order</div>
        <div style={styles.pageSubtitle}>Manage and view sales orders</div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
            className="btn btn-dark"
            style={styles.primaryBtn}
            onClick={() => {
                if (!canCreate) {
                    toast.error("You don't have Create access. Please contact admin.");
                    return;
                }
                handleOpenCreateSO();
            }}
        >
            + Create Sales Order
        </button>

                    <button
                        onClick={() => {
                            setManualExists(null);
                            setManualFile(null);
                            setShowManual(true);
                            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                .then((res) => res.json())
                                .then((data) => {
                                    const norm = (v) => String(v || "").trim().toLowerCase();
                                    const match = (data?.data || []).find((m) =>
                                        norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                        norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                        norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                    );
                                    if (match && match.drive_link) {
                                        setManualFile(match);
                                        setManualExists(true);
                                    } else {
                                        setManualExists(false);
                                    }
                                })
                                .catch(() => setManualExists(false));
                        }}
                        title="View User Manual"
            style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                color: "#3b82f6",
                fontSize: "14px",
                fontWeight: "600",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
        >
            <FileText size={18} />
            User Manual
        </button>
    </div>
</div>

{showManual && (
    <div
        onClick={() => setShowManual(false)}
        style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
        }}
    >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 20px",
                    borderBottom: "1px solid #e2e8f0",
                }}
            >
                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                    User Manual
                </h4>
                <button
                    onClick={() => setShowManual(false)}
                    style={{
                        border: "none",
                        background: "#f1f5f9",
                        borderRadius: "8px",
                        width: "32px",
                        height: "32px",
                        cursor: "pointer",
                        fontSize: "18px",
                        color: "#475569",
                        lineHeight: 1,
                    }}
                    title="Close"
                >
                    ×
                </button>
            </div>

                                    {manualExists === null && (
                            <div style={{
                                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#94a3b8", fontSize: "14px"
                            }}>
                                Checking for user manual...
                            </div>
                        )}

                        {manualExists === false && (
                            <div style={{
                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                            }}>
                                <FileText size={40} color="#cbd5e1" />
                                <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                    User Manual Not Available
                                </div>
                                <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                    The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                </div>
                            </div>
                        )}

                        {manualExists === true && manualFile && (() => {
                            const MANUAL_SCALE = 1.25; // 1.0 = normal, 1.25 = 25% bigger, 1.5 = 50% bigger
                            return (
                                <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                    <iframe
                                        src={manualFile.drive_link.replace("/view", "/preview")}
                                        title={manualFile.file_name || "User Manual"}
                                        allow="autoplay"
                                        style={{
                                            border: "none",
                                            width: `${100 / MANUAL_SCALE}%`,
                                            height: `${100 / MANUAL_SCALE}%`,
                                            transform: `scale(${MANUAL_SCALE})`,
                                            transformOrigin: "top left",
                                        }}
                                    />
                                </div>
                            );
                        })()}
        </div>
    </div>
)}
            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={salesOrgList.map(item => ({ value: item.code || item.name || item.sales_organization, label: item.code || item.name || item.sales_organization }))}
                            value={salesOrganization ? { value: salesOrganization, label: salesOrganization } : null}
                            onChange={opt => setSalesOrganization(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={customerSegmentList.map(item => ({ value: item.name || item.customer_segment || item, label: item.name || item.customer_segment || item }))}
                            value={customerSegment ? { value: customerSegment, label: customerSegment } : null}
                            onChange={opt => setCustomerSegment(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={channelList.map(item => ({ value: item.code || item.name || item.distribution_channel, label: item.code || item.name || item.distribution_channel }))}
                            value={dChannel ? { value: dChannel, label: dChannel } : null}
                            onChange={opt => setDCHannel(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>RFQ ID</label>
                        <Select
                            options={dropdownOptions.rfqIds.map(opt => ({ value: opt, label: opt }))}
                            value={rfqId ? { value: rfqId, label: rfqId } : null}
                            onChange={opt => setRfqId(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>SO ID</label>
                        <Select
                            options={dropdownOptions.soIds.map(opt => ({ value: opt, label: opt }))}
                            value={soIdFilter ? { value: soIdFilter, label: soIdFilter } : null}
                            onChange={opt => setSoIdFilter(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer</label>
                        <AsyncSelect
                            cacheOptions loadOptions={loadCustomerOptions} defaultOptions
                            value={selectedCustomer} onChange={setSelectedCustomer}
                            placeholder="Select Customer" isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Status</label>
                        <Select
                            options={dropdownOptions.statuses.map(opt => ({ value: opt, label: opt }))}
                            value={statusFilter ? { value: statusFilter, label: statusFilter } : null}
                            onChange={opt => setStatusFilter(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Creation Date From</label>
                        <DatePicker
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            selected={creationDateFrom}
                            onChange={(date) => setCreationDateFrom(date)}
                            isClearable
                            maxDate={creationDateTo || null}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Creation Date To</label>
                        <DatePicker
                            className="form-control form-control-sm"
                            wrapperClassName="w-100"
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            selected={creationDateTo}
                            onChange={(date) => setCreationDateTo(date)}
                            isClearable
                            minDate={creationDateFrom || null}
                        />
                    </div>
                </div>
            </div>

            {showExpiredOnly && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fca5a5',
                        color: '#991b1b',
                        borderRadius: 6,
                        padding: '8px 14px',
                        margin: '10px 0',
                        fontSize: 14,
                    }}
                >
                    <span>
                        Showing only Sales Orders with expired validity ({filteredData.length})
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowExpiredOnly(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#991b1b',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                    >
                        Clear filter
                    </button>
                </div>
            )}

            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={styles.erpTh}>SO ID</th>
                            <th style={styles.erpTh}>RFQ ID</th>
                            <th style={styles.erpTh}>Customer</th>
                            <th style={styles.erpTh}>Sales Org</th>
                            <th style={styles.erpTh}>Customer Segment</th>
                            <th style={styles.erpTh}>Distribution Channel</th>
                            <th style={styles.erpTh}>Product Count</th>
                            <th style={styles.erpTh}>Order Value</th>
                            <th style={styles.erpTh}>Creation Date</th>
                            <th style={styles.erpTh}>Status</th>
                            <th style={styles.erpTh}></th>
                            <th style={styles.erpTh}></th>
                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length ? paginatedData.map((item, index) => {
                            // Bootstrap's .table class paints background-color on each
                            // <td>, not the <tr> — so setting it only on <tr> gets
                            // painted over. Merge it into every cell's style instead
                            // (same fix as the RFQ/Quotation page).
                            const rowCellStyle = expiredKeys.has(item.so_id || item.rfq_id)
                                ? { ...styles.erpTd, backgroundColor: '#fee2e2' }
                                : styles.erpTd;
                            return (
                            <tr key={item.so_id || item.rfq_id || index}>
                                <td style={rowCellStyle}>
                                    {item.so_id ? (
                                        <span
                                            onClick={() => {
                                                if (!canView) {
                                                    toast.error("You don't have View access. Please contact admin.");
                                                    return;
                                                }
                                                // Prefer the real so_id: a converted/created SO row and
                                                // its (now-obsolete) pending RFQ row share the same
                                                // rfq_id, so passing rfq_id here is ambiguous and can
                                                // resolve to the wrong row on the backend.
                                                handleOpenModal(item.so_id || item.rfq_id, 'view');
                                            }}
                                            style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                        >
                                            {item.so_id}
                                        </span>
                                    ) : (
                                        "Pending"
                                    )}
                                </td>
                                <td style={rowCellStyle}>
                                    <span
                                        onClick={() => {
                                            navigate('/cable/sales/quotation', {
                                                state: { openSoId: item.rfq_id, returnTo: location.pathname }
                                            });
                                        }}
                                        style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                    >
                                        {item.rfq_id}
                                    </span>
                                </td>
                                <td style={rowCellStyle}>{item.customer}</td>
                                <td style={rowCellStyle}>{item.sales_organization || "—"}</td>
                                <td style={rowCellStyle}>{item.customer_segment || "—"}</td>
                                <td style={rowCellStyle}>{item.distribution_channel || "—"}</td>
                                <td style={rowCellStyle}>{item.product_count ?? "—"}</td>
                                <td style={rowCellStyle}>
                                    {item.grand_total != null
                                        ? Number(item.grand_total).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : '—'}
                                </td>
                                <td style={rowCellStyle}>
                                    {item.created_at ? formatDateDisplay(item.created_at) : '—'}
                                </td>
                                <td style={rowCellStyle}>{item.so_status}</td>
                                <td style={rowCellStyle}>
                                    <button
                                        className="btn btn-outline-dark btn-sm py-0"
                                        onClick={() => {
                                            if (!canView) {
                                                toast.error("You don't have View access. Please contact admin.");
                                                return;
                                            }
                                            // Same so_id-first fix as the so_id link above.
                                            handleOpenModal(item.so_id || item.rfq_id, 'view');
                                        }}
                                    >
                                        View
                                    </button>
                                </td>
                                <td style={rowCellStyle}>
                                    <button
                                        className="btn btn-dark btn-sm py-0"
                                        onClick={() => {
                                            if (!canCreate) {
                                                toast.error("You don't have Create access. Please contact admin.");
                                                return;
                                            }
                                            handleOpenModal(item.rfq_id, 'convert');
                                        }}
                                        disabled={!!item.so_id}
                                    >
                                        Convert
                                    </button>
                                </td>
                                <td style={rowCellStyle}>
                                    {item.so_id && (
                                        <button
                                            title="Delete"
                                            onClick={() => handleDeleteSO(item.so_id)}
                                            style={{
                                                background: "#fff",
                                                border: "1px solid #f5c2c7",
                                                borderRadius: "4px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: "4px 8px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                            </svg>
                                        </button>
                                    )}
                                </td>
                            </tr>
                            );
                        }) : (
                            <tr><td colSpan="13" className="text-center py-4 text-muted">No Records Found</td></tr>
                        )}
                    </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="d-flex align-items-center justify-content-between pt-3">
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                        Page <b>{page}</b> of <b>{totalPages}</b> • Total {filteredData.length}
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                        <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            </div>

            {showModal && selectedRfq && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1300px", width: "95%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                {modalMode === 'convert' ? `RFQ: ${selectedRfq.rfq_id}` : `Sales Order: ${selectedRfq.so_id || selectedRfq.rfq_id}`}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>

                            {/* Order Type — first field in the modal, same position as the
                                Create Sales Order modal, instead of being buried inside the
                                bordered Customer box or hidden in the Basic tab only. */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Order Type</label>
                                    {isEditing || modalMode === 'convert' ? (
                                        <Select
                                            options={soOrderTypeOptions}
                                            value={soOrderTypeOptions.find(o => o.value === soOrderType) || soOrderTypeOptions[0]}
                                            onChange={opt => {
                                                const val = opt ? opt.value : "domestic";
                                                setSoOrderType(val);
                                                if (val !== "export" && newSO.customerCountry) {
                                                    setNewSO(prev => ({ ...prev, customerCountry: "" }));
                                                }
                                            }}
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    ) : (
                                        <div className="form-control bg-light" style={styles.compactInput}>
                                            {!isFieldVisible('order_type') ? (
                                                <MaskedValue />
                                            ) : ((selectedRfq.order_type || selectedRfq.orderType || (soOrderType === "export" ? "Export" : "Domestic")))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>
                                        Quotation Type <span style={{ color: '#dc2626' }}>*</span>
                                    </label>

                                    {isEditing || modalMode === 'convert' ? (
                                        <Select
                                            options={quotationTypeList.map(item => ({
                                                value: item.name || item.value,
                                                label: item.name || item.label || item.value
                                            }))}
                                            value={quotationType}
                                            onChange={(opt) => {
                                                setQuotationType(opt);
                                                if (!isRcDriven(opt)) {
                                                    setRcNumber("");
                                                }
                                            }}
                                            isDisabled={quotationTypeLocked}
                                            placeholder="Select Quotation Type"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999
                                                })
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="form-control bg-light"
                                            style={styles.compactInput}
                                            title={
                                                quotationTypeLocked
                                                    ? "Auto-set from RFQ conversion — not editable"
                                                    : undefined
                                            }
                                        >
                                            {quotationType ? quotationType.label : "—"}
                                        </div>
                                    )}
                                </div>
                                {isRcDriven(quotationType) && (
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>RC Number <span style={{ color: '#dc2626' }}>*</span></label>
                                        {isEditing && !quotationTypeLocked ? (
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={styles.compactInput}
                                                value={rcNumber}
                                                onChange={e => setRcNumber(e.target.value)}
                                                placeholder="Enter RC Number"
                                            />
                                        ) : (
                                            <div className="form-control bg-light" style={styles.compactInput}>
                                                {rcNumber || "—"}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Sales Organization</label>
                                    <div className="form-control bg-light" style={styles.compactInput}>
                                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('sales_organization') ? (
                                            <MaskedValue />
                                        ) : (salesOrganization || selectedRfq.salesOrganization || "—")}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Customer Segment</label>
                                    <div className="form-control bg-light" style={styles.compactInput}>
                                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('customer_segment') ? (
                                            <MaskedValue />
                                        ) : (selectedRfq.customer_segment || selectedRfq.customerSegment || "—")}
                                    </div>
                                </div>

                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Distribution Channel</label>
                                    <div className="form-control bg-light" style={styles.compactInput}>
                                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('distribution_channel') ? (
                                            <MaskedValue />
                                        ) : (dChannel || selectedRfq.distributionChannel || "—")}
                                    </div>
                                </div>
                            </div>

                            {/* Order Type / Customer Country moved to the always-visible
                                Customer box above the tabs (Order Type first, Customer
                                Country next to Customer) — see below — so they're visible
                                no matter which tab (Product, Checklist, etc.) is active,
                                instead of being hidden inside the Basic tab only. */}
                            {/* ---- Created By / Created Date / Approved By / Approved Date ---- */}
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created By</label>
                                    <input type="text" className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={!isEditing && modalMode !== 'convert' && !isFieldVisible('created_by') ? '••••••' : createdBy} readOnly />
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Created Date</label>
                                    <input type="text" className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={!isEditing && modalMode !== 'convert' && !isFieldVisible('creation_date') ? '••••••' : createdDate} readOnly />
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Approved By</label>
                                    <input type="text" className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={!isEditing && modalMode !== 'convert' && !isFieldVisible('approved_by') ? '••••••' : (approvedBy || '—')} readOnly />
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Approved Date</label>
                                    <input type="text" className="form-control"
                                        style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                        value={!isEditing && modalMode !== 'convert' && !isFieldVisible('approved_at') ? '••••••' : (approvedDate || '—')} readOnly />
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Markup (%)</label>
                                    {isEditing ? (
                                        <input type="number" className="form-control" style={styles.compactInput}
                                            placeholder="Markup %"
                                            value={markup}
                                            onChange={(e) => handleHeaderMarkupChange(e.target.value)}
                                        />
                                    ) : (
                                        <div className="form-control bg-light" style={styles.compactInput}>
                                            {modalMode !== 'convert' && !isFieldVisible('header_markup') ? <MaskedValue /> : (markup || "—")}
                                        </div>
                                    )}
                                </div>
                                {/* 🔧 REMOVED: the Exchange Rate/Currency field used to render here
                                    as its own column. soCurrency/soExchangeRate are still populated
                                    (from the SO record on load, or the exchange-rate-config lookup on
                                    Customer Country change) and still drive the Product tab's currency
                                    badge and converted Unit Price/Grand Total — just not shown/editable
                                    in this header row anymore. */}
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Customer Inspection <span style={{ color: '#dc2626' }}>*</span></label>
                                    <div className="d-flex align-items-center gap-2">
                                        {isEditing ? (
                                            <select className="form-control" style={styles.compactInput}
                                                value={customerInspection}
                                                onChange={(e) => setCustomerInspection(e.target.value)}
                                            >
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        ) : (
                                            <div className="form-control bg-light" style={styles.compactInput}>
                                                {modalMode !== 'convert' && !isFieldVisible('customer_inspection') ? <MaskedValue /> : (customerInspection || "—")}
                                            </div>
                                        )}
                                        {/* Icon replaces the old 4 inline Proposed/Confirmed fields.
                                            Works before converting and regardless of isEditing — the
                                            modal it opens is always editable and saves independently. */}
                                        {customerInspection === "Yes" && (
                                            <button
                                                type="button"
                                                title="View / edit inspection schedule"
                                                onClick={openInspectionModal}
                                                style={{
                                                    flexShrink: 0, width: 34, height: 34, borderRadius: 8,
                                                    border: '1px solid #dee2e6', background: '#fff',
                                                    cursor: 'pointer', fontSize: 15, display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center'
                                                }}
                                            >
                                                📅
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Sales Block <span style={{ color: '#dc2626' }}>*</span></label>
                                    {isEditing ? (
                                        <select className="form-control" style={styles.compactInput}
                                            value={salesBlock}
                                            onChange={(e) => handleHeaderSalesBlockChange(e.target.value)}
                                        >
                                            <option value="Blocked">Blocked</option>
                                            <option value="Released">Released</option>
                                        </select>
                                    ) : (
                                        <div className="form-control bg-light" style={styles.compactInput}>
                                            {salesBlock || "—"}
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Validity <span style={{ color: '#dc2626' }}>*</span></label>
                                    {isEditing ? (
                                        <div className="d-flex gap-2">
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ ...styles.compactInput, width: '100px' }}
                                                placeholder="e.g. 15"
                                                value={validityNumber}
                                                onChange={(e) => setValidityNumber(e.target.value)}
                                            />
                                            <select
                                                className="form-control"
                                                style={{ ...styles.compactInput, width: '130px' }}
                                                value={validityUnit}
                                                onChange={(e) => setValidityUnit(e.target.value)}
                                            >
                                                <option value="days">Days</option>
                                                <option value="months">Months</option>
                                                <option value="years">Years</option>
                                            </select>
                                        </div>


                                    ) : (
                                        <div className="form-control bg-light" style={{ ...styles.compactInput }}>
                                            {modalMode !== 'convert' && !isFieldVisible('validity_date') ? <MaskedValue /> : (validityDate ? formatDateDisplay(validityDate) : "—")}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-3 bg-light rounded border mb-3">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>RFQ ID</label>
                                        <div className="form-control bg-white" style={styles.compactInput}>
                                            {selectedRfq?.rfq_id || "—"}
                                        </div>
                                    </div>
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>
                                            Customer
                                        </label>
                                        <div className="form-control bg-white" style={styles.compactInput}>
                                            {!isEditing && modalMode !== 'convert' && !isFieldVisible('customer') ? <MaskedValue /> : (selectedRfq?.customer || "—")}
                                        </div>
                                    </div>
                                    {(soOrderType === "export" || selectedRfq.order_type === "Export" || selectedRfq.orderType === "Export") && (
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>Customer Country <span style={{ color: '#dc2626' }}>*</span></label>
                                            {isEditing || modalMode === 'convert' ? (
                                                <Select
                                                    options={countryOptions.map(c => ({ value: c.name || c, label: c.name || c }))}
                                                    value={newSO.customerCountry ? { value: newSO.customerCountry, label: newSO.customerCountry } : (selectedRfq.customer_country || selectedRfq.customerCountry ? { value: selectedRfq.customer_country || selectedRfq.customerCountry, label: selectedRfq.customer_country || selectedRfq.customerCountry } : null)}
                                                    onChange={opt => setNewSO(prev => ({ ...prev, customerCountry: opt ? opt.value : "" }))}
                                                    placeholder="Select Country"
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            ) : (
                                                <div className="form-control bg-white" style={styles.compactInput}>
                                                    {!isFieldVisible('customer_country') ? (
                                                        <MaskedValue />
                                                    ) : (selectedRfq.customer_country || selectedRfq.customerCountry || newSO.customerCountry || "—")}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>
                                            Purchase Order <span style={{ color: '#dc2626' }}>*</span>
                                        </label>
                                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('purchase_order') ? (
                                            <div className="form-control bg-light" style={styles.compactInput}><MaskedValue /></div>
                                        ) : (
                                            <input type="text" className="form-control" style={styles.compactInput}
                                                placeholder="Purchase Order"
                                                value={purchaseOrder}
                                                onChange={(e) => setPurchaseOrder(e.target.value)}
                                                disabled={!isEditing && modalMode !== 'convert'}
                                            />
                                        )}
                                    </div>
                                    <div className="col-md-2">
                                        <label style={styles.compactLabel}>
                                            PO Date {modalMode === 'convert' && <span style={{ color: '#dc2626' }}>*</span>}
                                        </label>
                                        {(isEditing || modalMode === 'convert') ? (
                                            <div style={{ position: 'relative' }}>
                                                <DatePicker
                                                    className="form-control"
                                                    wrapperClassName="w-100"
                                                    customInput={<input style={{ ...styles.compactInput, paddingRight: 32 }} />}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="dd/mm/yyyy"
                                                    selected={poDateToObj(purchaseOrderDate)}
                                                    onChange={(dt) => setPurchaseOrderDate(poDateFromObj(dt))}
                                                />
                                                <CalendarDays size={16} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none' }} />
                                            </div>
                                        ) : (
                                            <div className="form-control bg-light" style={styles.compactInput}>
                                                {!isFieldVisible('purchase_order') ? '••••••' : (purchaseOrderDate ? formatDateDisplay(purchaseOrderDate) : '—')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-2">
                                        <label style={styles.compactLabel}>Advance Payment</label>
                                        {!isEditing && modalMode !== 'convert' && !isFieldVisible('advance_payment') ? (
                                            <div className="form-control bg-light" style={styles.compactInput}><MaskedValue /></div>
                                        ) : (isEditing || modalMode === 'convert') ? (
                                            <select className="form-control" style={styles.compactInput}
                                                value={advancePaymentFlag}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setAdvancePaymentFlag(val);
                                                    if (val === "No") setAdvancePayment("");
                                                }}>
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        ) : (
                                            <div className="form-control bg-light" style={styles.compactInput}>
                                                {advancePaymentFlag || "No"}
                                            </div>
                                        )}
                                    </div>
                                    {advancePaymentFlag === "Yes" && (
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>Advance Amount</label>
                                            {!isEditing && modalMode !== 'convert' && !isFieldVisible('advance_payment') ? (
                                                <div className="form-control bg-light" style={styles.compactInput}><MaskedValue /></div>
                                            ) : (isEditing || modalMode === 'convert') ? (
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    style={styles.compactInput}
                                                    value={advancePayment}
                                                    onChange={(e) => setAdvancePayment(e.target.value)}
                                                    placeholder="Enter advance amount"
                                                />
                                            ) : (
                                                <div className="form-control bg-light" style={styles.compactInput}>
                                                    {advancePayment || "—"}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={styles.tabs}>
                                {['basic', 'product'].map(tab => (
                                    <button
                                        key={tab}
                                        className={`btn ${activeTab === tab ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}

                                {/* 🆕 FIX: also show in 'view' mode. Previously this only
                                    checked selectedRfq?.so_id, which is null for SOs whose
                                    sales_order row never got a real so_id assigned at
                                    conversion time — so an already-created SO could open in
                                    "view" with no Checklist tab at all, even though the exact
                                    same RFQ showed one under "Convert". The click handler
                                    below already falls back to building the checklist preview
                                    from selectedRfq.productDetails when so_id is missing, so
                                    it's safe to show the tab in 'view' mode unconditionally. */}
                                {(modalMode === 'convert' || modalMode === 'view' || selectedRfq?.so_id) && (
                                    <button
                                        className={`btn ${activeTab === "checklist" ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => {
                                            setActiveTab("checklist");
                                            if (selectedRfq.so_id) {
                                                // Already-saved SO — pull its actual saved,
                                                // product-wise checklist.
                                                fetchChecklist(selectedRfq.so_id);
                                            } else {
                                                // Convert mode: SO doesn't exist yet, but we
                                                // already know which products will be on it —
                                                // build the product-wise preview from those.
                                                fetchChecklistForCurrentProducts(selectedRfq.productDetails);
                                            }
                                        }}
                                    >
                                        Checklist
                                    </button>
                                )}

                                {isDiscountReviewVisible && (
                                    <button
                                        className={`btn ${activeTab === "discount_review" ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => setActiveTab("discount_review")}
                                    >
                                        Discount Review
                                    </button>
                                )}

                                {modalMode !== 'convert' && selectedRfq?.so_id && (
                                    <button
                                        className={`btn ${activeTab === "review" ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => {
                                            setActiveTab("review");
                                            // Review's Approve buttons need checklistItems/rcReviewStatus
                                            // to gate on an incomplete checklist — same data the
                                            // Checklist/Docs tabs load, fetched here too so it's ready
                                            // without requiring a visit to that tab first.
                                            if (!checklistItems.length) {
                                                fetchChecklist(selectedRfq.so_id);
                                            }
                                        }}
                                    >
                                        Review
                                    </button>
                                )}

                                {(modalMode === 'convert' ? ['docs'] : ['confirmation', 'docs']).map(tab => (
                                    <button
                                        key={tab}
                                        className={`btn ${activeTab === tab ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => {
                                            setActiveTab(tab);
                                            // The Docs tab shows a per-product Checklist form for
                                            // every product that has one — it needs checklistItems
                                            // loaded, same data the Checklist tab uses, so fetch it
                                            // here too rather than requiring a visit to that tab first.
                                            if (tab === 'docs' && !checklistItems.length && selectedRfq?.so_id) {
                                                fetchChecklist(selectedRfq.so_id);
                                            } else if (tab === 'docs' && !checklistItems.length && (modalMode === 'convert' || modalMode === 'view')) {
                                                // Same so_id-missing fallback as the Checklist tab
                                                // above — a 'view' SO without a real so_id still
                                                // has productDetails to preview the checklist from.
                                                fetchChecklistForCurrentProducts(selectedRfq.productDetails);
                                            }
                                        }}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}

                                {/* Payment History only becomes relevant once the SO has
                                    actually been approved (fully or partially) — before
                                    that there's nothing to record a payment against. */}
                                {isPaymentHistoryVisible && (
                                    <button
                                        className={`btn ${activeTab === "Payment History" ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => setActiveTab("Payment History")}
                                    >
                                        Payment History
                                    </button>
                                )}

                                {modalMode !== 'convert' && (
                                    <button
                                        className={`btn ${activeTab === "change_log" ? "btn-secondary" : "btn-light"}`}
                                        style={styles.compactTabBtn}
                                        onClick={() => setActiveTab("change_log")}
                                    >
                                        Change Log
                                    </button>
                                )}
                            </div>

                            <div className="mt-3">
                                {activeTab === "basic" && renderBasicTab()}
                                {activeTab === "product" && renderProductTab()}
                                {activeTab === "review" && (
                                    modalMode === 'convert' ? renderProductTab() : <Bd productDetails={selectedRfq.productDetails} selectedRfq={selectedRfq} canReview={isAdmin || isReviewOnly || canApprove} checklistItems={checklistItems} rcReviewStatus={rcReviewStatus} checklistMandatory={checklistMandatory} />
                                )}

                                {activeTab === "discount_review" && isDiscountReviewVisible && (
                                    <div className="border rounded p-2">
                                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                            Showing only line items where Discount (%) exceeds Markup (%).
                                        </div>
                                        <Bd
                                            productDetails={discountReviewRows}
                                            selectedRfq={selectedRfq}
                                            canReview={canApprove}
                                            mode="discount"
                                        />
                                    </div>
                                )}
                                {activeTab === "confirmation" && renderConfirmationTab()}

                                {activeTab === "Payment History" && isPaymentHistoryVisible && renderPaymentHistoryTab()}

                                {activeTab === "checklist" && renderChecklistTab()}

                                {activeTab === "change_log" && (
                                    <div style={styles.tableContainer}>
                                        <table className="table" style={styles.erpTable}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.erpTh}>Date & Time</th>
                                                    <th style={styles.erpTh}>User</th>
                                                    <th style={styles.erpTh}>Action</th>
                                                    <th style={styles.erpTh}>Status</th>
                                                    <th style={styles.erpTh}>Field</th>
                                                    <th style={styles.erpTh}>Previous Value</th>
                                                    <th style={styles.erpTh}>Modified Value</th>
                                                    <th style={styles.erpTh}>Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {changeLogs.length > 0 ? changeLogs.map((log, index) => {
                                                    const dateVal = log.updated_at || log.created_at;
                                                    const userVal = log.updated_by || log.created_by;

                                                    return (
                                                        <tr key={log.id || index}>
                                                            <td style={styles.erpTd}>
                                                                {formatChangeLogDateTime(dateVal)}
                                                            </td>
                                                            <td style={styles.erpTd}>{userVal || "Unknown"}</td>
                                                            <td style={styles.erpTd}>{log.action_type}</td>
                                                            <td style={styles.erpTd}>{log.doc_status}</td>
                                                            <td style={styles.erpTd}>
                                                                {log.field_name
                                                                    ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                                                    : '-'}
                                                            </td>
                                                            <td style={{ ...styles.erpTd, maxWidth: '150px', wordWrap: 'break-word' }}>
                                                                {log.old_value || '-'}
                                                            </td>
                                                            <td style={{ ...styles.erpTd, fontWeight: 600, maxWidth: '150px', wordWrap: 'break-word' }}>
                                                                {log.new_value || '-'}
                                                            </td>
                                                            <td style={{ ...styles.erpTd, maxWidth: '200px' }}>
                                                                {log.comment || '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                }) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center text-muted p-3">
                                                            No change logs available
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {activeTab === "docs" && renderDocsTab()}
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            {modalMode === 'convert' && (
                                <button className="btn btn-dark btn-sm" disabled={loading} onClick={() => {
                                    if (!canCreate) {
                                        toast.error("You don't have Create access. Please contact admin.");
                                        return;
                                    }
                                    handleGenerateSO();
                                }}>
                                    {loading ? "Saving..." : "Save"}
                                </button>
                            )}

                            {modalMode === 'view' && selectedRfq?.so_id && (
                                <button
                                    className={`btn btn-sm btn-dark`}
                                    onClick={() => {
                                        if (!isEditing) {
                                            if (!canEdit) {
                                                toast.error("You don't have Edit access. Please contact admin.");
                                                return;
                                            }
                                            const snapshot = {};
                                            (selectedRfq?.productDetails || []).forEach((prod, idx) => {
                                                snapshot[idx] = {
                                                    unitPrice: parseFloat(prod.unit_price) || 0,
                                                    markup: parseFloat(prod.markup ?? markup) || 0
                                                };
                                            });
                                            setEditPriceSnapshot(snapshot);
                                            setManualPriceEdits({});
                                            clearGlobalFilters();
                                            // Full pre-edit snapshot (Planning Block excluded on purpose) so
                                            // Save can tell whether this is a real change or just a Planning
                                            // Block edit, and only warn about a status reset for the former.
                                            preEditSnapshotRef.current = {
                                                purchaseOrder, purchaseOrderDate, advancePaymentFlag, advancePayment, markup, customerInspection,
                                                validity_terms: validityNumber ? `${validityNumber} ${validityUnit}` : null,
                                                customerInspectionProposedDate, customerInspectionProposedTime,
                                                customerInspectionConfirmedDate, customerInspectionConfirmedTime,
                                                productDetails: (selectedRfq?.productDetails || []).map(p => ({
                                                    id: p.id,
                                                    quantity: p.quantity,
                                                    unit_price: p.unit_price,
                                                    unit_cost: p.unit_cost,
                                                    discount: p.discount,
                                                    description: p.description,
                                                    uom: p.uom,
                                                    requirement_date: p.requirement_date
                                                }))
                                            };
                                            setIsEditing(true);
                                        } else {
                                            if (!canEdit) {
                                                toast.error("You don't have Edit access. Please contact admin.");
                                                return;
                                            }
                                            handleSaveSalesOrder();
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : (isEditing ? "Save" : "Edit")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {createModalOpen && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1300px", width: "95%", maxHeight: "90vh" }}>

                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.2rem' }}>Create Sales Order</div>
                            <button style={styles.iconBtn} onClick={() => setCreateModalOpen(false)}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            <div className="row g-3 mb-3">
                                <div className="col-md-3">
                                    <label style={{ ...styles.compactLabel, display: 'block' }}>Order Type</label>
                                    <Select
                                        options={soOrderTypeOptions}
                                        value={soOrderTypeOptions.find(o => o.value === soOrderType) || soOrderTypeOptions[0]}
                                        onChange={opt => {
                                            const val = opt ? opt.value : "domestic";
                                            setSoOrderType(val);
                                            // Customer Country dropdown only shows for Export — clear any
                                            // stale value picked before switching back to Domestic.
                                            if (val !== "export" && newSO.customerCountry) {
                                                setNewSO(prev => ({ ...prev, customerCountry: "" }));
                                            }
                                        }}
                                        placeholder="Select"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={dropdownOptionStyle}
                                    />
                                </div>
                                <div className="col-md-5">
                                    <label style={{ ...styles.compactLabel, display: 'block' }}>
                                        Reference SO <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}></span>
                                    </label>
                                    <Select
                                        options={refSoList}
                                        value={selectedRefSo}
                                        onChange={handleRefSoSelect}
                                        isClearable
                                        placeholder="Select an existing SO to copy from..."
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            control: (b) => ({ ...b, minHeight: 34, fontSize: '13px', borderColor: '#6366f1', boxShadow: selectedRefSo ? '0 0 0 2px rgba(99,102,241,0.2)' : 'none' }),
                                            menuPortal: (b) => ({ ...b, zIndex: 99999 })
                                        }}
                                    />
                                    {soUploadMessage && (
                                        <small className="text-success fw-bold d-block mt-1" style={{ fontSize: '12px' }}>
                                            {soUploadMessage}
                                        </small>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    <label style={styles.compactLabel}>Quotation Type <span style={{ color: '#dc2626' }}>*</span></label>
                                    {!quotationTypeLocked ? (
                                        <Select
                                            options={quotationTypeList.map(item => ({ value: item.name, label: item.name }))}
                                            value={quotationType}
                                            onChange={opt => {
                                                setQuotationType(opt);
                                                if (!isRcDriven(opt)) setRcNumber("");
                                                // RC Driven orders aren't tied to an RFQ — clear any
                                                // RFQ ID already picked so it doesn't linger hidden.
                                                if (isRcDriven(opt)) {
                                                    setSelectedCreateRfq(null);
                                                    setNewSO(prevSO => ({ ...prevSO, rfqId: "" }));
                                                }
                                            }}
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={dropdownOptionStyle}
                                        />
                                    ) : (
                                        <div className="form-control bg-light" style={styles.compactInput} title="Sales Order is tied to an RFQ — always Quotation Driven, not editable">
                                            {quotationType ? quotationType.label : "—"}
                                        </div>
                                    )}
                                </div>
                                {isRcDriven(quotationType) && !quotationTypeLocked && (
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>RC Number <span style={{ color: '#dc2626' }}>*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={styles.compactInput}
                                            value={rcNumber}
                                            onChange={e => setRcNumber(e.target.value)}
                                            placeholder="Enter RC Number"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Sales Organization <span style={{ color: '#dc2626' }}>*</span></label>
                                        <Select
                                            options={salesOrgList.map(item => ({ value: item.code || item.name || item.sales_organization, label: item.code || item.name || item.sales_organization }))}
                                            value={newSO.salesOrganization ? { value: newSO.salesOrganization, label: newSO.salesOrganization } : null}
                                            onChange={opt => setNewSO({ ...newSO, salesOrganization: opt ? opt.value : "" })}
                                            isClearable
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={dropdownOptionStyle}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Customer Segment</label>
                                        <Select
                                            options={customerSegmentList.map(item => ({ value: item.name || item.customer_segment || item, label: item.name || item.customer_segment || item }))}
                                            value={newSO.customerSegment ? { value: newSO.customerSegment, label: newSO.customerSegment } : null}
                                            onChange={opt => setNewSO({ ...newSO, customerSegment: opt ? opt.value : "" })}
                                            isClearable
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={dropdownOptionStyle}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label style={styles.compactLabel}>Distribution Channel</label>
                                        <Select
                                            options={channelList.map(item => ({ value: item.code || item.name || item.distribution_channel, label: item.code || item.name || item.distribution_channel }))}
                                            value={newSO.distributionChannel ? { value: newSO.distributionChannel, label: newSO.distributionChannel } : null}
                                            onChange={opt => setNewSO({ ...newSO, distributionChannel: opt ? opt.value : "" })}
                                            isClearable
                                            placeholder="Select"
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={dropdownOptionStyle}
                                        />
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Created By</label>
                                        <input type="text" className="form-control"
                                            style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                            value={createdBy} readOnly />
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Created Date</label>
                                        <input type="text" className="form-control"
                                            style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                            value={createdDate} readOnly />
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Approved By</label>
                                        <input type="text" className="form-control"
                                            style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                            value={approvedBy || '—'} readOnly />
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Approved Date</label>
                                        <input type="text" className="form-control"
                                            style={{ ...styles.compactInput, background: '#f8fafc', color: '#374151', cursor: 'default' }}
                                            value={approvedDate || '—'} readOnly />
                                    </div>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Markup (%)</label>
                                        <input type="number" className="form-control" style={styles.compactInput}
                                            placeholder="e.g. 5" value={markup} onChange={(e) => handleDirectHeaderMarkupChange(e.target.value)} />
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Customer Inspection <span style={{ color: '#dc2626' }}>*</span></label>
                                        <div className="d-flex align-items-center gap-2">
                                            <select className="form-control" style={styles.compactInput}
                                                value={customerInspection} onChange={(e) => setCustomerInspection(e.target.value)}>
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                            {/* Same icon/modal as the view/edit modal — works pre-conversion,
                                                before this SO has even been saved. */}
                                            {customerInspection === "Yes" && (
                                                <button
                                                    type="button"
                                                    title="View / edit inspection schedule"
                                                    onClick={openInspectionModal}
                                                    style={{
                                                        flexShrink: 0, width: 34, height: 34, borderRadius: 8,
                                                        border: '1px solid #dee2e6', background: '#fff',
                                                        cursor: 'pointer', fontSize: 15, display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center'
                                                    }}
                                                >
                                                    📅
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Sales Block <span style={{ color: '#dc2626' }}>*</span></label>
                                        <select className="form-control" style={styles.compactInput}
                                            value={salesBlock} onChange={(e) => handleDirectHeaderSalesBlockChange(e.target.value)}>
                                            <option value="Blocked">Blocked</option>
                                            <option value="Released">Released</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label style={styles.compactLabel}>Validity Terms <span style={{ color: '#dc2626' }}>*</span></label>
                                        <div className="d-flex gap-2">
                                            <input
                                                type="number"
                                                className="form-control"
                                                style={{ ...styles.compactInput, width: '100px' }}
                                                placeholder="e.g. 15"
                                                value={validityNumber}
                                                onChange={(e) => setValidityNumber(e.target.value)}
                                            />
                                            <select
                                                className="form-control"
                                                style={{ ...styles.compactInput, width: '130px' }}
                                                value={validityUnit}
                                                onChange={(e) => setValidityUnit(e.target.value)}
                                            >
                                                <option value="days">Days</option>
                                                <option value="months">Months</option>
                                                <option value="years">Years</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 bg-light rounded border">
                                    <div className="row g-3 align-items-start">
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>Customer <span style={{ color: '#dc2626' }}>*</span></label>
                                            <AsyncSelect
                                                cacheOptions loadOptions={loadCustomerOptions} defaultOptions
                                                value={newSO.customer}
                                                onChange={handleCustomerChange}
                                                placeholder="Select Customer"
                                                menuPortalTarget={document.body}
                                                styles={dropdownOptionStyle}
                                            />
                                        </div>
                                        {/* 🆕 Customer Country only matters for an Export SO (it drives
                                            the exchange-rate-config lookup for Currency/Exchange Rate) —
                                            a Domestic SO has no use for it, so it's hidden entirely rather
                                            than shown-but-irrelevant. Clear any stale value if the order
                                            type is switched back to Domestic after a country was picked. */}
                                        {soOrderType === "export" && (
                                            <div className="col-md-3">
                                                <label style={styles.compactLabel}>Customer Country <span style={{ color: '#dc2626' }}>*</span></label>
                                                <Select
                                                    options={countryOptions.map(c => ({ value: c.name || c, label: c.name || c }))}
                                                    value={newSO.customerCountry ? { value: newSO.customerCountry, label: newSO.customerCountry } : null}
                                                    onChange={opt => setNewSO({ ...newSO, customerCountry: opt ? opt.value : "" })}
                                                    isClearable
                                                    placeholder="Select Country"
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={dropdownOptionStyle}
                                                />
                                            </div>
                                        )}
                                        {/* 🔧 REMOVED: Currency / Exchange Rate fields used to render here
                                            in the Customer row. They still auto-fill into soCurrency /
                                            soExchangeRate from fetchExchangeRateForCustomerCountry and are
                                            used by the Product tab — just no longer shown/editable here. */}
                                        {!isRcDriven(quotationType) && (
                                            <div className="col-md-3">
                                                <label style={styles.compactLabel}>
                                                    RFQ ID
                                                </label>
                                                <Select
                                                    options={rfqRefList}
                                                    value={selectedCreateRfq}
                                                    onChange={handleCreateRfqSelect}
                                                    isClearable
                                                    placeholder="Select RFQ ID..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={dropdownOptionStyle}
                                                />
                                            </div>
                                        )}
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>
                                                Purchase Order <span style={{ color: '#dc2626' }}>*</span>
                                            </label>
                                            <input type="text" className="form-control" style={styles.compactInput}
                                                value={newSO.purchaseOrder} onChange={e => setNewSO({ ...newSO, purchaseOrder: e.target.value })}
                                                placeholder="Enter PO Number" />
                                        </div>
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>
                                                PO Date <span style={{ color: '#dc2626' }}>*</span>
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <DatePicker
                                                    className="form-control"
                                                    wrapperClassName="w-100"
                                                    customInput={<input style={{ ...styles.compactInput, paddingRight: 32 }} />}
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="dd/mm/yyyy"
                                                    selected={poDateToObj(newSO.purchaseOrderDate)}
                                                    onChange={(dt) => setNewSO({ ...newSO, purchaseOrderDate: poDateFromObj(dt) })} />
                                                <CalendarDays size={16} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none' }} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <label style={styles.compactLabel}>Advance Payment</label>
                                            <select className="form-control" style={styles.compactInput}
                                                value={advancePaymentOption}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setAdvancePaymentOption(val);
                                                    if (val === "No") setNewSO(prev => ({ ...prev, advancePayment: "" }));
                                                }}>
                                                <option value="No">No</option>
                                                <option value="Yes">Yes</option>
                                            </select>
                                        </div>
                                        {advancePaymentOption === "Yes" && (
                                            <div className="col-md-3">
                                                <label style={styles.compactLabel}>Advance Amount</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    style={styles.compactInput}
                                                    value={newSO.advancePayment}
                                                    onChange={(e) => setNewSO(prev => ({ ...prev, advancePayment: e.target.value }))}
                                                    placeholder="Enter advance amount"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>


                            <div style={styles.tabs}>
                                <button
                                    className={`btn ${createActiveTab === "basic" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setCreateActiveTab("basic")}
                                >
                                    Basic
                                </button>
                                <button
                                    className={`btn ${createActiveTab === "product" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setCreateActiveTab("product")}
                                >
                                    Product
                                </button>
                                <button
                                    className={`btn ${createActiveTab === "checklist" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => {
                                        setCreateActiveTab("checklist");
                                        // No so_id yet for a manually created SO — build the
                                        // product-wise checklist preview (one TECHNICAL/COMMERCIAL
                                        // block per product) from whatever products are on the
                                        // order so far, so it can be filled in before saving.
                                        fetchChecklistForCurrentProducts();
                                    }}
                                >
                                    Checklist
                                </button>
                                <button
                                    className={`btn ${createActiveTab === "docs" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setCreateActiveTab("payment_history")}
                                >
                                    Payment History
                                </button>
                                <button
                                    className={`btn ${createActiveTab === "docs" ? "btn-secondary" : "btn-light"}`}
                                    style={styles.compactTabBtn}
                                    onClick={() => setCreateActiveTab("docs")}
                                >
                                    Docs
                                </button>
                            </div>

                            {createActiveTab === "basic" && (
                                <div className="row g-3">
                                    <div className="col-md-8">
                                        <label style={styles.compactLabel}>Terms and Conditions <span style={{ color: '#dc2626' }}>*</span></label>
                                        <div className="d-flex gap-2">
                                            <input type="text" className="form-control" style={styles.compactInput}
                                                placeholder="e.g. 30 days net, advance payment, etc."
                                                value={paymentTerms}
                                                onChange={(e) => setPaymentTerms(e.target.value)}
                                            />
                                            <button
                                                className="btn btn-dark btn-sm"
                                                onClick={() => setShowPaymentModal(true)}
                                            >
                                                View
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {createActiveTab === "product" && (
                                <div>
                                    <div className="d-flex justify-content-end mb-2">
                                        <button className="btn btn-sm btn-dark" onClick={handleAddRow}>+ Add Product</button>
                                    </div>
                                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}></span>
                                            {[
                                                { key: 'category', opts: filterMasterData.categories, label: 'Category' },
                                                { key: 'cableType', opts: filterMasterData.cableTypes, label: 'Cable Type' },
                                                { key: 'code', opts: filterMasterData.codes, label: 'Code' },
                                                { key: 'color', opts: filterMasterData.colours, label: 'Color' },
                                                { key: 'noOfCores', opts: filterMasterData.cores, label: 'Cores' },
                                                { key: 'nominalCrossSection', opts: filterMasterData.nominalCrossSections, label: 'Cross Section' },
                                                { key: 'referenceOfStandard', opts: filterMasterData.referenceOdStandards, label: 'Std. Ref' },
                                                { key: 'voltage', opts: filterMasterData.voltages, label: 'Voltage' },
                                                { key: 'construction', opts: filterMasterData.constructions, label: 'Construction' },
                                            ].map(({ key, opts, label }) => (
                                                <div
                                                    key={key}
                                                    title={
                                                        globalFilters[key] && globalFilters[key].length > 0
                                                            ? globalFilters[key].map(o => o.label).join(', ')
                                                            : undefined
                                                    }
                                                    style={{ minWidth: 120, flex: '1 1 110px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 3 }}
                                                >
                                                    <span style={{
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: '#64748b',
                                                        whiteSpace: 'nowrap',
                                                        letterSpacing: '0.02em',
                                                        paddingLeft: 2
                                                    }}>
                                                        {label}
                                                    </span>
                                                    <Select
                                                        options={getFilterOptions(opts)}
                                                        value={globalFilters[key] && globalFilters[key].length > 0 ? globalFilters[key] : null}
                                                        isMulti
                                                        closeMenuOnSelect={false}
                                                        hideSelectedOptions={false}
                                                        formatOptionLabel={(opt) => (
                                                            <span title={opt.label}>{opt.label}</span>
                                                        )}
                                                        onChange={selectedOpts => {
                                                            const selected = selectedOpts || [];
                                                            const updated = { ...globalFilters, [key]: selected };
                                                            setGlobalFilters(updated);
                                                            const anyActive = Object.values(updated).some(v => Array.isArray(v) ? v.length > 0 : v !== '');
                                                            if (!anyActive) { setFilteredProductList(null); return; }
                                                            const matchesFilter = (productVal, selectedVals) => {
                                                                if (!selectedVals || selectedVals.length === 0) return true;
                                                                return selectedVals.some(s => String(productVal || '').trim().toLowerCase() === s.value.toLowerCase());
                                                            };
                                                            setFilteredProductList(productList.filter(p =>
                                                                matchesFilter(p.construction, updated.construction) &&
                                                                matchesFilter(p.code, updated.code) &&
                                                                matchesFilter(p.cableType, updated.cableType) &&
                                                                matchesFilter(p.category, updated.category) &&
                                                                matchesFilter(p.noOfCores, updated.noOfCores) &&
                                                                matchesFilter(p.color, updated.color) &&
                                                                matchesFilter(p.nominalCrossSection, updated.nominalCrossSection) &&
                                                                matchesFilter(p.referenceOfStandard, updated.referenceOfStandard) &&
                                                                matchesFilter(p.voltage, updated.voltage)
                                                            ));
                                                        }}
                                                        isClearable
                                                        placeholder={label}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={{
                                                            control: (base) => ({ ...base, minHeight: 30, fontSize: '12px' }),
                                                            valueContainer: (base) => ({ ...base, padding: '2px 6px', flexWrap: 'wrap' }),
                                                            input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '12px' }),
                                                            indicatorsContainer: (base) => ({ ...base, alignSelf: 'flex-start', paddingTop: 2 }),
                                                            dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                                            clearIndicator: (base) => ({ ...base, padding: 2 }),
                                                            placeholder: (base) => ({ ...base, fontSize: '12px', color: '#94a3b8', fontWeight: 400 }),
                                                            option: (base, state) => ({
                                                                ...base, fontSize: '12px', fontWeight: 400,
                                                                color: state.isSelected ? '#fff' : '#334155',
                                                                backgroundColor: state.isSelected ? '#475569' : state.isFocused ? '#f1f5f9' : '#fff',
                                                                padding: '6px 10px',
                                                            }),
                                                            multiValue: (base) => ({ ...base, fontSize: '11px', backgroundColor: '#e2e8f0', borderRadius: 4 }),
                                                            multiValueLabel: (base) => ({ ...base, fontSize: '11px', color: '#334155', padding: '1px 4px' }),
                                                            multiValueRemove: (base) => ({ ...base, color: '#64748b', ':hover': { backgroundColor: '#cbd5e1', color: '#1e293b' } }),
                                                            menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                                            menu: (base) => ({ ...base, fontSize: '12px' }),
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                            {filteredProductList && (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    style={{ fontSize: 11, whiteSpace: 'nowrap', height: 30, padding: '0 10px' }}
                                                    onClick={clearGlobalFilters}
                                                >
                                                    ✕ Clear ({filteredProductList.length})
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ ...styles.tableContainer, padding: 0, overflowX: 'auto' }}>
                                        <table className="table" style={styles.erpTable}>
                                            <thead>
                                                <tr style={{ background: '#f8fafc' }}>
                                                    <th style={{ ...styles.erpTh, width: '50px', textAlign: 'center' }}>Item</th>
                                                    <th style={styles.erpTh}>Product</th>
                                                    <th style={styles.erpTh}>Description</th>
                                                    <th style={styles.erpTh}>Fast entry</th>
                                                    <th style={{ ...styles.erpTh, minWidth: '110px' }}>Sales Block</th>
                                                    <th style={styles.erpTh}>UOM</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Qty</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Range</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'center' }}>Currency</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Unit Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Spl. Price</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Discount</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Markup (%)</th>
                                                    <th style={{ ...styles.erpTh, textAlign: 'right' }}>Total</th>
                                                    <th style={{ ...styles.erpTh, minWidth: '150px' }}>
                                                        Req Date <span style={{ color: '#dc2626' }}>*</span>
                                                        <div style={{ marginTop: 4, fontWeight: 400 }}>
                                                            <DatePicker
                                                                className="form-control form-control-sm"
                                                                wrapperClassName="w-100"
                                                                dateFormat="dd/MM/yyyy"
                                                                placeholderText="Apply to all"
                                                                title="Pick a date to apply to all products (you can still change each product)"
                                                                selected={createHeaderReqDate ? new Date(createHeaderReqDate + 'T00:00:00') : null}
                                                                onChange={handleDirectHeaderReqDateChange}
                                                                isClearable
                                                            />
                                                        </div>
                                                    </th>
                                                    <th style={{ ...styles.erpTh, width: 60, textAlign: 'center' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {newProducts.map((row, idx) => {
                                                    const qty = parseFloat(row.quantity) || 0;
                                                    const price = parseFloat(row.unit_price) || 0;
                                                    const disc = parseFloat(row.discount) || 0;
                                                    // A per-row Markup (%) overrides the header-level Markup (%) for
                                                    // this line's Total only, if the user has set one on the row;
                                                    // otherwise it falls back to the header value, same as the
                                                    // fallback already used when editing an existing SO
                                                    // (see prod.markup ?? markup above).
                                                    const rowMarkup = (row.markup !== undefined && row.markup !== null && row.markup !== '')
                                                        ? (parseFloat(row.markup) || 0)
                                                        : (parseFloat(markup) || 0);
                                                    const priceWithMarkup = price + (price * rowMarkup) / 100;

                                                    // ✅ FIX: no more base-vs-converted split, and no separate
                                                    // "Total (Yuan)" column. For an Export SO, the product
                                                    // master's base price is converted through the Exchange
                                                    // Rate right here — using the rate already loaded in state
                                                    // (soExchangeRate / row.exchange_rate), no extra backend
                                                    // call — and THIS single converted number is what's shown
                                                    // in Unit Price / Total AND what gets sent to the backend
                                                    // as unit_price on Save (see the payload builder below,
                                                    // which now uses this same value instead of the raw base
                                                    // row.unit_price). What you see while creating is exactly
                                                    // what gets saved and exactly what View/Edit shows afterward.
                                                    const rowExchangeRate = soOrderType === "export"
                                                        ? (Number(row.exchange_rate ?? soExchangeRate) || 1)
                                                        : 1;
                                                    const displayUnitPrice = priceWithMarkup * rowExchangeRate;
                                                    const total = (qty * displayUnitPrice) - ((disc * qty * displayUnitPrice) / 100);

                                                    // Currency shown next to Unit Price is the SO's own currency
                                                    // for Export (since Unit Price is now already converted into
                                                    // it), or the product's own currency / INR for Domestic.
                                                    const displayCurrency = soOrderType === "export"
                                                        ? (soCurrency || row.currency || row.Currency || "")
                                                        : (row.currency || row.Currency || "INR");

                                                    return (
                                                        <tr key={idx}>
                                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>{idx + 1}</td>
                                                            <td style={{ ...styles.erpTd, minWidth: 160, overflow: 'visible' }}>
                                                                <CreatableSelect
                                                                    classNamePrefix="react-select"
                                                                    options={filteredProductList !== null ? filteredProductList : productList}
                                                                    value={
                                                                        row.product_code
                                                                            ? {
                                                                                value: row.product_code,
                                                                                label: row.description ? `${row.product_code} - ${row.description}` : row.product_code
                                                                            }
                                                                            : null
                                                                    }
                                                                    onChange={(opt) => handleNewProductChange(idx, "product_code", opt ? opt.value : "")}
                                                                    placeholder="Select or Type"
                                                                    isSearchable
                                                                    isClearable
                                                                    backspaceRemovesValue={false}
                                                                    closeMenuOnSelect={false}
                                                                    blurInputOnSelect={false}
                                                                    openMenuOnFocus
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    styles={{
                                                                        control: (base) => ({ ...base, minHeight: '30px', height: '30px', fontSize: '13px', minWidth: '140px' }),
                                                                        valueContainer: (base) => ({ ...base, height: '30px', padding: '0 8px' }),
                                                                        input: (base) => ({ ...base, margin: 0, padding: 0 }),
                                                                        indicatorsContainer: (base) => ({ ...base, height: '30px' }),
                                                                        dropdownIndicator: (base) => ({ ...base, padding: 2 }),
                                                                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                                                        option: (base, state) => ({
                                                                            ...base,
                                                                            fontSize: '13px',
                                                                            fontWeight: 400,
                                                                            color: state.isSelected ? '#fff' : '#334155',
                                                                            backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : '#fff',
                                                                            padding: '8px 12px',
                                                                            cursor: 'pointer',
                                                                        }),
                                                                        menu: (base) => ({ ...base, fontSize: '13px' })
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={styles.erpTd}>{row.description}</td>
                                                            <td style={styles.erpTd}>
                                                                {row.product_class}
                                                                {row.product_class && (
                                                                    <span
                                                                        style={{ cursor: 'pointer', marginLeft: 8, color: '#050505', fontWeight: 'bold' }}
                                                                        onClick={() => handleOpenCreateCharModal(idx)}
                                                                        title="Edit Characteristics"
                                                                    >
                                                                        ▶
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td style={{ ...styles.erpTd, minWidth: '110px' }}>
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    style={{ minWidth: '95px' }}
                                                                    value={row.sales_block || salesBlock || "Blocked"}
                                                                    onChange={e => handleNewProductChange(idx, 'sales_block', e.target.value)}
                                                                >
                                                                    <option value="Blocked">Blocked</option>
                                                                    <option value="Released">Released</option>
                                                                </select>
                                                            </td>
                                                            <td style={styles.erpTd}>{row.uom}</td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 70, textAlign: 'right', marginLeft: 'auto', MozAppearance: 'textfield' }}
                                                                    value={row.quantity}
                                                                    onChange={e => handleNewProductChange(idx, 'quantity', e.target.value)}
                                                                    disabled
                                                                />
                                                            </td>

                                                            {/* Range — read-only, calculated from Over/Under Tolerances via Characteristics (▶) */}
                                                            <td style={{ ...styles.erpTd, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: 12, fontWeight: 600, color: row.range ? '#16a34a' : '#94a3b8' }}>
                                                                    {row.range || '—'}
                                                                </span>
                                                            </td>

                                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 3, padding: '2px 6px' }}>
                                                                    {displayCurrency}
                                                                </span>
                                                            </td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm no-spinner"
                                                                    style={{ width: 80, textAlign: 'right', marginLeft: 'auto', MozAppearance: 'textfield', backgroundColor: '#e9ecef' }}
                                                                    value={Math.round(price * rowExchangeRate * 100) / 100}
                                                                    disabled={true}
                                                                    title={rowExchangeRate !== 1 ? `Unit Price is the reference price x exchange rate ${rowExchangeRate} (before Markup and Discount) - read-only` : "Unit Price is the reference price (before Markup and Discount) - read-only"}
                                                                />
                                                            </td>

                                                            {/* 🆕 Spl. Price — price per unit after Markup (%) and Discount (%); can be typed, in which
                                                                case the row's base price is worked back from it (same maths as the quotation). */}
                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 80, textAlign: 'right', marginLeft: 'auto', MozAppearance: 'textfield' }}
                                                                    value={splEdit && splEdit.grid === 'create' && splEdit.idx === idx ? splEdit.text : Math.round(displayUnitPrice * (1 - disc / 100) * 100) / 100}
                                                                    onFocus={() => setSplEdit({ grid: 'create', idx, text: String(Math.round(displayUnitPrice * (1 - disc / 100) * 100) / 100) })}
                                                                    onBlur={() => setSplEdit(null)}
                                                                    onChange={(e) => {
                                                                        const text = e.target.value;
                                                                        setSplEdit({ grid: 'create', idx, text });
                                                                        const typed = parseFloat(text);
                                                                        const factor = (1 + rowMarkup / 100) * rowExchangeRate * (1 - disc / 100);
                                                                        const rawPrice = (text === "" || isNaN(typed))
                                                                            ? ""
                                                                            : String(Math.round((factor > 0 ? typed / factor : typed) * 1e6) / 1e6);
                                                                        handleNewProductChange(idx, 'unit_price', rawPrice);
                                                                    }}
                                                                    title="Spl. Price (after Markup and Discount). Type a price to set it directly." />
                                                            </td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                <input type="number" className="form-control form-control-sm no-spinner" style={{ width: 60, textAlign: 'right', marginLeft: 'auto', MozAppearance: 'textfield' }}
                                                                    value={row.discount}
                                                                    onChange={e => handleNewProductChange(idx, 'discount', e.target.value)}
                                                                />
                                                            </td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>
                                                                <input type="number" className="form-control form-control-sm" style={{ width: 60, textAlign: 'right', marginLeft: 'auto' }}
                                                                    placeholder={markup || "0"}
                                                                    value={row.markup ?? ''}
                                                                    onChange={e => handleNewProductChange(idx, 'markup', e.target.value)}
                                                                    title="Overrides the header Markup (%) for this line only"
                                                                />
                                                            </td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 500 }}>
                                                                {formatCurrency(total)}
                                                            </td>
                                                            <td style={styles.erpTd}>
                                                                <DatePicker
                                                                    className="form-control form-control-sm"
                                                                    wrapperClassName="w-100"
                                                                    dateFormat="dd/MM/yyyy"
                                                                    placeholderText="dd/mm/yyyy"
                                                                    selected={row.requirement_date ? new Date(String(row.requirement_date).substring(0, 10) + 'T00:00:00') : null}
                                                                    onChange={(selectedDate) => {
                                                                        let newVal = '';
                                                                        if (selectedDate) {
                                                                            const yyyy = selectedDate.getFullYear();
                                                                            const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                                            const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                                            newVal = `${yyyy}-${mm}-${dd}`;
                                                                        }
                                                                        handleNewProductChange(idx, 'requirement_date', newVal);
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ ...styles.erpTd, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                <button
                                                                    title="Delete product"
                                                                    onClick={() => handleDeleteRow(idx)}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px', lineHeight: 1, padding: '2px 6px' }}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {newProducts.length === 0 && (
                                                    <tr><td colSpan="13" className="text-center text-muted p-3">No products added yet</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {createActiveTab === "payment_history" && renderPaymentHistoryTab()}

                            {createActiveTab === "checklist" && renderChecklistTab()}

                            {createActiveTab === "docs" && (
                                <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <label
                                            htmlFor="soCreateDocInput"
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: 8,
                                                padding: '18px 12px', cursor: 'pointer', background: '#f8fafc',
                                                transition: 'background 0.2s'
                                            }}
                                        >
                                            <span style={{ fontSize: 22 }}></span>
                                            <span style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginTop: 4 }}>Click to attach documents</span>
                                            <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>PDF, Word, Excel, Images, etc. (max {maxSoDocSizeKb} KB per file)</span>
                                            <input
                                                id="soCreateDocInput"
                                                type="file"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={async (e) => {
                                                    const rawFiles = Array.from(e.target.files);
                                                    e.target.value = "";
                                                    if (!rawFiles.length) return;
                                                    const files = filterFilesWithinSizeLimit(rawFiles);
                                                    if (!files.length) return;
                                                    const formData = new FormData();
                                                    files.forEach(f => formData.append("docs", f));
                                                    try {
                                                        const res = await fetch(
                                                            `${ORDER_API}/sales-order/upload-docs/DRAFT`,
                                                            {
                                                                headers: authHeaders(), method: "POST", body: formData
                                                            }
                                                        );
                                                        if (!res.ok) throw new Error(await res.text());
                                                        const result = await res.json();
                                                        const saved = result.files.map(f => ({
                                                            name: f.originalname,
                                                            filename: f.filename,
                                                            file_url: `/uploads/${f.filename}`,
                                                            size: f.size,
                                                            type: f.mimetype,
                                                            uploadedAt: formatChangeLogDateTime(new Date()),
                                                            soId: 'DRAFT'
                                                        }));
                                                        setSoDocFiles(prev => [...prev, ...saved]);
                                                        toast.success(`${saved.length} file(s) uploaded`);
                                                    } catch (err) {
                                                        toast.error("File upload failed: " + err.message);
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {soDocFiles.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '28px 0', color: '#94a3b8', fontSize: 13 }}>
                                            <div style={{ fontSize: 32, marginBottom: 8 }}></div>
                                            No documents attached yet.
                                        </div>
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr>
                                                    <th style={styles.erpTh}>#</th>
                                                    <th style={styles.erpTh}>File Name</th>
                                                    <th style={styles.erpTh}>Type</th>
                                                    <th style={styles.erpTh}>Size</th>
                                                    <th style={styles.erpTh}>Uploaded At</th>
                                                    <th style={styles.erpTh}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {soDocFiles.map((doc, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                        <td style={styles.erpTd}>{idx + 1}</td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{ marginRight: 6 }}>{getFileIcon(doc.name, doc.type)}</span>
                                                            <span style={{ color: '#1e293b', fontWeight: 500 }}>{doc.name}</span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '2px 7px', fontSize: 11, color: '#475569', fontWeight: 600 }}>
                                                                {doc.type ? doc.type.split('/').pop().toUpperCase() : (doc.name?.split('.').pop() || '—').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td style={styles.erpTd}>
                                                            {doc.size ? (doc.size > 1024 * 1024 ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB` : `${(doc.size / 1024).toFixed(1)} KB`) : '—'}
                                                        </td>
                                                        <td style={styles.erpTd}>{doc.uploadedAt || '—'}</td>
                                                        <td style={styles.erpTd}>
                                                            <button
                                                                onClick={() => setSoDocFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}
                                                                title="Remove"
                                                            >✕</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button className="btn btn-dark" disabled={loading} onClick={() => {
                                if (!canCreate) {
                                    toast.error("You don't have Create access. Please contact admin.");
                                    return;
                                }
                                handleSaveDirectSO();
                            }}>
                                {loading ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── SO Excel Upload Modal ── */}
            {showSoExcelModal && (
                <div style={{ ...styles.modalBackdrop, zIndex: 1060 }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>📊 Excel Upload</div>
                            <button style={styles.iconBtn} onClick={() => setShowSoExcelModal(false)}>✕</button>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '16px', marginBottom: '20px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 6 }}>Step 1 — Download Template</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Download the template file and fill in your product details. Make sure the column headers match exactly.
                                </p>
                                <button
                                    className="btn btn-sm"
                                    style={{ background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: 6, padding: '7px 16px' }}
                                    onClick={handleSoDownloadTemplate}
                                >
                                    ⬇️ Download Template
                                </button>
                            </div>
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '16px' }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', marginBottom: 6 }}>Step 2 — Upload Filled File</div>
                                <p style={{ fontSize: 12, color: '#4b5563', margin: '0 0 12px 0' }}>
                                    Upload the completed Excel or CSV file to import your product line items.
                                </p>
                                <label
                                    htmlFor="soExcelFileInput"
                                    style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        border: '2px dashed #93c5fd', borderRadius: 8, padding: '20px 12px', cursor: 'pointer',
                                        background: soSalesFile ? '#dbeafe' : '#fff', transition: 'background 0.2s', marginBottom: 12
                                    }}
                                >
                                    <span style={{ fontSize: 28 }}>{soSalesFile ? '✅' : '📁'}</span>
                                    <span style={{ fontSize: 12, color: '#374151', marginTop: 6, fontWeight: soSalesFile ? 600 : 400 }}>
                                        {soSalesFile ? soSalesFile.name : 'Click to browse or drag & drop'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Supports .xls, .xlsx, .csv</span>
                                    <input
                                        id="soExcelFileInput"
                                        type="file"
                                        ref={salesFileInputRef}
                                        accept=".xls,.xlsx,.csv"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setSoSalesFile(e.target.files[0])}
                                    />
                                </label>
                                {soSalesFile && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#374151', background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 10px' }}>
                                        <span>📄 {soSalesFile.name}</span>
                                        <button onClick={() => setSoSalesFile(null)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>✕</button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowSoExcelModal(false)}>Cancel</button>
                            <button
                                className="btn btn-dark btn-sm px-4"
                                onClick={async () => {
                                    await handleSoSalesFileUpload();
                                    setShowSoExcelModal(false);
                                }}
                                disabled={!soSalesFile || soExcelUploading}
                            >
                                {soExcelUploading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Reason Prompt Modal */}
            {showCancelPrompt && (
                <div style={{ ...styles.modalBackdrop, zIndex: 9999 }}>
                    <div style={{ ...styles.modal, maxWidth: "400px", padding: "20px" }}>
                        <h5 className="mb-3 fw-bold">Select Cancellation Reason</h5>
                        <div className="mb-4">
                            <select
                                className="form-control"
                                value={cancelReasonType}
                                onChange={(e) => setCancelReasonType(e.target.value)}
                            >
                                <option value="">-- Select Reason --</option>
                                <option value="Customer">Cancelled by Customer</option>
                                <option value="SBEE">Cancelled by SBEE</option>
                            </select>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowCancelPrompt(false)}>Close</button>
                            <button className="btn btn-danger btn-sm" onClick={submitCancelRequest}>Confirm Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <CharacteristicsModal
                modalVisible={modalVisible}
                selectedCharacteristics={selectedCharacteristics}
                handleClose={() => {
                    if (createModalOpen) {
                        handleSaveCreateChars(selectedCharacteristics);
                    } else {
                        setModalVisible(false);
                    }
                }}
                isEditing={createModalOpen || (isEditing && modalMode !== 'convert')}
                handleChange={onModalCharChange}
                handleToggleTolerance={handleToggleTolerance}
                onAdd={handleAddDrumGroup}
                onRemove={handleRemoveDrumGroup}
            />

            {showProductModal && productDetails && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "950px",
                            width: "95%",
                            maxHeight: "85vh",
                            padding: "14px",
                            fontSize: "13px"
                        }}
                    >
                        <div style={{ padding: "12px" }}>
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{productDetails.product_code || productDetails.product_number}</td>
                                        <td>{productDetails.hsn_code}</td>
                                        <td>{productDetails.quantity}</td>
                                        <td>₹ {productDetails.unit_price}</td>
                                        <td>{productDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            ₹ {formatCurrency(productDetails.totalPrice)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    borderTop: "1px solid #999",
                                    borderBottom: "1px solid #999",
                                    marginBottom: "10px",
                                    fontWeight: 600
                                }}
                            >
                                <span>Net Price :</span>
                                <span>₹ {formatCurrency(productDetails.netPrice)}</span>
                            </div>

                            <table
                                className="table table-sm"
                                style={{ width: "50%", marginLeft: "auto" }}
                            >
                                <tbody>
                                    {/* 🆕 FULLY DYNAMIC — one row per actual tax type configured
                                        for this line's HSN (see the taxes[] built when "View" was
                                        clicked, above), instead of assuming exactly CGST/SGST/IGST
                                        plus one lumped "Other Tax". Falls back to a single CGST
                                        placeholder row only if nothing resolved at all. */}
                                    {(productDetails.taxes && productDetails.taxes.length
                                        ? productDetails.taxes
                                        : [{ type: "CGST", percent: 0, value: 0 }]
                                    ).map((t, i) => (
                                        <tr key={i}>
                                            <td>{t.type} @ {t.percent || 0}%</td>
                                            <td style={{ textAlign: "right" }}>
                                                ₹ {formatCurrency(t.value)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                        <td>Total Tax</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {formatCurrency(productDetails.totalGST)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    marginTop: "8px",
                                    background: "#f1f5f9",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    borderRadius: "4px"
                                }}
                            >
                                {/* unit_price on this line is already the final,
                                    converted-to-order-currency selling price for an Export
                                    line (fixed at Create/Save time) — one Grand Total, in
                                    the line's real currency, no separate converted block. */}
                                <span>Grand Total ({productDetails.currency || "INR"}) :</span>
                                <span>{formatCurrency(productDetails.finalPriceAfterGST)}</span>
                            </div>
                        </div>

                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowProductModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= PAYMENT TERMS MODAL ================= */}
            {showPaymentModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "700px" }}>
                        <div style={styles.modalHeader}>
                            <strong>Payment Terms</strong>
                            <button style={styles.iconBtn} onClick={() => setShowPaymentModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <textarea
                                className="form-control"
                                rows={12}
                                value={paymentTerms}
                                onChange={(e) => setPaymentTerms(e.target.value)}
                            />
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-dark"
                                onClick={() => setShowPaymentModal(false)}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===================== DELETE CONFIRM MODAL ===================== */}
            {showInspectionModal && (
                <div style={styles.modalBackdrop} onClick={() => setShowInspectionModal(false)}>
                    <div
                        style={{ background: '#fff', borderRadius: 12, width: '820px', maxWidth: '92vw', padding: '28px 32px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 style={{ margin: 0 }}>Inspection Schedule</h5>
                            <button type="button" style={styles.iconBtn} onClick={() => setShowInspectionModal(false)}>✖</button>
                        </div>

                        <div className="ac-modal-items-label" style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                            Proposed Date History
                        </div>
                        {inspectionHistoryLoading ? (
                            <div style={{ fontSize: 13, color: "#98a2b3", marginBottom: 12 }}>Loading history…</div>
                        ) : (
                            <div style={{ maxHeight: 340, overflowY: "auto", marginBottom: 12 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #eef0f2", color: "#667085", fontWeight: 600 }}>Department</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #eef0f2", color: "#667085", fontWeight: 600 }}>Proposed Date <span style={{ color: '#dc2626' }}>*</span></th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #eef0f2", color: "#667085", fontWeight: 600 }}>Person Responsible</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #eef0f2", color: "#667085", fontWeight: 600 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Oldest first, top to bottom — every replaced slot in the
                                            order it was proposed, ending with the current live slot
                                            (which is the only row that's still confirmable). Department
                                            is derived from each row's own proposed_source the same way
                                            the current row is (falls back to "—" only for rows logged
                                            before that column existed). Person Responsible is tracked
                                            per line via assigned_to. */}
                                        {[...inspectionHistory].filter(h => h.eventType === "proposed").reverse().map((h, idx) => (
                                            <tr key={idx}>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", color: "#475467" }}>
                                                    {h.proposedSource ? (h.proposedSource === "sales_order" ? "Sales" : "Quality") : "—"}
                                                </td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", color: "#475467" }}>{formatDateDisplay(h.proposedDate)}</td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", color: "#475467" }}>{h.assignedTo || "—"}</td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb" }}></td>
                                            </tr>
                                        ))}
                                        {customerInspectionProposedDate && (
                                            <tr>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", fontWeight: 600, color: "#1a73e8" }}>
                                                    {inspectionDepartment}
                                                </td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", fontWeight: 600, color: "#1a73e8" }}>
                                                    {formatDateDisplay(customerInspectionProposedDate)} <span style={{ fontWeight: 400, fontSize: 11, color: "#98a2b3" }}>(current)</span>
                                                </td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb", fontWeight: 600, color: "#1a73e8" }}>
                                                    {customerInspectionAssignedTo || "—"}
                                                </td>
                                                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f8f9fb" }}>
                                                    {customerInspectionConfirmedDate === customerInspectionProposedDate
                                                        && customerInspectionConfirmedTime === customerInspectionProposedTime ? (
                                                        <span style={{ fontSize: 11, color: "#12a150", fontWeight: 600 }}>Confirmed</span>
                                                    ) : customerInspectionProposedSource === "audit_calendar" ? (
                                                        <button
                                                            type="button"
                                                            disabled={confirmingProposed}
                                                            onClick={() => handleConfirmProposedSlot(customerInspectionProposedDate, customerInspectionProposedTime)}
                                                            style={{
                                                                border: '1px solid #1a73e8', background: '#fff', color: '#1a73e8',
                                                                borderRadius: 6, padding: '3px 10px', fontSize: 11.5, fontWeight: 600,
                                                                cursor: confirmingProposed ? 'default' : 'pointer',
                                                                opacity: 1
                                                            }}
                                                        >
                                                            {confirmingProposed ? '…' : 'Confirm'}
                                                        </button>
                                                    ) : (
                                                        <span style={{ fontSize: 10.5, color: "#98a2b3" }} title="Drag this slot on the Audit Calendar before it can be confirmed">
                                                            Pending Audit Calendar
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                        {!customerInspectionProposedDate && !showAddProposedRow && inspectionHistory.filter(h => h.eventType === "proposed").length === 0 && (
                                            <tr>
                                                <td colSpan={4} style={{ padding: "6px 8px", color: "#98a2b3" }}>No proposed slots yet.</td>
                                            </tr>
                                        )}

                                        {/* Inline "add a new proposed slot" row, toggled by the "+"
                                            below. Its own Save button pushes it to the calendar and
                                            the row above becomes the new current line. Department and
                                            Person Responsible are never typed — added here purely as a
                                            preview of what will be saved: "Sales" (this screen) and
                                            whoever is currently logged in. */}
                                        {showAddProposedRow && (
                                            <tr>
                                                <td style={{ padding: "6px 8px", color: "#475467" }}>Sales</td>
                                                <td style={{ padding: "6px 8px", minWidth: 150 }}>
                                                    <DatePicker
                                                        className="form-control"
                                                        wrapperClassName="w-100"
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="dd/mm/yyyy"
                                                        selected={newProposedDate ? new Date(formatDate(newProposedDate)) : null}
                                                        onChange={(selectedDate) => {
                                                            let newVal = '';
                                                            if (selectedDate) {
                                                                const yyyy = selectedDate.getFullYear();
                                                                const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
                                                                const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                                newVal = `${yyyy}-${mm}-${dd}`;
                                                            }
                                                            setNewProposedDate(newVal);
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: "6px 8px", color: "#475467" }}>{getCurrentUser()}</td>
                                                <td style={{ padding: "6px 8px" }}>
                                                    <button
                                                        type="button"
                                                        disabled={pushingProposed}
                                                        onClick={handleSaveNewProposedSlot}
                                                        style={{
                                                            border: '1px solid #1a73e8', background: pushingProposed ? '#eaf1fd' : '#1a73e8',
                                                            color: pushingProposed ? '#1a73e8' : '#fff',
                                                            borderRadius: 6, padding: '5px 12px', fontSize: 11.5, fontWeight: 600,
                                                            cursor: pushingProposed ? 'default' : 'pointer',
                                                            opacity: 1
                                                        }}
                                                    >
                                                        {pushingProposed ? 'Saving…' : 'Save'}
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!showAddProposedRow && (
                            <button
                                type="button"
                                title="Add a new proposed date/time"
                                onClick={() => { setNewProposedDate(''); setNewProposedTime(''); setShowAddProposedRow(true); }}
                                style={{
                                    border: '1px dashed #1a73e8', background: '#fff', color: '#1a73e8',
                                    borderRadius: 8, padding: '5px 14px', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer',
                                    opacity: 1,
                                    marginBottom: 16
                                }}
                            >
                                + Add proposed date
                            </button>
                        )}

                        {customerInspectionConfirmedDate && (
                            <div style={{ fontSize: 12.5, color: '#475467', marginBottom: 8 }}>
                                <strong>Confirmed:</strong> {formatDateDisplay(customerInspectionConfirmedDate)}
                                {customerInspectionAssignedTo && <> — <strong>Person Responsible:</strong> {customerInspectionAssignedTo}</>}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.5)",
                    zIndex: 1070,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    <div style={{
                        background: "#fff",
                        width: "420px",
                        borderRadius: 14,
                        padding: "36px 32px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        textAlign: "center",
                        position: "relative"
                    }}>
                        {/* CLOSE */}
                        <button
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setSoToDelete(null);
                            }}
                            style={{
                                position: "absolute",
                                top: 16,
                                right: 18,
                                background: "transparent",
                                border: "none",
                                fontSize: 18,
                                cursor: "pointer",
                                color: "#94a3b8"
                            }}
                        >
                            ✖
                        </button>

                        {/* ICON BADGE */}
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                            fontSize: 28,
                            color: "#fff",
                            fontWeight: "bold"
                        }}>
                            🗑
                        </div>

                        {/* TITLE */}
                        <h4 style={{
                            margin: "0 0 12px",
                            fontSize: "19px",
                            fontWeight: 700,
                            color: "#1e293b"
                        }}>
                            Delete Sales Order
                        </h4>

                        {/* MESSAGE */}
                        <p style={{
                            fontSize: 14,
                            color: "#64748b",
                            marginBottom: 28,
                            lineHeight: 1.5
                        }}>
                            Delete Sales Order "{soToDelete}"? This will hide it from the list.
                        </p>

                        {/* ACTIONS */}
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 10
                        }}>
                            <button
                                onClick={confirmDeleteSO}
                                style={{
                                    padding: "12px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "#ef4444",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Yes, Delete Sales Order
                            </button>

                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setSoToDelete(null);
                                }}
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: 10,
                                    border: "none",
                                    background: "transparent",
                                    color: "#334155",
                                    cursor: "pointer",
                                    fontWeight: 500,
                                    fontSize: 14,
                                    width: "100%"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default SalesOrder;